// BUILD: NFL Advanced Stats compact centered tables and revised metric groups (2026-10-29)
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ScatterChart,
  Scatter,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Legend,
} from "recharts";

/** ================== CONFIG ================== */
/**
 * Your static JSON lives here:
 *   public/data/nfl/std/<YEAR>/*.json
 */
const BASE = "/data/nfl/std";
const UNIFIED_CACHE_VERSION = "v4_combined_charts";

/**
 * Add seasons here as you publish them.
 * Example: [2023, 2024, 2025]
 */
const SEASONS = [2025,2026];

/** NGS tabs (your current files) */
const TABS = [
  { key: "Passing", kind: "unified", category: "passing", files: ["ngs_passing.json", "pfr_advanced_pass_weekly.json", "seasonal_reg.json", "pbp.json"] },
  { key: "Receiving", kind: "unified", category: "receiving", files: ["ngs_receiving.json", "pfr_advanced_rec_weekly.json", "seasonal_reg.json", "pbp.json", "ff_opportunity.json"] },
  { key: "Rushing", kind: "unified", category: "rushing", files: ["ngs_rushing.json", "pfr_advanced_rush_weekly.json", "seasonal_reg.json", "pbp.json"] },
  { key: "Game Data", kind: "games", file: "schedules.json" },
  { key: "Snap Counts", kind: "snaps", file: "snap_counts.json" },
  { key: "Depth Charts", kind: "depth", file: "depth_charts.json" },
  { key: "Game Analyzer", kind: "pbp", file: "pbp.json" },
  { key: "Game Log", kind: "gamelog", file: "ff_opportunity.json" },
  { key: "Team Box Score", kind: "teambox", files: ["ff_opportunity.json", "snap_counts.json", "pbp.json"] },
  { key: "Fantasy Flow Chart", kind: "fantasyflow", file: "ff_opportunity.json" },
  { key: "Fantasy Opportunity", kind: "research", file: "ff_opportunity.json", group: "DFS" },
  { key: "Advanced Defense", kind: "research", file: "pfr_advanced_def_weekly.json", group: "Defense" },
  { key: "Team Tendencies", kind: "research", file: "ftn_charting.json", group: "Charting" },
  { key: "Pace", kind: "pace", file: "pbp.json" },
  { key: "Team Stats", kind: "teamstats", file: "pbp.json" },
 ];

const WORKSPACES = [
  {
    key: "tendencies",
    label: "Teams",
    description: "Team players, box scores, and tendencies",
    tabs: ["Team Box Score", "Team Stats", "Pace", "Team Tendencies"],
  },
  {
    key: "players",
    label: "Player Research",
    description: "Position-based performance, efficiency, and advanced detail",
    tabs: ["Passing", "Receiving", "Rushing", "Advanced Defense"],
  },
  {
    key: "opportunity",
    label: "Fantasy Opportunity",
    description: "Expected fantasy production, usage, and regression",
    tabs: ["Fantasy Opportunity", "Fantasy Flow Chart"],
  },
  {
    key: "gamelog",
    label: "Game Log",
    description: "Search any player for weekly box scores and DK/FD scoring",
    tabs: ["Game Log"],
  },
  {
    key: "games",
    label: "Games & Matchups",
    description: "Schedule, betting context, game summaries, and play-by-play",
    tabs: ["Game Data", "Game Analyzer"],
  },
  {
    key: "roles",
    label: "Roles & Depth",
    description: "Weekly playing time, depth charts, and role movement",
    tabs: ["Snap Counts", "Depth Charts"],
  },
];

const WORKSPACE_BY_TAB = Object.fromEntries(
  WORKSPACES.flatMap((workspace) => workspace.tabs.map((tabKey) => [tabKey, workspace.key]))
);

/** hide noisy/duplicate identity cols in NGS tables */
const HIDDEN_NGS_COLS = new Set([
  "PLAYER_SHORT_NAME",
  "PLAYER_GSIS_ID",
  "PLAYER_FIRST_NAME",
  "PLAYER_LAST_NAME",
  "PLAYER_JERSEY_NUMBER",
]);

/** Team colors (same as before) */
const TEAM_COLORS = {
  KC: "#C8102E",
  MIA: "#008E97",
  DAL: "#003C7D",
  PHI: "#004C54",
  DET: "#0076B6",
  SF: "#AA0000",
  BUF: "#00338D",
  NYJ: "#125740",
  CLE: "#311D00",
  CIN: "#FB4F14",
  MIN: "#4F2683",
  PIT: "#FFB612",
  GB: "#203731",
  NE: "#002244",
  BAL: "#241773",
  LAR: "#003594",
  LAC: "#0080C6",
  NO: "#D3BC8D",
  JAX: "#006778",
  SEA: "#002244",
  TEN: "#0C2340",
  IND: "#002C5F",
  ATL: "#A71930",
  WAS: "#773141",
  CAR: "#0085CA",
  CHI: "#0B162A",
  HOU: "#03202F",
  NYG: "#0B2265",
  LV: "#000000",
  TB: "#D50A0A",
  ARI: "#97233F",
  DEN: "#FB4F14",
};

const TEAM_NAMES = {
  ARI: "Arizona Cardinals",
  ATL: "Atlanta Falcons",
  BAL: "Baltimore Ravens",
  BUF: "Buffalo Bills",
  CAR: "Carolina Panthers",
  CHI: "Chicago Bears",
  CIN: "Cincinnati Bengals",
  CLE: "Cleveland Browns",
  DAL: "Dallas Cowboys",
  DEN: "Denver Broncos",
  DET: "Detroit Lions",
  GB: "Green Bay Packers",
  HOU: "Houston Texans",
  IND: "Indianapolis Colts",
  JAX: "Jacksonville Jaguars",
  KC: "Kansas City Chiefs",
  LV: "Las Vegas Raiders",
  LAC: "Los Angeles Chargers",
  LAR: "Los Angeles Rams",
  MIA: "Miami Dolphins",
  MIN: "Minnesota Vikings",
  NE: "New England Patriots",
  NO: "New Orleans Saints",
  NYG: "New York Giants",
  NYJ: "New York Jets",
  PHI: "Philadelphia Eagles",
  PIT: "Pittsburgh Steelers",
  SEA: "Seattle Seahawks",
  SF: "San Francisco 49ers",
  TB: "Tampa Bay Buccaneers",
  TEN: "Tennessee Titans",
  WAS: "Washington Commanders",
};

const POS_LONG = {
  QB: "Quarterback",
  RB: "Running Back",
  FB: "Fullback",
  WR: "Wide Receiver",
  TE: "Tight End",
  OL: "Offensive Line",
  C: "Center",
  G: "Guard",
  T: "Tackle",
  LT: "Left Tackle",
  LG: "Left Guard",
  RT: "Right Tackle",
  RG: "Right Guard",
  DL: "Defensive Line",
  EDGE: "Edge",
  DE: "Defensive End",
  DT: "Defensive Tackle",
  LB: "Linebacker",
  ILB: "Inside Linebacker",
  OLB: "Outside Linebacker",
  DB: "Defensive Back",
  CB: "Cornerback",
  S: "Safety",
  FS: "Free Safety",
  SS: "Strong Safety",
  K: "Kicker",
  P: "Punter",
  LS: "Long Snapper",
  KR: "Kick Returner",
  PR: "Punt Returner",
};

const colorForTeam = (t) => TEAM_COLORS[t] || "#6366F1";

/** NFL team logos are served from public/logos/nfl/*.png */
const LOGO_BASE = "/logos/nfl";
const LOGO_EXT = "png";
const TEAM_ABBR_MAP = {
  WSH: "WAS",
  JAC: "JAX",
  LA: "LAR",   // nflverse/PBP uses LA; NGS and the UI use LAR
  OAK: "LV",
  SD: "LAC",
  STL: "LAR",
};

/** ================== HELPERS ================== */
async function fetchJson(url) {
  const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} @ ${url}`);
  const txt = await res.text();
  const ct = res.headers.get("content-type") || "";
  const looksJson = ct.includes("application/json") || /^[\[{]/.test(txt.trim());
  if (!looksJson) throw new Error(`Not JSON @ ${url}`);
  try {
    return JSON.parse(txt);
  } catch {
    throw new Error(`Invalid JSON @ ${url}`);
  }
}

function fmt(v) {
  if (v == null || v === "") return "";
  if (typeof v === "number") {
    const rounded = Math.round(v * 100) / 100;
    return String(rounded);
  }
  return String(v);
}

// nflverse shares and rates are stored as 0–1 fractions; completion_percentage
// is calculated on the 0–100 scale below.
function isFractionalRate(key) {
  const name = String(key || "").toLowerCase();
  return /(?:^|_)(?:share|pct|rate)$/.test(name) ||
    ["wopr", "passing_success_rate", "receiving_success_rate", "rushing_success_rate"].includes(name);
}
function formatStat(value, key) {
  if (value == null || value === "") return "";
  if (typeof value !== "number" || !Number.isFinite(value)) return fmt(value);
  if (key === "completion_percentage") return `${value.toFixed(1)}%`;
  return isFractionalRate(key) ? `${(value * 100).toFixed(1)}%` : fmt(value);
}

/** Limit dense charts for faster rendering. Ranking follows the selected Y-axis metric. */
function paddedChartDomain(rows, keys, includeZero = false) {
  const metrics = Array.isArray(keys) ? keys : [keys];
  let min = Infinity, max = -Infinity;
  for (const row of rows || []) for (const key of metrics) {
    const value = row?.[key];
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (includeZero) { min = Math.min(0, min); max = Math.max(0, max); }
  if (includeZero && min === 0 && max === 0) return [0, 0.01];
  const span = max - min;
  const buffer = span > 0 ? span * 0.1 : Math.max(Math.abs(min) * 0.1, 0.01);
  // Bars retain their zero baseline so their lengths remain meaningful.
  return [includeZero && min === 0 ? 0 : min - buffer, includeZero && max === 0 ? 0 : max + buffer];
}

function limitChartRows(rows, limitMode, rankKey) {
  const arr = Array.isArray(rows) ? [...rows] : [];
  if (!arr.length || limitMode === "all") return arr;

  arr.sort((a, b) => {
    const av = Number(a?.[rankKey]);
    const bv = Number(b?.[rankKey]);
    const aOk = Number.isFinite(av);
    const bOk = Number.isFinite(bv);
    if (aOk && bOk) return bv - av;
    if (aOk) return -1;
    if (bOk) return 1;
    return 0;
  });

  let take = Number(limitMode);
  if (String(limitMode).startsWith("p")) {
    const pct = Number(String(limitMode).slice(1));
    take = Math.max(1, Math.ceil(arr.length * pct / 100));
  }
  if (!Number.isFinite(take) || take <= 0) return arr;
  return arr.slice(0, take);
}

const CHART_LIMIT_OPTIONS = [
  ["10", "Top 10"],
  ["25", "Top 25"],
  ["50", "Top 50"],
  ["100", "Top 100"],
  ["p10", "Top 10%"],
  ["p25", "Top 25%"],
  ["all", "All points"],
];

/** "PLAYER_DISPLAY_NAME" -> "Player Display Name", "offense_pct" -> "Offense Pct" */
function prettyHeader(col) {
  const s = String(col || "").replace(/_/g, " ").trim().toLowerCase();
  return s.replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatGameTime(val) {
  if (!val) return "";

  const raw = String(val).trim();

  let hours, minutes;

  // Handle "1900"
  if (/^\d{4}$/.test(raw)) {
    hours = Number(raw.slice(0, 2));
    minutes = Number(raw.slice(2, 4));
  }

  // Handle "20:20"
  else if (/^\d{1,2}:\d{2}$/.test(raw)) {
    const parts = raw.split(":");
    hours = Number(parts[0]);
    minutes = Number(parts[1]);
  }

  if (hours == null || minutes == null) return raw;

  const d = new Date();
  d.setHours(hours);
  d.setMinutes(minutes);

  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit", // 🔥 always show minutes
    hour12: true,
  });
}

function formatKickoff(row) {
  const dateStr = row?.gameday;
  const timeStr = formatGameTime(row?.gametime);

  if (!dateStr) return timeStr;

  const d = new Date(dateStr);

  const formattedDate = d.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return `${formattedDate} • ${timeStr}`;
}

/** short label for column picker */
function abbrev(col) {
  const label = prettyHeader(col);
  if (label.length <= 16) return label;
  return label.slice(0, 16) + "…";
}

/** basic ordering (puts name/team/pos/week near the front) */
function orderColumns(cols, { kind }) {
  const set = new Set(cols);
  const front =
    kind === "snaps"
      ? ["player", "position", "team", "week", "game_type", "opponent"]
      : ["PLAYER_DISPLAY_NAME", "TEAM_ABBR", "PLAYER_POSITION", "SEASON", "SEASON_TYPE", "WEEK"];
  const head = front.filter((c) => set.has(c));
  const tail = cols.filter((c) => !head.includes(c));
  return [...head, ...tail];
}

function exportToCsv(filename, rows, columns) {
  const header = columns.join(",");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const raw = row[col];
          const cell = raw == null ? "" : String(raw);
          return '"' + cell.replaceAll('"', '""') + '"';
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** suffix-insensitive last name (for chart labels) */
const SUFFIXES = new Set(["JR", "SR", "II", "III", "IV", "V"]);
function baseLastName(name = "") {
  const parts = String(name).replace(/[,\.]/g, " ").replace(/\s+/g, " ").trim().split(" ");
  if (!parts.length) return "";
  const last = parts[parts.length - 1].toUpperCase();
  if (SUFFIXES.has(last) && parts.length >= 2) return parts[parts.length - 2];
  return parts[parts.length - 1];
}
function firstInitial(name = "") {
  const m = String(name).trim().match(/^([A-Za-z])/);
  return m ? `${m[1].toUpperCase()}. ` : "";
}

/** Compute the visible segment for a line y = m x + b clipped to [xMin,xMax]×[yMin,yMax] */
function clipLineToRect(m, b, xMin, xMax, yMin, yMax) {
  const pts = [];
  const yAtXMin = m * xMin + b;
  if (yAtXMin >= yMin && yAtXMin <= yMax) pts.push({ x: xMin, y: yAtXMin });
  const yAtXMax = m * xMax + b;
  if (yAtXMax >= yMin && yAtXMax <= yMax) pts.push({ x: xMax, y: yAtXMax });

  if (Math.abs(m) > 1e-12) {
    const xAtYMin = (yMin - b) / m;
    if (xAtYMin >= xMin && xAtYMin <= xMax) pts.push({ x: xAtYMin, y: yMin });
    const xAtYMax = (yMax - b) / m;
    if (xAtYMax >= xMin && xAtYMax <= xMax) pts.push({ x: xAtYMax, y: yMax });
  }

  const uniq = [];
  for (const p of pts) {
    if (!uniq.some((q) => Math.abs(q.x - p.x) < 1e-9 && Math.abs(q.y - p.y) < 1e-9)) uniq.push(p);
  }
  if (uniq.length < 2) return null;

  let best = [uniq[0], uniq[1]],
    bestDist = -1;
  for (let i = 0; i < uniq.length; i++) {
    for (let j = i + 1; j < uniq.length; j++) {
      const d = (uniq[i].x - uniq[j].x) ** 2 + (uniq[i].y - uniq[j].y) ** 2;
      if (d > bestDist) {
        bestDist = d;
        best = [uniq[i], uniq[j]];
      }
    }
  }
  return best;
}

/** ========= NGS key helpers (so charts work even with raw NGS headers) ========= */
function getNgsName(r) {
  return (
    r?.PLAYER_DISPLAY_NAME ??
    r?.player_display_name ??
    r?.name ??
    r?.player ??
    ""
  );
}
function getNgsTeam(r) {
  return (
    r?.TEAM_ABBR ??
    r?.team_abbr ??
    r?.team ??
    ""
  );
}

function normalizeTeamAbbr(team = "") {
  const abbr = String(team).trim().toUpperCase();
  return TEAM_ABBR_MAP[abbr] || abbr;
}

function shortPlayerName(name = "") {
  const parts = String(name)
    .replace(/[,\.]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);

  if (!parts.length) return "";

  if (parts.length > 1 && SUFFIXES.has(parts[parts.length - 1].toUpperCase())) {
    parts.pop();
  }

  if (parts.length === 1) return parts[0];
  return `${parts[0][0].toUpperCase()}. ${parts[parts.length - 1]}`;
}

function nflLogoUrl(team) {
  const normalizedTeam = normalizeTeamAbbr(team);
  return `${LOGO_BASE}/${normalizedTeam}.${LOGO_EXT}`;
}

function PlayerLogoPoint({ cx, cy, payload, showLabel = true }) {
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !payload) return null;

  const outerRadius = 17;
  const clipRadius = 14;
  const imageSize = clipRadius * 2;
  const logoUrl = payload._logo;

  // Each scatter point needs its own SVG clip-path ID.
  const clipId = `nfl-logo-clip-${String(payload._team || "team").replace(/[^a-zA-Z0-9_-]/g, "")}-${Math.round(cx * 10)}-${Math.round(cy * 10)}`;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <circle cx={cx} cy={cy} r={clipRadius} />
        </clipPath>
      </defs>

      <circle
        cx={cx}
        cy={cy}
        r={outerRadius}
        fill="#0F172A"
        stroke="#F8FAFC"
        strokeWidth={1.5}
      />

      <image
        href={logoUrl}
        xlinkHref={logoUrl}
        x={cx - imageSize / 2}
        y={cy - imageSize / 2}
        width={imageSize}
        height={imageSize}
        preserveAspectRatio="xMidYMid meet"
        clipPath={`url(#${clipId})`}
        style={{ pointerEvents: "none" }}
      />

      {showLabel && <text
        x={cx}
        y={cy + outerRadius + 14}
        textAnchor="middle"
        fill="#F8FAFC"
        fontSize={11}
        fontWeight={700}
        stroke="#020617"
        strokeWidth={3.5}
        paintOrder="stroke fill"
        strokeLinejoin="round"
        style={{ pointerEvents: "none" }}
      >
        {payload._label}
      </text>}
    </g>
  );
}

function ChartTooltip({ active, payload, xKey, yKey }) {
  if (!active || !payload || !payload.length) return null;

  const row = payload[0] && payload[0].payload;
  if (!row) return null;

  return (
    <div
      style={{
        minWidth: 210,
        padding: "12px 14px",
        borderRadius: 10,
        backgroundColor: "#020617",
        border: "1px solid #64748B",
        boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
        color: "#FFFFFF",
        opacity: 1,
      }}
    >
      <div style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 800, marginBottom: 3 }}>
        {row._fullName || row._label}
      </div>
      <div style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 700, marginBottom: 9 }}>
        {row._team}
      </div>
      <div style={{ color: "#FFFFFF", fontSize: 12, marginBottom: 5 }}>
        <span style={{ color: "#CBD5E1" }}>{prettyHeader(xKey)}:</span>{" "}
        <span style={{ color: "#FFFFFF", fontWeight: 800 }}>{fmt(row[xKey])}</span>
      </div>
      <div style={{ color: "#FFFFFF", fontSize: 12 }}>
        <span style={{ color: "#CBD5E1" }}>{prettyHeader(yKey)}:</span>{" "}
        <span style={{ color: "#FFFFFF", fontWeight: 800 }}>{fmt(row[yKey])}</span>
      </div>
    </div>
  );
}


/** ================== EXPANDED RESEARCH DATASETS ================== */
const RESEARCH_IDENTITY_KEYS = new Set([
  "season", "season_type", "game_type", "week", "game_id", "pfr_game_id", "ftn_game_id",
  "player_id", "player_gsis_id", "pfr_player_id", "pfr_player_name", "player_display_name",
  "full_name", "player_name", "player", "name", "position", "player_position", "team", "team_abbr",
  "posteam", "opponent", "recent_team"
]);

function researchName(r = {}) {
  return String(r.full_name ?? r.player_display_name ?? r.pfr_player_name ?? r.player_name ?? r.player ?? r.name ?? "").trim();
}
function researchTeam(r = {}) {
  return normalizeTeamAbbr(r.posteam ?? r.team_abbr ?? r.recent_team ?? r.team ?? "");
}
function researchPos(r = {}) {
  return String(r.position ?? r.player_position ?? r.position_group ?? "").toUpperCase();
}
function researchWeek(r = {}) {
  const n = Number(r.week ?? r.WEEK);
  return Number.isFinite(n) ? n : 0;
}

function playerJoinName(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(JR|SR|II|III|IV|V)\.?$/i, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}

function unifiedIdentity(row = {}) {
  const name = researchName(row) || getNgsName(row);
  const team = researchTeam(row) || normalizeTeamAbbr(getNgsTeam(row));
  const position = researchPos(row) || String(row.POSITION ?? row.POS ?? "").toUpperCase();
  const week = researchWeek(row);
  return { name, team, position, week };
}

function mergeMetricRow(target, source, prefix = "") {
  for (const [key, value] of Object.entries(source || {})) {
    if (value == null || value === "" || key.startsWith("_")) continue;
    if (!(key in target) || target[key] == null || target[key] === "") {
      target[key] = value;
    } else if (target[key] !== value && typeof value === "number") {
      const alternate = prefix ? `${prefix}_${key}` : key;
      if (!(alternate in target)) target[alternate] = value;
    }
  }
}

/**
 * Join NGS, PFR advanced, seasonal, and play-by-play EPA into one player view.
 * Weekly rows are keyed by player/team/week; season rows use week 0. EPA is
 * calculated from the play-level `epa` field and credited by play role.
 */
function buildUnifiedPlayerRows(datasets = [], category = "", playerLookupRows = [], situational = false) {
  const [ngsRows = [], advancedRows = [], seasonalRows = [], pbpRows = [], gameLogRows = []] = datasets;
  const byKey = new Map();
  const knownPlayers = new Set();

  const keyFor = ({ name, team, week }) =>
    `${playerJoinName(name)}__${normalizeTeamAbbr(team)}__${week}`;

  const upsert = (row, source, forcedWeek = null) => {
    const id = unifiedIdentity(row);
    if (!id.name) return null;
    if (forcedWeek != null) id.week = forcedWeek;

    const key = keyFor(id);
    const current = byKey.get(key) || {
      full_name: id.name,
      team: id.team,
      position: id.position,
      week: id.week,
    };

    if (!current.position && id.position) current.position = id.position;
    mergeMetricRow(current, row, source);
    current._sources = Array.from(new Set([...(current._sources || []), source]));
    byKey.set(key, current);
    knownPlayers.add(`${playerJoinName(id.name)}__${id.team}`);
    return current;
  };

  // Weekly NGS/PFR rows are the base of the research table.
  advancedRows.forEach((row) => upsert(row, "advanced"));
  ngsRows.forEach((row) => upsert(row, "ngs"));

  // Build lookup tables so nflverse PBP IDs can be matched back to the full
  // player names already present in NGS/PFR. This avoids relying on abbreviated
  // PBP names such as "J.Allen".
  const playerByGsis = new Map();
  const playerByNameTeam = new Map();

  for (const row of [...ngsRows, ...advancedRows, ...seasonalRows, ...playerLookupRows]) {
    const id = unifiedIdentity(row);
    if (!id.name) continue;

    knownPlayers.add(`${playerJoinName(id.name)}__${normalizeTeamAbbr(id.team)}`);
    const canonical = {
      name: id.name,
      team: normalizeTeamAbbr(id.team),
      position: id.position,
    };

    const gsis = String(
      row?.player_gsis_id ??
      row?.PLAYER_GSIS_ID ??
      row?.gsis_id ??
      row?.player_id ??
      ""
    ).trim();

    if (gsis && gsis !== "0" && gsis.toLowerCase() !== "nan") {
      playerByGsis.set(gsis, canonical);
    }

    if (canonical.team) {
      playerByNameTeam.set(
        `${playerJoinName(canonical.name)}__${canonical.team}`,
        canonical
      );
    }
  }

  // Keep the source season row available only as a lookup source.
  // DO NOT insert it as Week 0. Previously the synthetic Season Avg reused that
  // row, so fields missing from the weekly datasets stayed as season TOTALS.
  // The Season Avg below is now built only from actual Week 1-18 rows.

  const role =
    category === "passing"
      ? "passer"
      : category === "receiving"
        ? "receiver"
        : "rusher";

  const nameField = `${role}_player_name`;
  const idField = `${role}_player_id`;

  const pbpGroups = new Map();
  const receivingTeamTotals = new Map();

  for (const play of pbpRows) {
    const rawName = String(play?.[nameField] || "").trim();
    const rawId = String(play?.[idField] || "").trim();
    const team = normalizeTeamAbbr(play?.posteam || "");
    const week = Number(play?.week || 0);

    if ((!rawName && !rawId) || !team || !week) continue;

    // Count every target for the team, including players missing from the
    // research table. Otherwise the denominator is only the visible pool.
    if (category === "receiving") {
      const totalKey = `${team}__${week}`;
      const totals = receivingTeamTotals.get(totalKey) || { targets: 0, airYards: 0 };
      totals.targets += 1;
      const air = Number(play?.air_yards);
      if (Number.isFinite(air)) totals.airYards += air;
      receivingTeamTotals.set(totalKey, totals);
    }

    const canonical =
      (rawId && playerByGsis.get(rawId)) ||
      playerByNameTeam.get(`${playerJoinName(rawName)}__${team}`) ||
      { name: rawName, team, position: "" };

    const name = String(canonical?.name || rawName).trim();
    if (!name || name.toLowerCase() === "nan") continue;

    const playerKey = `${playerJoinName(name)}__${team}`;
    if (!situational && !knownPlayers.has(playerKey)) continue;

    const key = `${playerJoinName(name)}__${team}__${week}`;
    const current = pbpGroups.get(key) || {
      name,
      team,
      position: canonical?.position || "",
      week,
      epa: 0,
      epaPlays: 0,
      successes: 0,

      attempts: 0,
      completions: 0,
      passing_yards: 0,
      passing_tds: 0,
      passing_interceptions: 0,

      targets: 0,
      receptions: 0,
      receiving_yards: 0,
      receiving_tds: 0,
      receiving_air_yards: 0,
      receiving_yac: 0,

      carries: 0,
      rushing_yards: 0,
      rushing_tds: 0,
    };

    const epa = Number(play?.epa);
    if (Number.isFinite(epa) && (!situational || paceNumber(play.epa) != null)) {
      current.epa += epa;
      current.epaPlays += 1;
      if (epa > 0) current.successes += 1;
    }

    if (category === "passing") {
      const complete = Number(play?.complete_pass || 0);
      const incomplete = Number(play?.incomplete_pass || 0);
      const interception = Number(play?.interception || 0);

      // NFL pass attempts exclude sacks. The PBP flags cover completions,
      // incompletions and interceptions.
      if (complete === 1 || incomplete === 1 || interception === 1) {
        current.attempts += 1;
      }
      if (complete === 1) current.completions += 1;

      const yards = Number(play?.passing_yards);
      if (Number.isFinite(yards)) current.passing_yards += yards;

      current.passing_tds += Number(play?.pass_touchdown || 0);
      current.passing_interceptions += interception;
    }

    if (category === "receiving") {
      // A receiver attached to a pass play represents a target.
      current.targets += 1;

      const complete = Number(play?.complete_pass || 0);
      if (complete === 1) current.receptions += 1;

      const yards = Number(play?.receiving_yards);
      if (Number.isFinite(yards)) current.receiving_yards += yards;

      current.receiving_tds += Number(play?.pass_touchdown || 0);

      const air = Number(play?.air_yards);
      if (Number.isFinite(air)) current.receiving_air_yards += air;

      const yac = Number(play?.yards_after_catch);
      if (Number.isFinite(yac)) current.receiving_yac += yac;
    }

    if (category === "rushing") {
      // Exclude kneels from normal rushing-attempt usage.
      if (Number(play?.qb_kneel || 0) !== 1) {
        current.carries += 1;

        const yards = Number(play?.rushing_yards);
        if (Number.isFinite(yards)) current.rushing_yards += yards;

        current.rushing_tds += Number(play?.rush_touchdown || 0);
      }
    }

    pbpGroups.set(key, current);
  }

  const epaPrefix = category;

  for (const item of pbpGroups.values()) {
    const row = upsert(
      {
        full_name: item.name,
        team: item.team,
        position: item.position,
        week: item.week,
      },
      "pbp_weekly"
    );
    if (!row) continue;

    if (item.epaPlays > 0) {
      row[`${epaPrefix}_epa`] = item.epa;
      row[`${epaPrefix}_epa_per_play`] = item.epa / item.epaPlays;
      row[`${epaPrefix}_epa_plays`] = item.epaPlays;
      row[`${epaPrefix}_success_rate`] = item.successes / item.epaPlays;
    }

    // Only fill box-score fields that are absent from NGS/PFR.
    // Existing weekly source values always win.
    const fillMissing = (key, value) => {
      if (
        (row[key] == null || row[key] === "") &&
        typeof value === "number" &&
        Number.isFinite(value)
      ) {
        row[key] = value;
      }
    };

    if (category === "passing") {
      fillMissing("attempts", item.attempts);
      fillMissing("completions", item.completions);
      fillMissing("passing_yards", item.passing_yards);
      fillMissing("passing_tds", item.passing_tds);
      fillMissing("passing_interceptions", item.passing_interceptions);
      if (situational && item.attempts > 0) {
        row.completion_percentage = 100 * item.completions / item.attempts;
      }
    } else if (category === "receiving") {
      const teamTotals = receivingTeamTotals.get(`${item.team}__${item.week}`);
      if (teamTotals?.targets > 0) row.target_share = item.targets / teamTotals.targets;
      if (teamTotals?.airYards > 0) row.air_yards_share = item.receiving_air_yards / teamTotals.airYards;
      fillMissing("targets", item.targets);
      fillMissing("receptions", item.receptions);
      fillMissing("receiving_yards", item.receiving_yards);
      fillMissing("receiving_tds", item.receiving_tds);
      fillMissing("receiving_air_yards", item.receiving_air_yards);
      if (item.receptions > 0) {
        fillMissing("avg_yac", item.receiving_yac / item.receptions);
      }
    } else if (category === "rushing") {
      fillMissing("carries", item.carries);
      fillMissing("rushing_yards", item.rushing_yards);
      fillMissing("rushing_tds", item.rushing_tds);
      if (item.carries > 0) {
        fillMissing("yards_per_carry", item.rushing_yards / item.carries);
      }
    }
  }

  // The Game Log already calculates these shares from ff_opportunity for the
  // full team. Use that same weekly source here, rather than a partial PBP or
  // the set of players present in the Receiving research table.
  if (category === "receiving") {
    for (const source of enrichFantasyScoring(gameLogRows)) {
      const id = unifiedIdentity(source);
      if (!id.name || !id.team || !id.week) continue;
      const row = byKey.get(keyFor(id));
      if (!row) continue;
      for (const metric of ["target_share", "air_yards_share"]) {
        if (typeof source[metric] === "number" && Number.isFinite(source[metric])) {
          row[metric] = source[metric];
        }
      }
    }
  }

  if (situational) return Array.from(byKey.values());

  // Create a CLEAN Week-0 Season Avg from actual weekly rows only.
  // No season-total source row is reused, so every numeric metric shown here
  // is genuinely the mean of its available Week 1-18 values.
  const weeklyGroups = new Map();

  for (const row of byKey.values()) {
    const week = researchWeek(row);
    if (week < 1 || week > 18) continue;

    const id = unifiedIdentity(row);
    const key = `${playerJoinName(id.name)}__${id.team}`;
    if (!weeklyGroups.has(key)) weeklyGroups.set(key, []);
    weeklyGroups.get(key).push(row);
  }

  for (const group of weeklyGroups.values()) {
    if (!group.length) continue;

    const first = group[0];
    const seasonRow = {
      full_name: researchName(first),
      team: researchTeam(first),
      position: researchPos(first),
      week: 0,
      _syntheticSeasonAverage: true,
      _gamesInAverage: group.length,
      _sources: ["weekly_average"],
    };

    const metricKeys = new Set(group.flatMap((row) => Object.keys(row)));

    for (const metric of metricKeys) {
      if (
        ["season", "week", "WEEK"].includes(metric) ||
        metric.startsWith("_")
      ) {
        continue;
      }

      const values = group
        .map((row) => row?.[metric])
        .filter((value) => typeof value === "number" && Number.isFinite(value));

      if (values.length) {
        seasonRow[metric] =
          values.reduce((sum, value) => sum + value, 0) / values.length;
      }
    }

    byKey.set(
      keyFor({
        name: seasonRow.full_name,
        team: seasonRow.team,
        week: 0,
      }),
      seasonRow
    );
  }

  return Array.from(byKey.values());
}

function combinePlayerResearchRows(categoryRows, fantasyRows = []) {
  const joined = new Map();
  for (const [category, rows] of [...categoryRows, ["fantasy", enrichFantasyScoring(fantasyRows)]]) {
    for (const row of rows) {
      const id = unifiedIdentity(row);
      if (!id.name || id.week < 1 || id.week > 18 || row._syntheticSeasonAverage) continue;
      if (row.season_type && String(row.season_type).toUpperCase() !== "REG") continue;
      const key = `${playerJoinName(id.name)}__${id.team}__${id.week}`;
      const current = joined.get(key) || { full_name: id.name, team: id.team, position: id.position, week: id.week };
      mergeMetricRow(current, row, category);
      // Preserve category-qualified names for numeric fields that can overlap.
      for (const [metric, value] of Object.entries(row)) {
        if (typeof value !== "number" || !Number.isFinite(value) || metric.startsWith("_") || RESEARCH_IDENTITY_KEYS.has(metric.toLowerCase()) || metric.toLowerCase().endsWith("_id")) continue;
        if (!metric.startsWith(`${category}_`)) current[`${category}_${metric}`] = value;
      }
      joined.set(key, current);
    }
  }
  return addSyntheticSeasonAverages([...joined.values()], "Advanced Passing");
}

function numericKeys(rows = []) {
  const keys = new Set();
  rows.forEach(r => Object.entries(r || {}).forEach(([k, v]) => {
    if (typeof v === "number" && Number.isFinite(v)) keys.add(k);
  }));
  return [...keys];
}


function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function gameLogOpponent(row = {}) {
  if (row.opponent) return normalizeTeamAbbr(row.opponent);
  const gameId = String(row.game_id || "");
  const parts = gameId.split("_");
  if (parts.length < 4) return "";
  const away = normalizeTeamAbbr(parts[parts.length - 2]);
  const home = normalizeTeamAbbr(parts[parts.length - 1]);
  const team = researchTeam(row);
  return team === away ? home : team === home ? away : "";
}

/**
 * DraftKings classic NFL offense:
 * full PPR; 300 passing / 100 rushing / 100 receiving bonuses.
 * FanDuel NFL offense:
 * half PPR; the same yardage bonuses; fumbles lost are -2.
 */
function calculateDkPoints(row = {}) {
  const passYards = numberValue(row.pass_yards_gained ?? row.passing_yards);
  const passTds = numberValue(row.pass_touchdown ?? row.passing_tds);
  const passInts = numberValue(row.pass_interception ?? row.passing_interceptions);
  const rushYards = numberValue(row.rush_yards_gained ?? row.rushing_yards);
  const rushTds = numberValue(row.rush_touchdown ?? row.rushing_tds);
  const recYards = numberValue(row.rec_yards_gained ?? row.receiving_yards);
  const recTds = numberValue(row.rec_touchdown ?? row.receiving_tds);
  const receptions = numberValue(row.receptions);
  const twoPoint =
    numberValue(row.pass_two_point_conv ?? row.passing_2pt_conversions)
    + numberValue(row.rush_two_point_conv ?? row.rushing_2pt_conversions)
    + numberValue(row.rec_two_point_conv ?? row.receiving_2pt_conversions);
  const fumblesLost =
    numberValue(row.rec_fumble_lost ?? row.receiving_fumbles_lost)
    + numberValue(row.rush_fumble_lost ?? row.rushing_fumbles_lost);

  return (
    passYards * 0.04
    + passTds * 4
    - passInts
    + rushYards * 0.1
    + rushTds * 6
    + recYards * 0.1
    + recTds * 6
    + receptions
    + twoPoint * 2
    - fumblesLost
    + (passYards >= 300 ? 3 : 0)
    + (rushYards >= 100 ? 3 : 0)
    + (recYards >= 100 ? 3 : 0)
  );
}

function calculateFdPoints(row = {}) {
  const passYards = numberValue(row.pass_yards_gained ?? row.passing_yards);
  const passTds = numberValue(row.pass_touchdown ?? row.passing_tds);
  const passInts = numberValue(row.pass_interception ?? row.passing_interceptions);
  const rushYards = numberValue(row.rush_yards_gained ?? row.rushing_yards);
  const rushTds = numberValue(row.rush_touchdown ?? row.rushing_tds);
  const recYards = numberValue(row.rec_yards_gained ?? row.receiving_yards);
  const recTds = numberValue(row.rec_touchdown ?? row.receiving_tds);
  const receptions = numberValue(row.receptions);
  const twoPoint =
    numberValue(row.pass_two_point_conv ?? row.passing_2pt_conversions)
    + numberValue(row.rush_two_point_conv ?? row.rushing_2pt_conversions)
    + numberValue(row.rec_two_point_conv ?? row.receiving_2pt_conversions);
  const fumblesLost =
    numberValue(row.rec_fumble_lost ?? row.receiving_fumbles_lost)
    + numberValue(row.rush_fumble_lost ?? row.rushing_fumbles_lost);

  return (
    passYards * 0.04
    + passTds * 4
    - passInts
    + rushYards * 0.1
    + rushTds * 6
    + recYards * 0.1
    + recTds * 6
    + receptions * 0.5
    + twoPoint * 2
    - fumblesLost * 2
    + (passYards >= 300 ? 3 : 0)
    + (rushYards >= 100 ? 3 : 0)
    + (recYards >= 100 ? 3 : 0)
  );
}


function calculateCustomFantasyPoints(row = {}, receptionPoints = 1, passingTdPoints = 4) {
  const passYards = numberValue(row.pass_yards_gained ?? row.passing_yards);
  const passTds = numberValue(row.pass_touchdown ?? row.passing_tds);
  const passInts = numberValue(row.pass_interception ?? row.passing_interceptions);
  const rushYards = numberValue(row.rush_yards_gained ?? row.rushing_yards);
  const rushTds = numberValue(row.rush_touchdown ?? row.rushing_tds);
  const recYards = numberValue(row.rec_yards_gained ?? row.receiving_yards);
  const recTds = numberValue(row.rec_touchdown ?? row.receiving_tds);
  const receptions = numberValue(row.receptions);
  const twoPoint =
    numberValue(row.pass_two_point_conv ?? row.passing_2pt_conversions)
    + numberValue(row.rush_two_point_conv ?? row.rushing_2pt_conversions)
    + numberValue(row.rec_two_point_conv ?? row.receiving_2pt_conversions);
  const fumblesLost =
    numberValue(row.rec_fumble_lost ?? row.receiving_fumbles_lost)
    + numberValue(row.rush_fumble_lost ?? row.rushing_fumbles_lost);

  return (
    passYards * 0.04
    + passTds * passingTdPoints
    - passInts
    + rushYards * 0.1
    + rushTds * 6
    + recYards * 0.1
    + recTds * 6
    + receptions * receptionPoints
    + twoPoint * 2
    - fumblesLost * 2
  );
}

function enrichCustomScoring(rows = [], receptionPoints = 1, passingTdPoints = 4) {
  return rows.map((row) => ({
    ...row,
    custom_points: calculateCustomFantasyPoints(row, receptionPoints, passingTdPoints),
  }));
}

function enrichFantasyScoring(rows = []) {
  if (!Array.isArray(rows) || !rows.length) return [];

  const teamTotals = new Map();
  for (const row of rows) {
    const week = researchWeek(row);
    const team = researchTeam(row);
    if (!team || !week) continue;
    const key = `${row.season || ""}__${week}__${team}`;
    const current = teamTotals.get(key) || { targets: 0, airYards: 0 };
    current.targets += numberValue(row.rec_attempt ?? row.targets);
    current.airYards += numberValue(row.rec_air_yards ?? row.receiving_air_yards);
    teamTotals.set(key, current);
  }

  const scored = rows.map((row) => {
    const week = researchWeek(row);
    const team = researchTeam(row);
    const key = `${row.season || ""}__${week}__${team}`;
    const totals = teamTotals.get(key) || { targets: 0, airYards: 0 };
    const attempts = numberValue(row.pass_attempt ?? row.attempts);
    const completions = numberValue(row.pass_completions ?? row.completions);
    const rushAttempts = numberValue(row.rush_attempt ?? row.carries);
    const rushYards = numberValue(row.rush_yards_gained ?? row.rushing_yards);
    const targets = numberValue(row.rec_attempt ?? row.targets);
    const airYards = numberValue(row.rec_air_yards ?? row.receiving_air_yards);

    return {
      ...row,
      opponent: gameLogOpponent(row),
      completion_percentage: attempts > 0 ? (completions / attempts) * 100 : 0,
      yards_per_carry: rushAttempts > 0 ? rushYards / rushAttempts : 0,
      target_share: Number.isFinite(Number(row.target_share)) && row.target_share != null
        ? Number(row.target_share) : totals.targets > 0 ? targets / totals.targets : null,
      air_yards_share: Number.isFinite(Number(row.air_yards_share)) && row.air_yards_share != null
        ? Number(row.air_yards_share) : totals.airYards > 0 ? airYards / totals.airYards : null,
      dk_points: calculateDkPoints(row),
      fd_points: calculateFdPoints(row),
    };
  });

  // Unified player season rows are constructed before the weekly scoring pass.
  // Their shares must use the resulting weekly shares, including those derived
  // here from team totals when the source did not provide a share column.
  const weeklyShares = new Map();
  for (const row of scored) {
    const week = researchWeek(row);
    if (week < 1 || week > 18 || row._syntheticSeasonAverage) continue;
    const key = `${playerJoinName(researchName(row))}__${researchTeam(row)}`;
    if (!weeklyShares.has(key)) weeklyShares.set(key, []);
    weeklyShares.get(key).push(row);
  }
  for (const row of scored) {
    if (!row._syntheticSeasonAverage) continue;
    const key = `${playerJoinName(researchName(row))}__${researchTeam(row)}`;
    const games = weeklyShares.get(key) || [];
    for (const metric of ["target_share", "air_yards_share"]) {
      const values = games.map((game) => game[metric])
        .filter((value) => typeof value === "number" && Number.isFinite(value));
      if (values.length) {
        row[metric] = values.reduce((sum, value) => sum + value, 0) / values.length;
      }
    }
  }
  return scored;
}

const TEAM_BOX_POSITIONS = ["QB", "RB", "WR", "TE"];
const TEAM_BOX_METRICS = {
  QB: [["snap_pct", "Snap %"], ["pass_attempt", "Pass Att"], ["pass_yards_gained", "Pass Yds"], ["rush_attempt", "Rush Att"], ["epa", "EPA"], ["xfp", "Expected FP"], ["fp", "FP"]],
  RB: [["snap_pct", "Snap %"], ["rush_attempt", "Rush Att"], ["rush_yards_gained", "Rush Yds"], ["rec_attempt", "Targets"], ["receptions", "Rec"], ["rec_yards_gained", "Rec Yds"], ["target_share", "Tgt %"], ["air_yards_share", "Air %"], ["epa", "EPA"], ["xfp", "Expected FP"], ["fp", "FP"]],
  WR: [["snap_pct", "Snap %"], ["rec_attempt", "Targets"], ["target_share", "Tgt %"], ["air_yards_share", "Air %"], ["receptions", "Rec"], ["rec_yards_gained", "Rec Yds"], ["epa", "EPA"], ["xfp", "Expected FP"], ["fp", "FP"]],
  TE: [["snap_pct", "Snap %"], ["rec_attempt", "Targets"], ["target_share", "Tgt %"], ["air_yards_share", "Air %"], ["receptions", "Rec"], ["rec_yards_gained", "Rec Yds"], ["epa", "EPA"], ["xfp", "Expected FP"], ["fp", "FP"]],
};

function teamBoxTableGroups(position, weekly = false, seasonAverage = false) {
  const buckets = new Map([[weekly ? "Game" : "Player", [{key: weekly ? "week" : "full_name", label: weekly ? "Week" : "Player", identity: !weekly}, ...(!weekly && seasonAverage ? [{key: "games", label: "G"}] : [])]]]);
  for (const [key, label] of TEAM_BOX_METRICS[position] || []) {
    const group = ["fp","xfp"].includes(key) ? "Fantasy" : key === "epa" ? "Efficiency" : /snap|share/.test(key) ? "Usage" : /pass_/.test(key) ? "Passing" : /rush_/.test(key) ? "Rushing" : "Receiving";
    if (!buckets.has(group)) buckets.set(group, []);
    buckets.get(group).push({key, label: ({pass_attempt: "Att", pass_yards_gained: "Yds", rush_attempt: "Att", rush_yards_gained: "Yds", rec_attempt: "Tgt", rec_yards_gained: "Yds", xfp: "xFP"})[key] || label});
  }
  return [...buckets].map(([label, columns]) => ({label, columns}));
}

function buildTeamBoxRows(opportunityRows = [], snapRows = [], pbpRows = []) {
  const snapByPlayer = new Map();
  for (const snap of snapRows) {
    if (String(snap.game_type || "REG").toUpperCase() !== "REG") continue;
    const key = `${playerJoinName(snap.player)}__${researchTeam(snap)}__${researchWeek(snap)}`;
    const raw = Number(snap.offense_pct);
    if (Number.isFinite(raw) && snap.offense_pct != null) {
      snapByPlayer.set(key, raw > 1 ? raw / 100 : raw);
    }
  }

  const epaByPlayer = new Map();
  for (const play of pbpRows) {
    const week = researchWeek(play);
    const team = normalizeTeamAbbr(play.posteam || "");
    const epa = Number(play.epa);
    if (!team || !week || !Number.isFinite(epa)) continue;
    if (play.season_type && String(play.season_type).toUpperCase() !== "REG") continue;
    for (const role of ["passer", "rusher", "receiver"]) {
      const id = String(play[`${role}_player_id`] || "").trim();
      const name = playerJoinName(play[`${role}_player_name`] || "");
      if (!id && !name) continue;
      for (const key of [id ? `id__${id}__${team}__${week}` : "", name ? `name__${name}__${team}__${week}` : ""]) {
        if (key) epaByPlayer.set(key, (epaByPlayer.get(key) || 0) + epa);
      }
    }
  }

  return enrichFantasyScoring(opportunityRows)
    .filter((row) => TEAM_BOX_POSITIONS.includes(researchPos(row)) && researchWeek(row) >= 1 && researchWeek(row) <= 18)
    .map((row) => {
      const team = researchTeam(row);
      const name = researchName(row);
      const week = researchWeek(row);
      const playerId = String(row.player_id || row.player_gsis_id || "").trim();
      const identity = `${playerJoinName(name)}__${team}__${week}`;
      const epaId = `id__${playerId}__${team}__${week}`;
      const epaName = `name__${playerJoinName(name)}__${team}__${week}`;
      const epaShortName = `name__${playerJoinName(shortPlayerName(name))}__${team}__${week}`;
      const expected = row.total_fantasy_points_exp;
      const actual = row.total_fantasy_points;
      return {
        ...row,
        full_name: name,
        team,
        position: researchPos(row),
        week,
        snap_pct: snapByPlayer.get(identity) ?? null,
        epa: (playerId && epaByPlayer.has(epaId) ? epaByPlayer.get(epaId) : epaByPlayer.get(epaName) ?? epaByPlayer.get(epaShortName)) ?? null,
        xfp: expected == null || !Number.isFinite(Number(expected)) ? null : Number(expected),
        fp: actual == null || !Number.isFinite(Number(actual)) ? null : Number(actual),
      };
    });
}

function TeamBoxScoreView({ rows, season, loading }) {
  const teams = useMemo(() => Array.from(new Set(rows.map(researchTeam).filter(Boolean))).sort(), [rows]);
  const [team, setTeam] = useState("");
  const [week, setWeek] = useState("SEASON");
  const [search, setSearch] = useState("");
  const [selectedPlayerKey, setSelectedPlayerKey] = useState("");
  // Warm local logos while the team data is loading so the first logo and
  // subsequent team changes are ready as soon as the box score appears.
  useEffect(() => {
    const images = Object.keys(TEAM_NAMES).map((code) => {
      const img = new Image();
      img.src = nflLogoUrl(code);
      return img;
    });
    return () => { images.length = 0; };
  }, []);
  const boxPlayerKey = (row) => `${row.player_id || playerJoinName(researchName(row))}__${researchPos(row)}__${researchTeam(row)}`;
  useEffect(() => {
    if (teams.length && !teams.includes(team)) setTeam(teams[0]);
  }, [teams, team]);
  const weeks = useMemo(() => Array.from(new Set(rows.filter((row) => researchTeam(row) === team).map(researchWeek))).sort((a, b) => a - b), [rows, team]);
  useEffect(() => {
    if (week !== "SEASON" && !weeks.includes(Number(week))) setWeek("SEASON");
  }, [week, weeks]);

  const displayed = useMemo(() => {
    const eligible = rows.filter((row) => researchTeam(row) === team);
    if (week !== "SEASON") return eligible.filter((row) => researchWeek(row) === Number(week));
    const byPlayer = new Map();
    for (const row of eligible) {
      const key = `${row.player_id || playerJoinName(researchName(row))}__${researchPos(row)}`;
      if (!byPlayer.has(key)) byPlayer.set(key, []);
      byPlayer.get(key).push(row);
    }
    return Array.from(byPlayer.values()).map((games) => {
      const avg = { ...games[0], week: 0, games: games.length };
      for (const metric of new Set(Object.values(TEAM_BOX_METRICS).flat().map(([key]) => key))) {
        const values = games.map((game) => game[metric]).filter((value) => typeof value === "number" && Number.isFinite(value));
        avg[metric] = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
      }
      return avg;
    });
  }, [rows, team, week]);
  const needle = search.trim().toLowerCase();
  const visible = needle ? displayed.filter((row) => researchName(row).toLowerCase().includes(needle)) : displayed;
  const playerWeeks = selectedPlayerKey ? rows.filter((row) => researchTeam(row) === team && boxPlayerKey(row) === selectedPlayerKey).sort((a, b) => a.week - b.week) : [];
  const playerDetail = playerWeeks[0];
  const teamGames = week === "SEASON" ? weeks.length : weeks.includes(Number(week)) ? 1 : 0;
  const teamWeekRows = rows.filter((row) => researchTeam(row) === team && (week === "SEASON" || researchWeek(row) === Number(week)));
  const teamTotal = (metric, position = "") => {
    const values = teamWeekRows.filter((row) => !position || researchPos(row) === position)
      .map((row) => row[metric]).filter((value) => typeof value === "number" && Number.isFinite(value));
    return values.length && teamGames ? values.reduce((sum, value) => sum + value, 0) / teamGames : null;
  };
  const boxValue = (value, metric, perGame = week === "SEASON") => value == null ? "—" : ["snap_pct", "target_share", "air_yards_share"].includes(metric)
    ? `${(value * 100).toFixed(1)}%` : Number(value).toFixed(perGame || ["epa", "xfp", "fp"].includes(metric) ? 1 : 0);
  const exportBox = () => exportToCsv(`nfl_team_box_score_${team}_${season}_${week.toLowerCase()}.csv`, visible, ["full_name", "team", "position", "week", ...(week === "SEASON" ? ["games"] : []), ...new Set(Object.values(TEAM_BOX_METRICS).flat().map(([key]) => key))]);

  return <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/95" style={{ borderTop: `4px solid ${colorForTeam(team)}` }}>
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 p-3 sm:p-5">
      <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-slate-700 bg-white/95 p-2">{team ? <img src={nflLogoUrl(team)} alt="" fetchPriority="high" decoding="async" className="h-12 w-12 object-contain" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}</div>
      <div className="mr-auto"><div className="text-xs font-bold uppercase tracking-widest text-sky-400">Team Player Box Score · {season}</div><h2 className="text-2xl font-extrabold">{TEAM_NAMES[team] || team || "Select a team"}</h2><p className="text-xs text-slate-400">{week === "SEASON" ? `${teamGames} regular season games · per-game averages` : `Week ${week}`} · PPR scoring · Expected FP from the opportunity data</p></div>
      <select value={team} onChange={(event) => { setTeam(event.target.value); setSelectedPlayerKey(""); }} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm" aria-label="Team">{teams.map((code) => <option key={code} value={code}>{TEAM_NAMES[code] || code}</option>)}</select>
      <select value={week} onChange={(event) => setWeek(event.target.value)} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm" aria-label="Week"><option value="SEASON">Season Avg</option>{weeks.map((number) => <option key={number} value={number}>Week {number}</option>)}</select>
      <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find player" className="w-32 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-sky-500" />
      <button onClick={exportBox} disabled={!visible.length} className="rounded-lg border border-sky-500/40 px-3 py-2 text-xs font-bold text-sky-300 disabled:opacity-40">Export CSV</button>
    </div>
    {teamGames > 0 && <div className="grid grid-cols-2 gap-2 border-b border-slate-800 p-3 sm:grid-cols-4 sm:p-4">{[
      ["Pass attempts", teamTotal("pass_attempt", "QB")],
      ["Rush attempts", teamTotal("rush_attempt")],
      ["Targets", teamTotal("rec_attempt")],
      ["Expected FP", teamTotal("xfp")],
    ].map(([label, value]) => <div key={label} className="rounded-lg bg-slate-900/75 px-3 py-2"><div className="text-[11px] font-semibold text-slate-400">{label}{week === "SEASON" ? " / game" : ""}</div><div className="text-xl font-bold tabular-nums">{value == null ? "—" : value.toFixed(1)}</div></div>)}</div>}
    {loading ? <p className="p-5 text-sm text-slate-400">Loading team data…</p> : !visible.length ? <p className="p-5 text-sm text-slate-400">No QB, RB, WR, or TE rows for this selection.</p> :
      <div className="grid gap-4 p-3 sm:p-4">{TEAM_BOX_POSITIONS.map((pos) => {
        const volumeKey = pos === "QB" ? "pass_attempt" : pos === "RB" ? "rush_attempt" : "rec_attempt";
        const players = visible.filter((row) => researchPos(row) === pos).sort((a, b) =>
          (Number(b[volumeKey] ?? -1) - Number(a[volumeKey] ?? -1)) ||
          (Number(b.snap_pct ?? -1) - Number(a.snap_pct ?? -1)) ||
          researchName(a).localeCompare(researchName(b))
        );
        return <div key={pos} className="min-w-0 overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-2"><span className="rounded-md px-2 py-1 text-xs font-black text-white" style={{ backgroundColor: colorForTeam(team) }}>{pos}</span><span className="font-bold">{POS_LONG[pos]}</span><span className="ml-auto text-xs text-slate-400">{players.length} players</span></div>
          <div className="max-h-[420px] overflow-auto"><GroupedStatTable groups={teamBoxTableGroups(pos, false, week === "SEASON")} rows={players} caption={`${team} ${pos} box score`} onOpen={row => setSelectedPlayerKey(current => current === boxPlayerKey(row) ? "" : boxPlayerKey(row))} renderCell={(row,key) => key === "games" ? row.games : boxValue(row[key],key)} /></div>
          {!players.length && <p className="px-3 py-4 text-xs text-slate-500">No {pos} data.</p>}
        </div>;
      })}</div>}
    {playerDetail && <div className="border-t border-slate-800 p-3 sm:p-4">
      <div className="mb-2 flex items-center justify-between"><div><h3 className="font-bold">{researchName(playerDetail)} · weekly detail</h3><p className="text-xs text-slate-400">Select another player above to compare weeks</p></div><button onClick={() => setSelectedPlayerKey("")} className="rounded-lg px-2 py-1 text-xs text-slate-400 hover:bg-slate-800">Close</button></div>
      <div className="overflow-x-auto rounded-lg border border-slate-800"><GroupedStatTable groups={teamBoxTableGroups(researchPos(playerDetail), true)} rows={playerWeeks} caption={`${researchName(playerDetail)} weekly team box score`} renderCell={(row,key) => key === "week" ? row.week : boxValue(row[key],key,false)} /></div>
    </div>}
  </section>;
}

const SYNTHETIC_SEASON_AVG = "SEASON_AVG";
const SYNTHETIC_SEASON_TOTAL = "SEASON_TOTAL";
const SYNTHETIC_SEASON_PACE = "SEASON_PACE_17";
const WEEKLY_RESEARCH_TABS = new Set([
  "Fantasy Opportunity",
  "Advanced Passing",
  "Advanced Receiving",
  "Advanced Rushing",
  "Advanced Defense",
]);

/**
 * Build one regular-season per-game average row per player/team.
 * Only Weeks 1-18 are included; Week 19+ is postseason and is intentionally excluded.
 * Existing source rows are never mutated and a real Week 0 row always wins.
 */
function addSyntheticSeasonAverages(rows = [], tab = "") {
  if (!WEEKLY_RESEARCH_TABS.has(tab) || !rows.length) return rows;

  const hasPlayerRows = rows.some((r) => researchName(r));
  const hasWeeklyRows = rows.some((r) => {
    const w = researchWeek(r);
    return w >= 1 && w <= 18;
  });
  if (!hasPlayerRows || !hasWeeklyRows) return rows;

  const realWeekZeroKeys = new Set(
    rows
      .filter((r) => researchWeek(r) === 0 && researchName(r))
      .map((r) => [
        String(r.player_id ?? r.player_gsis_id ?? r.pfr_player_id ?? researchName(r)).toLowerCase(),
        researchTeam(r),
        researchPos(r),
      ].join("__"))
  );

  const groups = new Map();
  for (const row of rows) {
    const week = researchWeek(row);
    if (week < 1 || week > 18) continue;

    const seasonType = String(row?.season_type ?? row?.game_type ?? "REG").toUpperCase();
    if (seasonType && !["REG", "REGULAR", "REGULAR SEASON"].includes(seasonType)) continue;

    const name = researchName(row);
    if (!name) continue;
    const playerKey = String(row.player_id ?? row.player_gsis_id ?? row.pfr_player_id ?? name).toLowerCase();
    const key = [playerKey, researchTeam(row), researchPos(row)].join("__");
    if (realWeekZeroKeys.has(key)) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }

  const synthetic = [];
  for (const groupRows of groups.values()) {
    if (!groupRows.length) continue;
    const first = groupRows[0];
    const avgRow = { ...first, _syntheticSeasonAverage: true, _gamesInAverage: groupRows.length };

    // Average every actual metric while preserving IDs and descriptive fields from the first row.
    const numeric = new Set();
    groupRows.forEach((r) => Object.entries(r || {}).forEach(([key, value]) => {
      if (typeof value === "number" && Number.isFinite(value)) numeric.add(key);
    }));

    for (const key of numeric) {
      const lower = key.toLowerCase();
      if (RESEARCH_IDENTITY_KEYS.has(lower) || lower.endsWith("_id") || lower === "season") continue;
      const vals = groupRows.map((r) => r?.[key]).filter((v) => typeof v === "number" && Number.isFinite(v));
      if (vals.length) avgRow[key] = vals.reduce((sum, value) => sum + value, 0) / vals.length;
    }

    if (Object.prototype.hasOwnProperty.call(avgRow, "week")) avgRow.week = 0;
    if (Object.prototype.hasOwnProperty.call(avgRow, "WEEK")) avgRow.WEEK = 0;
    if (Object.prototype.hasOwnProperty.call(avgRow, "game_id")) avgRow.game_id = SYNTHETIC_SEASON_AVG;
    if (Object.prototype.hasOwnProperty.call(avgRow, "pfr_game_id")) avgRow.pfr_game_id = SYNTHETIC_SEASON_AVG;
    if (Object.prototype.hasOwnProperty.call(avgRow, "opponent")) avgRow.opponent = "Season Avg";
    synthetic.push(avgRow);
  }

  return [...rows, ...synthetic];
}
/** Build totals from weekly records, never from synthetic per-game averages. */
function addResearchSeasonTotals(rows = [], tab = "") {
  if (!WEEKLY_RESEARCH_TABS.has(tab) && !["Passing", "Receiving", "Rushing"].includes(tab)) return rows;
  const groups = new Map();
  for (const row of rows) {
    const week = researchWeek(row);
    if (week < 1 || week > 18 || !researchName(row) || row._syntheticSeasonTotal) continue;
    const type = String(row.season_type ?? row.game_type ?? "REG").toUpperCase();
    if (type && !["REG", "REGULAR", "REGULAR SEASON"].includes(type)) continue;
    const key = researchGatePlayerKey(row);
    if (!groups.has(key)) groups.set(key, new Map());
    // Weekly joins already represent one record per player/team/week.
    groups.get(key).set(week, row);
  }
  const totals = [];
  for (const weekly of groups.values()) {
    const games = [...weekly.values()];
    const total = { ...games[0], week: 0, _syntheticSeasonAverage: false,
      _syntheticSeasonTotal: true, _gamesInAverage: games.length, games: games.length };
    if ("WEEK" in total) total.WEEK = 0;
    if ("game_id" in total) total.game_id = SYNTHETIC_SEASON_TOTAL;
    if ("pfr_game_id" in total) total.pfr_game_id = SYNTHETIC_SEASON_TOTAL;
    if ("opponent" in total) total.opponent = "Season Total";
    const valid = value => typeof value === "number" && Number.isFinite(value);
    const sum = key => games.reduce((acc, row) => acc + (valid(row[key]) ? row[key] : 0), 0);
    for (const key of numericKeys(games)) {
      const name = key.toLowerCase();
      if (key.startsWith("_") || RESEARCH_IDENTITY_KEYS.has(name) || name.endsWith("_id") || name === "games") continue;
      const values = games.map(row => row[key]).filter(valid);
      // Percentages, averages, ratings, shares and efficiency cannot be summed.
      const isRate = /pct|percentage|percent|rate|share|per_|_avg|^avg_|average|rating|_rat$|efficiency|wopr|cpoe|time_to_throw|separation|cushion|probability/.test(name);
      const isTotal = !isRate && (isResearchVolumeMetric(key) ||
        /(?:^|_)(?:yards|yardage|tds?|touchdowns?|interceptions?|fumbles?|sacks?|epa|points|xfp|drops?|broken_tackles|pressures|blitzed|hurried|hit|sacked|bad_throws|two_point_conv|2pt_conversions)(?:_|$)/.test(name));
      total[key] = /(?:^|_)(?:max|longest)(?:_|$)/.test(name) ? Math.max(...values)
        : isTotal ? sum(key) : values.reduce((a, b) => a + b, 0) / values.length;
      // Recompute EPA/play and success using the recorded eligible EPA plays.
      const prefix = name.match(/^(.*)_(?:epa_per_play|success_rate)$/)?.[1];
      if (prefix && sum(`${prefix}_epa_plays`) > 0) {
        if (name.endsWith("epa_per_play")) total[key] = sum(`${prefix}_epa`) / sum(`${prefix}_epa_plays`);
        else {
          const eligible = games.filter(row => valid(row[key]) && valid(row[`${prefix}_epa_plays`]) && row[`${prefix}_epa_plays`] > 0);
          const denominator = eligible.reduce((n, row) => n + row[`${prefix}_epa_plays`], 0);
          total[key] = denominator ? eligible.reduce((n, row) => n + row[key] * row[`${prefix}_epa_plays`], 0) / denominator : null;
        }
      }
    }
    if (sum("attempts") > 0 && games.some(row => valid(row.completions))) total.completion_percentage = 100 * sum("completions") / sum("attempts");
    if (sum("carries") > 0 && games.some(row => valid(row.rushing_yards))) total.yards_per_carry = sum("rushing_yards") / sum("carries");
    totals.push(total);
  }
  return [...rows.filter(row => !row._syntheticSeasonTotal), ...totals];
}

/** Extrapolate counting stats from per-game averages; never inflate rates or records. */
function addResearchSeasonPace(rows = []) {
  const averages = new Map(rows.filter(row => row._syntheticSeasonAverage)
    .map(row => [researchGatePlayerKey(row), row]));
  // Mixed chart datasets may not include average rows for every category.
  const weeklyByPlayer = new Map();
  for (const row of rows) {
    const week = researchWeek(row);
    if (week < 1 || week > 18 || row._syntheticSeasonAverage || row._syntheticSeasonTotal || row._syntheticSeasonPace) continue;
    const type = String(row.season_type ?? row.game_type ?? "REG").toUpperCase();
    if (!["REG", "REGULAR", "REGULAR SEASON"].includes(type)) continue;
    const key = researchGatePlayerKey(row);
    if (!weeklyByPlayer.has(key)) weeklyByPlayer.set(key, new Map());
    weeklyByPlayer.get(key).set(week, row);
  }
  for (const [key, weekly] of weeklyByPlayer) {
    if (averages.has(key)) continue;
    const games = [...weekly.values()], average = {};
    for (const metric of numericKeys(games)) {
      const values = games.map(row => row[metric]).filter(value => typeof value === "number" && Number.isFinite(value));
      if (values.length) average[metric] = values.reduce((sum, value) => sum + value, 0) / values.length;
    }
    averages.set(key, average);
  }
  const paceRows = rows.filter(row => row._syntheticSeasonTotal && row._gamesInAverage > 0).map(total => {
    const average = averages.get(researchGatePlayerKey(total));
    const pace = { ...total, ...(average || {}), week: 0, games: 17,
      _syntheticSeasonAverage: false, _syntheticSeasonTotal: false,
      _syntheticSeasonPace: true, _gamesInAverage: total._gamesInAverage };
    if ("WEEK" in pace) pace.WEEK = 0;
    if ("game_id" in pace) pace.game_id = SYNTHETIC_SEASON_PACE;
    if ("pfr_game_id" in pace) pace.pfr_game_id = SYNTHETIC_SEASON_PACE;
    if ("opponent" in pace) pace.opponent = "17 Game Pace";
    for (const key of numericKeys([total])) {
      const name = key.toLowerCase();
      if (key.startsWith("_") || RESEARCH_IDENTITY_KEYS.has(name) || name.endsWith("_id") || name === "games") continue;
      if (/(?:^|_)(?:max|longest)(?:_|$)/.test(name)) { pace[key] = total[key]; continue; }
      const isRate = /pct|percentage|percent|rate|share|per_|_avg|^avg_|average|rating|_rat$|efficiency|wopr|cpoe|time_to_throw|separation|cushion|probability/.test(name);
      const isCount = !isRate && (isResearchVolumeMetric(key) ||
        /(?:^|_)(?:yards|yardage|tds?|touchdowns?|interceptions?|fumbles?|sacks?|epa|points|xfp|drops?|drop|broken_tackles|tackles|assists|pressures|blitzed|hurried|hit|sacked|bad_throws|two_point_conv|2pt_conversions)(?:_|$)/.test(name));
      if (isCount) {
        const perGame = typeof average?.[key] === "number" && Number.isFinite(average[key])
          ? average[key] : total[key] / total._gamesInAverage;
        pace[key] = perGame * 17;
      }
    }
    return pace;
  });
  return [...rows.filter(row => !row._syntheticSeasonPace), ...paceRows];
}

function researchQualification(rows, tab, percent = 10) {
  const candidates = { Passing: ["attempts", "passing_attempts", "pass_attempt"],
    Receiving: ["targets", "receiving_targets", "rec_attempt"],
    Rushing: ["carries", "rushing_carries", "rush_attempt"] }[tab];
  if (!candidates) return null;
  const metric = candidates.find(key => rows.some(row => researchWeek(row) > 0 && typeof row[key] === "number" && row[key] > 0));
  if (!metric) return null;
  const totals = buildResearchGateTotals(rows, metric);
  const positive = [...totals.values()].filter(value => value > 0);
  if (!positive.length) return null;
  const average = positive.reduce((a, b) => a + b, 0) / positive.length;
  return { metric, totals, average, minimum: Math.max(1, Math.ceil(average * percent / 100)) };
}

function defaultResearchColumns(tab, keys) {
  const preferredByTab = {
    "Passing": ["full_name","team","position","week","passing_epa","passing_epa_per_play","passing_success_rate","attempts","completions","passing_yards","passing_tds","passing_bad_throw_pct","times_pressured_pct","avg_time_to_throw","passer_rating"],
    "Receiving": ["full_name","team","position","week","receiving_epa","receiving_epa_per_play","receiving_success_rate","targets","receptions","receiving_yards","receiving_tds","target_share","air_yards_share","receiving_drop_pct","avg_yac"],
    "Rushing": ["full_name","team","position","week","rushing_epa","rushing_epa_per_play","rushing_success_rate","carries","rushing_yards","rushing_tds","yards_per_carry","rushing_yards_after_contact_avg","rushing_broken_tackles","efficiency"],
    "Fantasy Opportunity": ["full_name","posteam","position","week","dk_points","fd_points","total_fantasy_points","total_fantasy_points_exp","total_fantasy_points_diff","total_yards_gained","total_yards_gained_exp","total_touchdown","total_touchdown_exp","rec_attempt","rush_attempt","pass_attempt"],
    "Seasonal Stats": ["player_display_name","recent_team","position","games","fantasy_points_ppr","fantasy_points","target_share","air_yards_share","wopr","passing_epa","rushing_epa","receiving_epa","attempts","carries","targets","receptions"],
    "Advanced Passing": ["pfr_player_name","team","opponent","week","passing_bad_throws","passing_bad_throw_pct","passing_drops","passing_drop_pct","times_blitzed","times_hurried","times_hit","times_pressured","times_pressured_pct","times_sacked"],
    "Advanced Receiving": ["pfr_player_name","team","opponent","week","receiving_broken_tackles","receiving_drop","receiving_drop_pct","receiving_int","receiving_rat"],
    "Advanced Rushing": ["pfr_player_name","team","opponent","week","carries","rushing_yards_before_contact","rushing_yards_before_contact_avg","rushing_yards_after_contact","rushing_yards_after_contact_avg","rushing_broken_tackles"],
    "Advanced Defense": ["pfr_player_name","team","opponent","week","def_targets","def_completions_allowed","def_completion_pct","def_yards_allowed","def_yards_allowed_per_tgt","def_receiving_td_allowed","def_passer_rating_allowed","def_pressures","def_missed_tackle_pct"],
    "FTN Charting": ["nflverse_game_id","week","ftn_play_id","nflverse_play_id","qb_location","n_offense_backfield","n_defense_box","is_motion","is_play_action","is_screen_pass","is_rpo","is_no_huddle","is_qb_out_of_pocket","is_interception_worthy","is_catchable_ball","is_contested_ball","is_drop","n_blitzers","n_pass_rushers","is_qb_fault_sack"],
  };
  const preferred = preferredByTab[tab] || [];
  const set = new Set(keys);
  return preferred.filter((k) => set.has(k)).slice(0, 16);
}

function researchCellValue(row, key) {
  if (key === "total_fantasy_points_diff" && typeof row?.[key] === "number" && Number.isFinite(row[key])) return <span className={row[key] > 0 ? "font-bold text-emerald-300" : row[key] < 0 ? "font-bold text-rose-300" : "text-slate-400"}>{row[key] > 0 ? "+" : ""}{row[key].toFixed(1)}</span>;
  if (row?._syntheticSeasonPace && ["week", "WEEK"].includes(key)) return "17 Game Pace";
  if (row?._syntheticSeasonTotal && ["week", "WEEK"].includes(key)) return "Season Total";
  if (row?._syntheticSeasonAverage && ["week", "WEEK"].includes(key)) return "Season Avg";
  return formatStat(row?.[key], key);
}

function ResearchTooltip({ active, payload, xKey, yKey }) {
  if (!active || !payload?.length) return null;
  const r = payload[0]?.payload;
  if (!r) return null;
  return <div className="min-w-[220px] rounded-xl border border-slate-600 bg-slate-950 p-3 text-xs shadow-2xl">
    <div className="font-extrabold text-white">{r._fullName || "Team / Play"}</div>
    <div className="mb-2 text-slate-400">{[r._team, r._position, r._week ? `Week ${r._week}` : ""].filter(Boolean).join(" • ")}</div>
    <div><span className="text-slate-400">{prettyHeader(xKey)}:</span> <b>{formatStat(r[xKey], xKey)}</b></div>
    <div><span className="text-slate-400">{prettyHeader(yKey)}:</span> <b>{formatStat(r[yKey], yKey)}</b></div>
  </div>;
}

const RESEARCH_METRIC_CATEGORIES = ["Passing", "Rushing", "Receiving", "Fantasy", "Defense", "Other"];

function researchMetricCategory(key) {
  const name = String(key).toLowerCase();
  if (/^(?:fantasy_|dk_|fd_)|fantasy|(^|_)xfp($|_)/.test(name)) return "Fantasy";
  if (/(?:^|_)(?:def|defense|defensive)(?:_|$)|pressures|missed_tackle/.test(name)) return "Defense";
  if (/^(?:passing_|pass_)/.test(name)) return "Passing";
  if (/^(?:rushing_|rush_)/.test(name)) return "Rushing";
  if (/^(?:receiving_|rec_)/.test(name)) return "Receiving";
  if (/pass|completion|interception|blitz|hurr|times_hit|times_sack|time_to_throw|air_distance/.test(name) || name === "attempts") return "Passing";
  if (/rush|carr|yards_per_carry|efficiency/.test(name)) return "Rushing";
  if (/receiv|(^|_)rec_|reception|target|air_yards|wopr|yac|catch|separation|cushion/.test(name)) return "Receiving";
  return "Other";
}

function researchChartMetricKeys(keys, rows) {
  return keys.filter(key => {
    if (key.startsWith("_") || RESEARCH_IDENTITY_KEYS.has(key.toLowerCase()) || key.toLowerCase().endsWith("_id")) return false;
    const unqualified = key.replace(/^(passing|rushing|receiving|fantasy|defense)_/, "");
    // Hide an alias only when the original exists and matches every available value.
    if (unqualified !== key && keys.includes(unqualified)) {
      const present = rows.filter(row => typeof row[key] === "number" && Number.isFinite(row[key]));
      if (present.length && present.every(row => row[unqualified] === row[key])) return false;
    }
    return true;
  }).sort((a, b) => prettyHeader(a).localeCompare(prettyHeader(b)));
}

function isResearchVolumeMetric(key) {
  const name = String(key).toLowerCase();
  if (/pct|percentage|rate|share|per_|_avg|average|exp|diff/.test(name)) return false;
  return /(?:^|_)(?:attempts?|targets?|carries|receptions|completions|snaps|snap_count|plays|games)(?:$|_)/.test(name);
}

function researchGatePlayerKey(row) {
  return `${playerJoinName(researchName(row))}__${researchTeam(row)}`;
}

function buildResearchGateTotals(rows, metric) {
  const totals = new Map();
  const seen = new Set();
  for (const row of rows) {
    const week = researchWeek(row);
    if (row._syntheticSeasonTotal || row._syntheticSeasonAverage || week < 1 || week > 18 || !researchName(row)) continue;
    if (row.season_type && String(row.season_type).toUpperCase() !== "REG") continue;
    const value = row[metric];
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    const player = researchGatePlayerKey(row);
    const unique = `${player}__${week}`;
    if (seen.has(unique)) continue;
    seen.add(unique);
    totals.set(player, (totals.get(player) || 0) + value);
  }
  return totals;
}

function researchGateValue(row, metric, totals) {
  return row._syntheticSeasonAverage && isResearchVolumeMetric(metric)
    ? totals.get(researchGatePlayerKey(row)) : row[metric];
}

function ResearchMetricSelector({ label, value, onChange, keys, allowNone = false }) {
  const selectedCategory = value ? researchMetricCategory(value) : "";
  const [category, setCategory] = useState(selectedCategory || RESEARCH_METRIC_CATEGORIES.find(group => keys.some(key => researchMetricCategory(key) === group)) || "Other");
  useEffect(() => { if (value) setCategory(researchMetricCategory(value)); }, [value]);
  const groups = RESEARCH_METRIC_CATEGORIES.filter(group => keys.some(key => researchMetricCategory(key) === group));
  const activeCategory = groups.includes(category) ? category : groups[0] || "Other";
  const options = keys.filter(key => researchMetricCategory(key) === activeCategory);
  const selectClass = "block min-w-0 rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white";
  return <div className="flex min-w-[220px] flex-col gap-1">
    <span className="text-[11px] text-slate-400">{label}</span>
    <div className="flex gap-1.5">
      <select aria-label={`${label} category`} className={`${selectClass} w-[105px]`} value={activeCategory} onChange={event => {
        const group = event.target.value;
        setCategory(group);
        onChange(keys.find(key => researchMetricCategory(key) === group) || "");
      }}>{groups.map(group => <option key={group} value={group}>{group}</option>)}</select>
      <select aria-label={`${label} stat`} className={`${selectClass} max-w-[260px] flex-1`} value={value} onChange={event => onChange(event.target.value)}>
        <option value="">{allowNone ? "— no gate —" : "Choose stat…"}</option>
        {options.map(key => <option key={key} value={key}>{prettyHeader(key)}</option>)}
      </select>
    </div>
  </div>;
}

function PlayerBarTick({ x = 0, y = 0, index, payload, rows = [] }) {
  const row = rows[index] || rows.find(item => item._label === payload?.value);
  const name = shortPlayerName(row?._fullName || payload?.value || "");
  const team = normalizeTeamAbbr(row?._team || "");
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{row?._fullName || name}{team ? ` · ${team}` : ""}</title>
      <text x={-30} y={0} textAnchor="end" dominantBaseline="central" fill="#e2e8f0" fontSize={11} fontWeight={600}>{name}</text>
      {team && <image href={nflLogoUrl(team)} x={-24} y={-10} width={20} height={20} preserveAspectRatio="xMidYMid meet" onError={event => { event.currentTarget.style.visibility = "hidden"; }} />}
    </g>
  );
}


// Shared presentation for research tables and weekly player logs.
const STAT_TONES = {
  Player: "text-slate-300 bg-slate-800/70", Game: "text-slate-300 bg-slate-800/70",
  Volume: "text-sky-300 bg-sky-500/10", Usage: "text-sky-300 bg-sky-500/10",
  Production: "text-emerald-300 bg-emerald-500/10", Efficiency: "text-violet-300 bg-violet-500/10",
  Tracking: "text-cyan-300 bg-cyan-500/10", Contact: "text-orange-300 bg-orange-500/10",
  "Pressure & Accuracy": "text-orange-300 bg-orange-500/10",
  Passing: "text-sky-300 bg-sky-500/10", Rushing: "text-emerald-300 bg-emerald-500/10",
  Receiving: "text-violet-300 bg-violet-500/10", Fantasy: "text-amber-300 bg-amber-500/10",
  Opportunity: "text-sky-300 bg-sky-500/10", "Actual Points": "text-emerald-300 bg-emerald-500/10",
  "Expected Points": "text-amber-300 bg-amber-500/10", Difference: "text-violet-300 bg-violet-500/10",
  Coverage: "text-sky-300 bg-sky-500/10", Disruption: "text-orange-300 bg-orange-500/10",
};
const RESEARCH_OVERVIEW = {
  Passing: ["attempts", "completions", "passing_yards", "passing_tds", "completion_percentage", "passing_epa_per_play", "passing_success_rate", "passer_rating"],
  Receiving: ["targets", "target_share", "air_yards_share", "receptions", "receiving_yards", "receiving_tds", "receiving_epa_per_play", "avg_yac"],
  Rushing: ["carries", "rushing_yards", "rushing_tds", "yards_per_carry", "rushing_epa_per_play", "rushing_success_rate", "rushing_yards_after_contact_avg"],
  "Fantasy Opportunity": ["rush_attempt", "rec_attempt", "dk_points", "fd_points", "total_fantasy_points_exp", "total_fantasy_points_diff"],
  "Advanced Defense": ["def_targets", "def_completion_pct", "def_yards_allowed", "def_receiving_td_allowed", "def_passer_rating_allowed", "def_pressures", "def_missed_tackle_pct"],
};
const STAT_LABELS = {
  full_name: "Player", player_display_name: "Player", pfr_player_name: "Player", player_name: "Player",
  team: "Team", posteam: "Team", recent_team: "Team", position: "Pos", week: "Week", opponent: "Opp", season: "Season", games: "G",
  attempts: "Att", completions: "Cmp", carries: "Att", targets: "Tgt", receptions: "Rec",
  receiving_broken_tackles: "Broken Tkls", receiving_rat: "Rating", receiving_int: "INT",
  rushing_yards_before_contact: "YBC", rushing_yards_after_contact: "YAC",
  avg_completed_air_yards: "Comp Air Yds", avg_air_yards_differential: "Air Yds Diff",
  percent_attempts_gte_eight_defenders: "8+ Box%", rush_yards_over_expected_per_att: "RYOE/Att",
  passing_yards: "Yds", passing_tds: "TD", passing_epa: "Total EPA", passing_epa_per_play: "EPA/Play", passing_success_rate: "Success%",
  receiving_yards: "Yds", receiving_tds: "TD", receiving_epa: "Total EPA", receiving_epa_per_play: "EPA/Play", receiving_success_rate: "Success%",
  rushing_yards: "Yds", rushing_tds: "TD", rushing_epa: "Total EPA", rushing_epa_per_play: "EPA/Play", rushing_success_rate: "Success%",
  completion_percentage: "Cmp%", completion_percentage_above_expectation: "CPOE", passer_rating: "Rating", yards_per_carry: "YPC",
  target_share: "Tgt%", air_yards_share: "Air%", avg_yac: "YAC/Rec", avg_expected_yac: "xYAC/Rec", avg_yac_above_expectation: "YAC OE",
  passing_bad_throw_pct: "Bad Throw%", passing_bad_throws: "Bad Throws", times_pressured_pct: "Pressure%", avg_time_to_throw: "Time to Throw",
  receiving_drop_pct: "Drop%", receiving_drop: "Drops", avg_separation: "Separation", avg_cushion: "Cushion", avg_intended_air_yards: "aDOT",
  rushing_yards_after_contact_avg: "YAC/Att", rushing_yards_before_contact_avg: "YBC/Att", rushing_broken_tackles: "Broken Tkls",
  def_targets: "Tgt", def_completions_allowed: "Cmp Allowed", def_completion_pct: "Cmp%", def_yards_allowed: "Yds Allowed",
  def_yards_allowed_per_tgt: "Yds/Tgt", def_receiving_td_allowed: "TD Allowed", def_passer_rating_allowed: "Rating Allowed", def_pressures: "Pressures", def_missed_tackle_pct: "Missed Tkl%",
  dk_points: "DK", fd_points: "FD", total_fantasy_points: "Actual FP", total_fantasy_points_exp: "xFP", total_fantasy_points_diff: "FPOE",
  pass_attempt: "Pass Att", rush_attempt: "Rush Att", rec_attempt: "Targets",
  total_yards_gained: "Yds", total_yards_gained_exp: "Expected Yds", total_touchdown: "TD", total_touchdown_exp: "Expected TD",
};
const STAT_HELP = {
  passing_epa_per_play: "Passing expected points added per play.", receiving_epa_per_play: "Receiving expected points added per play.",
  rushing_epa_per_play: "Rushing expected points added per play.", total_fantasy_points_exp: "Expected fantasy points from the opportunity model; this uses the source model's scoring basis.",
  total_fantasy_points_diff: "Actual minus expected fantasy points on the source model's scoring basis; positive means above expectation.",
  target_share: "Share of team targets.", air_yards_share: "Share of team air yards.",
  rushing_yards_after_contact_avg: "Average rushing yards after contact per attempt.",
};
function researchStatGroup(tab, key) {
  if (["full_name","player_display_name","pfr_player_name","player_name","player","name","team","posteam","recent_team","position","week","season","games","opponent"].includes(key)) return "Player";
  if (tab === "Fantasy Opportunity") {
    if (/fantasy_points_diff/.test(key)) return "Difference";
    if (/fantasy_points_exp/.test(key)) return "Expected Points";
    if (/fantasy_points|^(dk|fd)_points/.test(key)) return "Actual Points";
    if (/_exp$/.test(key)) return "Expected Production";
    if (/yards_gained|touchdown/.test(key) || ["receptions","completions","pass_completions"].includes(key)) return "Production";
    return "Opportunity";
  }
  if (tab === "Advanced Defense") {
    if (/pressur|sack|blitz|hurr|hit|tackl/.test(key)) return "Disruption";
    return "Coverage";
  }
  if (/^(dk|fd)_|fantasy/.test(key)) return "Fantasy";
  if (/contact|broken_tackle/.test(key)) return "Contact";
  if (/bad_throw|pressur|blitz|hurr|times_hit|times_sack|passing_drop/.test(key)) return "Pressure & Accuracy";
  // Opportunity is usage; completed catches and passes are production.
  if (["attempts","carries","targets","pass_attempt","rush_attempt","rec_attempt"].includes(key) || /share|wopr|snap|_attempts$/.test(key)) return "Usage";
  if (["completions","receptions","pass_completions","receiving_drop"].includes(key) || /^(passing|rushing|receiving)_epa$/.test(key)) return "Production";
  if (["avg_yac","avg_expected_yac","avg_yac_above_expectation","efficiency","receiving_rat"].includes(key) || /epa_per|success|rating|pct|percentage|per_|cpoe|yards_per/.test(key)) return "Efficiency";
  if (/cushion|separation|air_yards|air_distance|time_to_throw|time_to_los|aggressiveness/.test(key)) return "Tracking";
  if (/yards|tds|touchdown|interception|receiving_int/.test(key)) return "Production";
  return "Detail";
}
function researchColumnGroups(tab, keys) {
  const order = ["Player", "Volume", "Usage", "Opportunity", "Production", "Actual Points", "Expected Points", "Difference", "Efficiency", "Tracking", "Contact", "Pressure & Accuracy", "Coverage", "Disruption", "Fantasy", "Expected Production", "Detail"];
  return order.map(label => ({label, keys: keys.filter(key => researchStatGroup(tab, key) === label)})).filter(group => group.keys.length);
}
function researchPresetColumns(tab, keys, preset = "overview") {
  const name = ["full_name","player_display_name","pfr_player_name","player_name","player","name"].find(key => keys.includes(key));
  const identity = [name, "week"].filter(key => key && keys.includes(key));
  const preferred = RESEARCH_OVERVIEW[tab] || defaultResearchColumns(tab, keys).filter(key => researchStatGroup(tab, key) !== "Player");
  let metrics = preferred.filter(key => keys.includes(key));
  if (preset === "detailed") {
    const extras = {
      Passing: ["passing_epa","completion_percentage_above_expectation","avg_completed_air_yards","avg_intended_air_yards","avg_air_yards_differential","aggressiveness","max_completed_air_distance","avg_time_to_throw","passing_bad_throws","passing_bad_throw_pct","passing_drops","passing_drop_pct","times_blitzed","times_hurried","times_hit","times_pressured","times_pressured_pct"],
      Receiving: ["receiving_epa","avg_cushion","avg_separation","avg_intended_air_yards","avg_expected_yac","avg_yac_above_expectation","receiving_drop","receiving_drop_pct","receiving_broken_tackles","receiving_rat"],
      Rushing: ["rushing_epa","efficiency","percent_attempts_gte_eight_defenders","avg_time_to_los","rush_pct_over_expected","rush_yards_over_expected_per_att","rushing_yards_before_contact","rushing_yards_before_contact_avg","rushing_yards_after_contact","rushing_yards_after_contact_avg","rushing_broken_tackles"],
      "Fantasy Opportunity": ["total_fantasy_points","pass_fantasy_points_exp","rush_fantasy_points_exp","rec_fantasy_points_exp","total_yards_gained","total_yards_gained_exp","total_touchdown","total_touchdown_exp","pass_attempt"],
      "Advanced Defense": ["def_completions_allowed","def_yards_allowed_per_tgt","def_missed_tackles","def_sacks","def_hurries","def_blitzes"],
    };
    metrics = [...new Set([...metrics, ...defaultResearchColumns(tab, keys), ...(extras[tab] || [])])].filter(key => keys.includes(key) && researchStatGroup(tab, key) !== "Player");
  }

  return researchColumnGroups(tab, [...new Set([...identity, ...metrics])]).flatMap(group => group.keys);
}
// Include cumulative EPA by default when viewing full-season totals.
function researchDisplayColumns(tab, columns, keys, weekFilter, preset) {
  const epaKey = { Passing: "passing_epa", Receiving: "receiving_epa", Rushing: "rushing_epa" }[tab];
  if (weekFilter !== SYNTHETIC_SEASON_TOTAL || preset === "custom" || !epaKey ||
      !keys.includes(epaKey) || columns.includes(epaKey)) return columns;
  const next = [...columns];
  const perPlayIndex = next.indexOf(`${epaKey}_per_play`);
  next.splice(perPlayIndex < 0 ? next.length : perPlayIndex, 0, epaKey);
  return next;
}

function StatIdentity({ row, onOpen }) {
  const name = researchName(row), team = researchTeam(row), position = researchPos(row);
  return <div className="flex items-center gap-1.5 text-left">
    {team && <img src={nflLogoUrl(team)} alt="" className="h-5 w-5 shrink-0 object-contain" onError={e => {e.currentTarget.style.visibility = "hidden";}} />}
    <div className="min-w-0"><button type="button" disabled={!onOpen} onClick={() => onOpen?.(row)} className="block whitespace-nowrap text-[11px] leading-4 font-bold text-slate-100 enabled:hover:text-sky-300 enabled:hover:underline disabled:cursor-default">{name || "—"}</button>
      <div className="flex items-center gap-1 text-[9px] leading-3 text-slate-400">{team}{position && <span className="rounded bg-slate-700/60 px-1 py-0 font-semibold text-slate-300">{position}</span>}</div>
    </div>
  </div>;
}
function GroupedStatTable({ groups, rows, sort, onSort, renderCell, loading = false, empty = "No results match these filters.", caption = "Player stats", onOpen }) {
  const columns = groups.flatMap(group => group.columns.map((column, index) => ({ ...column, group: group.label, start: index === 0 })));
  if (!columns.length) return <p className="px-4 py-12 text-center text-sm text-slate-400">{loading ? "Loading stats…" : !rows.length ? empty : "Choose at least one column in Custom, or select Overview."}</p>;
  return <table className="w-max border-separate border-spacing-0 text-[11px] tabular-nums">
    <caption className="sr-only">{caption}</caption>
    <thead className="sticky top-0 z-20 bg-slate-900">
      <tr>{groups.map((group, index) => <th key={group.label} scope="colgroup" colSpan={group.columns.length} className={`h-6 border-b border-slate-700 px-2 text-center text-[9px] font-extrabold uppercase tracking-[.13em] ${STAT_TONES[group.label] || "text-slate-300 bg-slate-800/70"} ${index ? "border-l border-l-slate-700" : ""}`}>{group.label}</th>)}</tr>
      <tr>{columns.map((column, index) => <th key={column.key} style={{minWidth: column.identity ? 180 : column.key === "week" ? 88 : 58}} scope="col" aria-sort={sort?.key === column.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined} title={column.title || prettyHeader(column.key)} className={`h-8 whitespace-nowrap border-b border-slate-700 bg-slate-900 text-slate-300 ${column.start && index ? "border-l border-l-slate-700" : ""} ${index === 0 ? "sticky left-0 z-30 text-left" : "text-center"}`}>
        {onSort ? <button type="button" onClick={() => onSort(column.key)} className={`relative flex w-full items-center gap-1 py-1.5 pr-4 hover:text-white focus-visible:outline focus-visible:outline-sky-400 ${column.identity ? "justify-start pl-2 text-left" : "justify-center pl-4 text-center"}`}>{column.label}<span className={`absolute right-1 text-[9px] ${sort?.key === column.key ? "text-sky-400" : "text-slate-600"}`}>{sort?.key === column.key ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}</span></button> : <span className="block px-2 py-1.5 text-center">{column.label}</span>}
      </th>)}</tr>
    </thead>
    <tbody>{loading || !rows.length ? <tr><td colSpan={Math.max(1, columns.length)} className="px-4 py-12 text-center text-slate-400">{loading ? "Loading stats…" : empty}</td></tr> : rows.map((row, ri) => <tr key={`${row.player_id || researchName(row) || ri}-${row.week || ri}`} className="group">
      {columns.map((column, ci) => <td key={column.key} className={`h-8 whitespace-nowrap border-b border-slate-800/70 px-2 py-1 ${ci === 0 ? `sticky left-0 z-10 ${column.identity ? "text-left" : "text-center"} ${ri % 2 ? "bg-slate-900" : "bg-slate-950"} group-hover:bg-slate-800` : `${ri % 2 ? "bg-slate-900/40" : "bg-slate-950"} text-center group-hover:bg-slate-800/70`} ${column.start && ci ? "border-l border-l-slate-700" : ""} ${["dk_points","fd_points","custom_points"].includes(column.key) ? "font-extrabold text-sky-300" : /fantasy_points_exp/.test(column.key) ? "font-bold text-amber-300" : "text-slate-200"}`}>
        {column.identity ? <StatIdentity row={row} onOpen={onOpen} /> : renderCell ? renderCell(row, column.key) : (row?.[column.key] == null || row[column.key] === "" ? "—" : researchCellValue(row, column.key))}
      </td>)}
    </tr>)}</tbody>
  </table>;
}
function researchTableGroups(tab, keys) {
  return researchColumnGroups(tab, keys).map(group => ({label: group.label === "Player" && group.keys.every(key => key === "week") ? "Game" : group.label, columns: group.keys.map(key => ({key, label: STAT_LABELS[key] || prettyHeader(key), title: STAT_HELP[key] || prettyHeader(key), identity: ["full_name","player_display_name","pfr_player_name","player_name","player","name"].includes(key)}))}));
}

function ResearchPlayerSummary({ tab, rows }) {
  const games = rows.filter(row => !row._syntheticSeasonAverage && researchWeek(row) >= 1 && researchWeek(row) <= 18);
  const average = key => {const values = games.map(row => row[key]).filter(value => typeof value === "number" && Number.isFinite(value)); return values.length ? (values.reduce((sum,value) => sum + value, 0) / values.length).toFixed(1) : "—";};
  const metrics = ({Passing: [["attempts","Att/G"],["passing_yards","Pass Yds/G"],["passing_tds","Pass TD/G"]], Receiving: [["targets","Targets/G"],["receiving_yards","Rec Yds/G"],["receiving_tds","Rec TD/G"]], Rushing: [["carries","Att/G"],["rushing_yards","Rush Yds/G"],["rushing_tds","Rush TD/G"]], "Fantasy Opportunity": [["dk_points","Avg DK"],["fd_points","Avg FD"],["total_fantasy_points_exp","Avg xFP"]], "Advanced Defense": [["def_targets","Targets/G"],["def_yards_allowed","Yds Allowed/G"],["def_pressures","Pressures/G"]]})[tab] || [];
  return <div className="mb-4"><div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Regular-season snapshot</div><div className="grid grid-cols-2 gap-2 lg:grid-cols-4">{[["Games", games.length], ...metrics.map(([key,label]) => [label,average(key)])].map(([label,value]) => <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div><div className="mt-1 text-xl font-extrabold text-slate-100">{value}</div></div>)}</div></div>;
}

function ResearchDatasetView({ tab, rows, chartSourceRows, season, loading }) {
  const [mode, setMode] = useState("table");
  const [chartScope, setChartScope] = useState("all");
  const [qualifiedOnly, setQualifiedOnly] = useState(true);
  const [qualifiedPercent, setQualifiedPercent] = useState(100);
  const [chartQualificationTab, setChartQualificationTab] = useState("Passing");
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState("ALL");
  const [position, setPosition] = useState("ALL");
  const [weekFilter, setWeekFilter] = useState(SYNTHETIC_SEASON_AVG);
  const [sortState, setSortState] = useState({ key: "", dir: "desc" });
  const [visible, setVisible] = useState([]);
  const [tablePreset, setTablePreset] = useState("overview");
  const [columnSearch, setColumnSearch] = useState("");
  const [modalDetailed, setModalDetailed] = useState(false);
  const [xKeyLocal, setXKeyLocal] = useState("");
  const [yKeyLocal, setYKeyLocal] = useState("");
  const [gateKeyLocal, setGateKeyLocal] = useState("");
  const [gateMinLocal, setGateMinLocal] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [modalMetric, setModalMetric] = useState("");
  const [comparePlayers, setComparePlayers] = useState([]);
  const [compareSearch, setCompareSearch] = useState("");
  const [includePostseasonCompare, setIncludePostseasonCompare] = useState(false);
  const [pageSize, setPageSize] = useState(250);
  const [chartLimit, setChartLimit] = useState("25");
  const playerTableScrollRef = useRef(null);
  const playerTableMirrorRef = useRef(null);
  const [playerTableScrollWidth, setPlayerTableScrollWidth] = useState(0);
  const syncingPlayerScroll = useRef(false);

  useEffect(() => {
    if (!selectedPlayer) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dismiss = event => {if (event.key === "Escape") {setSelectedPlayer(null); setComparePlayers([]); setCompareSearch("");}};
    window.addEventListener("keydown", dismiss);
    return () => {document.body.style.overflow = previous; window.removeEventListener("keydown", dismiss);};
  }, [selectedPlayer]);

  const scoredRows = useMemo(() => enrichFantasyScoring(rows), [rows]);
  const rowsWithSeasonAverage = useMemo(
    () => addResearchSeasonPace(addResearchSeasonTotals(addSyntheticSeasonAverages(scoredRows, tab), tab)),
    [scoredRows, tab]
  );
  const combinedChartRows = useMemo(() => chartSourceRows
    ? addResearchSeasonPace(addResearchSeasonTotals(chartSourceRows, tab)) : rowsWithSeasonAverage,
    [chartSourceRows, rowsWithSeasonAverage, tab]);
  // Use full league pools before team, position, search, week and Top N filters.
  // Charts can mix categories, so their qualification basis is selectable.
  // Qualification always uses regular-season volume, even when displaying averages.
  const chartQualificationOptions = useMemo(() => ["Passing", "Receiving", "Rushing"]
    .filter(category => researchQualification(combinedChartRows, category, qualifiedPercent)),
    [combinedChartRows, qualifiedPercent]);
  const activeChartQualificationTab = chartQualificationOptions.includes(chartQualificationTab)
    ? chartQualificationTab : chartQualificationOptions[0];
  const qualification = useMemo(() => researchQualification(
    mode === "table" ? rowsWithSeasonAverage : combinedChartRows,
    mode === "table" ? tab : activeChartQualificationTab,
    qualifiedPercent
  ), [mode, rowsWithSeasonAverage, combinedChartRows, tab, activeChartQualificationTab, qualifiedPercent]);
  const categoryPlayerKeys = useMemo(() => new Set(rowsWithSeasonAverage.map(r => `${playerJoinName(researchName(r))}__${researchTeam(r)}`)), [rowsWithSeasonAverage]);
  const activeResearchRows = useMemo(() => mode === "table" ? rowsWithSeasonAverage : chartScope === "all" ? combinedChartRows : combinedChartRows.filter(r => categoryPlayerKeys.has(`${playerJoinName(researchName(r))}__${researchTeam(r)}`)), [mode, chartScope, combinedChartRows, rowsWithSeasonAverage, categoryPlayerKeys]);
  const keys = useMemo(() => Array.from(new Set(rowsWithSeasonAverage.flatMap((row) => Object.keys(row || {})))).filter((k) => !k.startsWith("_")), [rowsWithSeasonAverage]);
  const nums = useMemo(() => numericKeys(combinedChartRows).filter((k) => !k.startsWith("_")).sort(), [combinedChartRows]);
  const chartMetricKeys = useMemo(() => researchChartMetricKeys(nums, combinedChartRows), [nums, combinedChartRows]);
  const gateUsesTotal = weekFilter === SYNTHETIC_SEASON_AVG && isResearchVolumeMetric(gateKeyLocal);
  const gateTotals = useMemo(() => gateKeyLocal && isResearchVolumeMetric(gateKeyLocal) ? buildResearchGateTotals(combinedChartRows, gateKeyLocal) : new Map(), [combinedChartRows, gateKeyLocal]);
  const playerCapable = useMemo(() => rowsWithSeasonAverage.some((r) => researchName(r)), [rowsWithSeasonAverage]);
  const teams = useMemo(() => ["ALL", ...Array.from(new Set(activeResearchRows.map(researchTeam).filter(Boolean))).sort()], [activeResearchRows]);
  const positions = useMemo(() => ["ALL", ...Array.from(new Set(activeResearchRows.map(researchPos).filter(Boolean))).sort()], [activeResearchRows]);
  const weeks = useMemo(() => {
    const actualWeeks = Array.from(new Set(combinedChartRows.map(researchWeek).filter((w) => w > 0))).sort((a,b)=>a-b).map(String);
    const hasSyntheticAverage = rowsWithSeasonAverage.some((r) => r?._syntheticSeasonAverage);
    const hasTotals = rowsWithSeasonAverage.some(r => r._syntheticSeasonTotal);
    const hasPace = rowsWithSeasonAverage.some(r => r._syntheticSeasonPace);
    return ["ALL", ...(hasSyntheticAverage ? [SYNTHETIC_SEASON_AVG] : []), ...(hasTotals ? [SYNTHETIC_SEASON_TOTAL] : []), ...(hasPace ? [SYNTHETIC_SEASON_PACE] : []), ...actualWeeks];
  }, [combinedChartRows, rowsWithSeasonAverage]);

  useEffect(() => {
    const defaults = researchPresetColumns(tab, keys);
    setTablePreset("overview");
    setColumnSearch("");
    setVisible(defaults.length ? defaults : keys.slice(0, 14));
    const candidates = nums.filter((k) => !["season","week"].includes(k));
    const pick = (needles, fallback) => candidates.find((k) => needles.some((n) => k.toLowerCase().includes(n))) || fallback;
    if (tab === "Fantasy Opportunity") {
      setXKeyLocal(pick(["fantasy_points_exp"], candidates[0] || ""));
      setYKeyLocal(pick(["fantasy_points_diff"], candidates[1] || candidates[0] || ""));
      setGateKeyLocal(pick(["rec_attempt","rush_attempt","pass_attempt"], ""));
    } else {
      const axisDefaults = {
        Passing: ["passing_yards", "passing_epa_per_play"],
        Receiving: ["targets", "receiving_yards"],
        Rushing: ["carries", "rushing_yards"],
        "Advanced Defense": ["def_targets", "def_yards_allowed"],
      };
      const [defaultX, defaultY] = axisDefaults[tab] || [];
      setXKeyLocal(candidates.includes(defaultX) ? defaultX : candidates[0] || "");
      setYKeyLocal(candidates.includes(defaultY) ? defaultY : candidates[1] || candidates[0] || "");
      setGateKeyLocal("");
    }
    setSortState({ key: ({Passing: "passing_yards", Receiving: "receiving_yards", Rushing: "rushing_yards", "Fantasy Opportunity": "total_fantasy_points_exp", "Advanced Defense": "def_targets"})[tab] || defaults.find(key => researchStatGroup(tab, key) !== "Player") || defaults[0] || "", dir: "desc" });
    setSearch("");
    setQualifiedOnly(true);
    setChartQualificationTab(["Passing", "Receiving", "Rushing"].includes(tab) ? tab : "Passing");
    setTeam("ALL");
    setPosition("ALL");
    setWeekFilter(
      rowsWithSeasonAverage.some((row) => row?._syntheticSeasonAverage)
        ? SYNTHETIC_SEASON_AVG
        : "ALL"
    );
    setPageSize(250);
    setChartLimit("25");
  }, [tab, keys.join("|"), nums.join("|")]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let out = activeResearchRows.filter((r) => team === "ALL" || researchTeam(r) === team)
      .filter((r) => position === "ALL" || researchPos(r) === position)
      // "All weeks" means actual games only so synthetic averages never duplicate players in charts/tables.
      .filter((r) => weekFilter === "ALL" ? (!r?._syntheticSeasonAverage && !r?._syntheticSeasonTotal && !r?._syntheticSeasonPace && researchWeek(r) > 0)
        : weekFilter === SYNTHETIC_SEASON_AVG ? r?._syntheticSeasonAverage
        : weekFilter === SYNTHETIC_SEASON_TOTAL ? r?._syntheticSeasonTotal
        : weekFilter === SYNTHETIC_SEASON_PACE ? r?._syntheticSeasonPace
        : (!r?._syntheticSeasonAverage && !r?._syntheticSeasonTotal && !r?._syntheticSeasonPace && String(researchWeek(r)) === weekFilter))
      .filter(r => !qualifiedOnly || !qualification ||
        (qualification.totals.get(researchGatePlayerKey(r)) ?? 0) >= qualification.minimum);
    if (needle) out = out.filter((r) => Object.values(r || {}).some((v) => String(v ?? "").toLowerCase().includes(needle)));
    const {key,dir} = sortState;
    if (key) out = [...out].sort((a,b) => {
      const av=a?.[key], bv=b?.[key], sign=dir === "asc" ? 1 : -1;
      if (typeof av === "number" && typeof bv === "number") return (av-bv)*sign;
      return String(av??"").localeCompare(String(bv??""))*sign;
    });
    return out;
  }, [activeResearchRows, search, team, position, weekFilter, sortState, qualifiedOnly, qualification]);

  const plottedAll = useMemo(() => filteredRows.filter((r) =>
      (mode === "bar" || typeof r[xKeyLocal] === "number") && typeof r[yKeyLocal] === "number" &&
      (!gateKeyLocal || gateMinLocal === "" || (typeof researchGateValue(r, gateKeyLocal, gateTotals) === "number" && Number.isFinite(researchGateValue(r, gateKeyLocal, gateTotals)) && researchGateValue(r, gateKeyLocal, gateTotals) >= Number(gateMinLocal)))
    ).map((r, i) => ({...r, _fullName: researchName(r) || `Play ${r.ftn_play_id ?? i+1}`, _team: researchTeam(r), _position: researchPos(r), _week: researchWeek(r), _logo: nflLogoUrl(researchTeam(r)), _label: shortPlayerName(researchName(r) || researchTeam(r) || `#${i+1}`)})),
    [filteredRows,xKeyLocal,yKeyLocal,gateKeyLocal,gateMinLocal,mode,gateTotals]);

  const plotted = useMemo(
    () => limitChartRows(plottedAll, chartLimit, yKeyLocal),
    [plottedAll, chartLimit, yKeyLocal]
  );

  const playerXDomain = useMemo(() => paddedChartDomain(plotted, xKeyLocal), [plotted, xKeyLocal]);
  const playerYDomain = useMemo(() => paddedChartDomain(plotted, yKeyLocal), [plotted, yKeyLocal]);
  const playerBarDomain = useMemo(() => paddedChartDomain(plotted, yKeyLocal, true), [plotted, yKeyLocal]);

  const playerRows = useMemo(() => {
    if (!selectedPlayer) return [];
    const target = selectedPlayer.name.toLowerCase();
    return rowsWithSeasonAverage
      .filter((r) => !r._syntheticSeasonTotal && !r._syntheticSeasonPace && researchName(r).toLowerCase() === target)
      .sort((a,b) => {
        if (a?._syntheticSeasonAverage) return -1;
        if (b?._syntheticSeasonAverage) return 1;
        return researchWeek(a)-researchWeek(b);
      });
  }, [selectedPlayer, rowsWithSeasonAverage]);
  const playerMetrics = useMemo(() => numericKeys(playerRows).filter((k)=>!["season","week"].includes(k)), [playerRows]);

  const playerDirectory = useMemo(() => {
    const byKey = new Map();
    for (const row of rowsWithSeasonAverage) {
      const name = researchName(row);
      if (!name) continue;
      const teamValue = researchTeam(row);
      const positionValue = researchPos(row);
      const key = `${name.toLowerCase()}__${teamValue}__${positionValue}`;
      if (!byKey.has(key)) {
        byKey.set(key, { name, team: teamValue, position: positionValue, key });
      }
    }
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rowsWithSeasonAverage]);

  const compareSuggestions = useMemo(() => {
    const needle = compareSearch.trim().toLowerCase();
    if (!needle) return [];
    const selectedKeys = new Set(comparePlayers.map((player) => player.key));
    return playerDirectory
      .filter((player) => !selectedKeys.has(player.key))
      .filter((player) =>
        player.name.toLowerCase().includes(needle)
        || player.team.toLowerCase().includes(needle)
        || player.position.toLowerCase().includes(needle)
      )
      .slice(0, 8);
  }, [compareSearch, comparePlayers, playerDirectory]);

  const comparisonSeries = useMemo(() => {
    if (!comparePlayers.length || !modalMetric) return [];

    return comparePlayers.map((player, index) => {
      const rowsForPlayer = rowsWithSeasonAverage
        .filter((row) =>
          researchName(row).toLowerCase() === player.name.toLowerCase()
          && researchTeam(row) === player.team
          && !row?._syntheticSeasonAverage
          && !row?._syntheticSeasonPace
          && (includePostseasonCompare || researchWeek(row) <= 18)
        )
        .sort((a, b) => researchWeek(a) - researchWeek(b));

      return {
        ...player,
        dataKey: `_compare_${index}`,
        rows: rowsForPlayer,
      };
    });
  }, [comparePlayers, rowsWithSeasonAverage, modalMetric, includePostseasonCompare]);

  const comparisonChartData = useMemo(() => {
    if (!comparisonSeries.length || !modalMetric) return [];

    const byWeek = new Map();
    for (const series of comparisonSeries) {
      for (const row of series.rows) {
        const weekValue = researchWeek(row);
        if (!Number.isFinite(weekValue) || weekValue <= 0) continue;
        if (!byWeek.has(weekValue)) byWeek.set(weekValue, { _week: weekValue });
        const metricValue = row?.[modalMetric];
        if (typeof metricValue === "number" && Number.isFinite(metricValue)) {
          byWeek.get(weekValue)[series.dataKey] = metricValue;
        }
      }
    }
    return Array.from(byWeek.values()).sort((a, b) => a._week - b._week);
  }, [comparisonSeries, modalMetric]);

  const comparisonStats = useMemo(() => {
    const median = (values) => {
      if (!values.length) return 0;
      const sortedValues = [...values].sort((a, b) => a - b);
      const middle = Math.floor(sortedValues.length / 2);
      return sortedValues.length % 2
        ? sortedValues[middle]
        : (sortedValues[middle - 1] + sortedValues[middle]) / 2;
    };

    return comparisonSeries.map((series) => {
      const values = series.rows
        .map((row) => row?.[modalMetric])
        .filter((value) => typeof value === "number" && Number.isFinite(value));

      const average = values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : 0;
      const variance = values.length
        ? values.reduce((sum, value) => sum + ((value - average) ** 2), 0) / values.length
        : 0;

      return {
        ...series,
        games: values.length,
        average,
        median: median(values),
        ceiling: values.length ? Math.max(...values) : 0,
        floor: values.length ? Math.min(...values) : 0,
        stdDev: Math.sqrt(variance),
      };
    });
  }, [comparisonSeries, modalMetric]);

  const comparisonLeaders = useMemo(() => {
    const usable = comparisonStats.filter((player) => player.games > 0);
    if (!usable.length) return {};

    const highest = (key) => [...usable].sort((a, b) => b[key] - a[key])[0];
    const lowest = (key) => [...usable].sort((a, b) => a[key] - b[key])[0];

    return {
      average: highest("average"),
      median: highest("median"),
      ceiling: highest("ceiling"),
      floor: highest("floor"),
      consistency: lowest("stdDev"),
    };
  }, [comparisonStats]);

  // Pick the most useful trend automatically when a player modal opens.
  // Fantasy Opportunity is split into passing / rushing / receiving components,
  // so the default should follow the player's position instead of whichever
  // fantasy-points field happens to appear first in the JSON.
  useEffect(() => {
    if (!playerRows.length) return;

    // Total fantasy points already combines passing, rushing, and receiving
    // production, so use it as the universal default for every player.
    const preferredMetrics = [
      "total_fantasy_points",
      "total_fantasy_points_exp",
      "fantasy_points_ppr",
      "fantasy_points",
    ];

    const preferredMetric = preferredMetrics.find((key) => playerMetrics.includes(key));
    const fallbackMetric =
      playerMetrics.find((key) => key === "total_fantasy_points")
      || playerMetrics.find((key) => key.includes("total_fantasy_points"))
      || playerMetrics.find((key) => key.includes("fantasy_points"))
      || playerMetrics[0]
      || "";

    setModalMetric(preferredMetric || fallbackMetric);
  }, [selectedPlayer?.name, selectedPlayer?.position, playerRows.length, playerMetrics.join("|")]);

  useEffect(() => {
    if (!selectedPlayer) return;

    const updateWidth = () => {
      const tableScroller = playerTableScrollRef.current;
      if (tableScroller) setPlayerTableScrollWidth(tableScroller.scrollWidth > tableScroller.clientWidth + 1 ? tableScroller.scrollWidth : 0);
    };

    const frame = requestAnimationFrame(updateWidth);
    window.addEventListener("resize", updateWidth);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateWidth);
    };
  }, [selectedPlayer, playerRows.length, tab, visible.length, modalDetailed]);

  const syncPlayerTableFromMain = (event) => {
    if (syncingPlayerScroll.current) return;
    const mirror = playerTableMirrorRef.current;
    if (!mirror) return;
    syncingPlayerScroll.current = true;
    mirror.scrollLeft = event.currentTarget.scrollLeft;
    requestAnimationFrame(() => { syncingPlayerScroll.current = false; });
  };

  const syncPlayerTableFromMirror = (event) => {
    if (syncingPlayerScroll.current) return;
    const tableScroller = playerTableScrollRef.current;
    if (!tableScroller) return;
    syncingPlayerScroll.current = true;
    tableScroller.scrollLeft = event.currentTarget.scrollLeft;
    requestAnimationFrame(() => { syncingPlayerScroll.current = false; });
  };

  const displayedColumns = researchDisplayColumns(tab, visible, keys, weekFilter, tablePreset);
  const exportResearch = () => exportToCsv(`nfl_${tab.toLowerCase().replaceAll(" ","_")}_${season}.csv`, filteredRows, mode === "table" ? researchColumnGroups(tab, displayedColumns).flatMap(group => group.keys) : [...new Set(["full_name", "team", "position", "week", ...nums])]);
  const openPlayer = (r) => {
    const name = researchName(r);
    if (!name) return;
    const player = {
      name,
      team: researchTeam(r),
      position: researchPos(r),
      key: `${name.toLowerCase()}__${researchTeam(r)}__${researchPos(r)}`,
    };
    setModalDetailed(false);
    setSelectedPlayer(player);
    setComparePlayers([player]);
    setCompareSearch("");
    setIncludePostseasonCompare(false);
  };

  const addComparePlayer = (player) => {
    setComparePlayers((current) => {
      if (current.some((item) => item.key === player.key) || current.length >= 6) return current;
      return [...current, player];
    });
    setCompareSearch("");
  };

  const removeComparePlayer = (playerKey) => {
    setComparePlayers((current) => current.filter((player, index) => index === 0 || player.key !== playerKey));
  };

  const closePlayerModal = () => {
    setSelectedPlayer(null);
    setComparePlayers([]);
    setCompareSearch("");
    setIncludePostseasonCompare(false);
  };

  const qualificationControls = (qualification && <>
          {mode !== "table" && <label className="text-[11px] text-slate-400">Qualify by<select aria-label="Chart qualification basis" value={activeChartQualificationTab || ""} onChange={e => setChartQualificationTab(e.target.value)} className="mt-1 block rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white">{chartQualificationOptions.map(category => <option key={category} value={category}>{category === "Passing" ? "Passing attempts" : category === "Receiving" ? "Receiving targets" : "Rushing carries"}</option>)}</select></label>}
          <label className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-bold">
            <input aria-label="Qualified players only" type="checkbox" checked={qualifiedOnly} onChange={e => setQualifiedOnly(e.target.checked)} className="accent-sky-500" />Qualified
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-400">Cutoff
            <input aria-label="Qualified percent of league average" type="number" min="0" max="100" step="5" value={qualifiedPercent} onChange={e => setQualifiedPercent(Math.min(100, Math.max(0, Number(e.target.value) || 0)))} className="w-16 rounded-lg border border-slate-700 bg-slate-900 px-2 py-2 text-xs text-white" />% of league avg
          </label>
        </>);
  const qualificationSummary = (qualification && <p className="mt-2 text-[11px] text-slate-400">Qualified: at least {qualification.minimum} total {mode === "table" ? (tab === "Rushing" ? "carries" : tab === "Receiving" ? "targets" : "attempts") : (activeChartQualificationTab === "Rushing" ? "carries" : activeChartQualificationTab === "Receiving" ? "targets" : "attempts")} across available regular season weeks ({qualifiedPercent}% of league average {qualification.average.toFixed(1)}). Uses players with positive volume, before team or search filters; uncheck to show everyone.</p>);

  return <>
    <style>{`
      .player-game-log-scroller {
        scrollbar-width: thin;
        scrollbar-color: #475569 #020617;
      }
      .player-game-log-scroller::-webkit-scrollbar {
        height: 8px;
      }
    `}</style>
    <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <div className="text-lg font-extrabold text-white">{tab} <span className="ml-2 text-xs font-medium text-slate-400">{filteredRows.length.toLocaleString()} rows</span></div>
        </div>
        <div className="flex rounded-full border border-slate-700 bg-slate-900 p-1">
          {[["table","Table"],["chart","Scatter"],["bar","Bar"]].map(([m,label])=><button key={m} onClick={()=>setMode(m)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${mode===m?"bg-sky-500 text-slate-950":"text-slate-300"}`}>{label}</button>)}
        </div>
        <button onClick={exportResearch} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-bold hover:border-sky-500">Export CSV</button>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search any field…" className="min-w-[180px] flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs outline-none focus:border-sky-500" />
        <select value={team} onChange={(e)=>setTeam(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs">{teams.map((v)=><option key={v}>{v}</option>)}</select>
        <select value={position} onChange={(e)=>setPosition(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs">{positions.map((v)=><option key={v}>{v}</option>)}</select>
        <select value={weekFilter} onChange={(e)=>setWeekFilter(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs">{weeks.map((v)=><option key={v} value={v}>{v === "ALL" ? "All weeks" : v === SYNTHETIC_SEASON_AVG ? "Season Avg (Weeks 1–18)" : v === SYNTHETIC_SEASON_TOTAL ? "Full season totals (Weeks 1–18)" : v === SYNTHETIC_SEASON_PACE ? "17 Game Pace" : `Week ${v}`}</option>)}</select>
        {mode === "table" && qualification && <label className="flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-xs"><input type="checkbox" checked={qualifiedOnly} onChange={e => setQualifiedOnly(e.target.checked)} className="accent-sky-500" />Qualified players</label>}
      </div>
      {mode === "table" && qualification && <details className="mt-3 border-t border-slate-800 pt-2"><summary className="cursor-pointer text-xs text-slate-400">Qualification · {qualifiedOnly ? `Minimum ${qualification.minimum} ${tab === "Rushing" ? "carries" : tab === "Receiving" ? "targets" : "attempts"}` : "All players"}</summary><div className="mt-2 flex flex-wrap gap-3">{qualificationControls}</div>{qualificationSummary}</details>}
      {weekFilter === SYNTHETIC_SEASON_PACE && <p className="mt-2 text-[11px] text-slate-400">17 Game Pace projects per-game counting stats over 17 games. Percentages, efficiency and other averages stay unchanged. Qualification uses actual regular-season volume.</p>}
      {weekFilter === SYNTHETIC_SEASON_TOTAL && <p className="mt-2 text-[11px] text-slate-400">Season totals cover available Weeks 1–18. Counts, yards, touchdowns, fantasy points and EPA are summed. EPA/play, success rate, completion % and yards/carry use season denominators; other averages, ratings and shares remain averages.</p>}
    </div>

    {mode === "table" ? <>
      <section className="w-fit min-w-0 max-w-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-950">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 p-3">
          <div><div className="text-sm font-bold text-slate-100">{tab} leaders</div><p className="mt-1 text-[11px] text-slate-400">{weekFilter === SYNTHETIC_SEASON_AVG ? "Per-game season averages" : weekFilter === SYNTHETIC_SEASON_TOTAL ? "Season totals" : weekFilter === SYNTHETIC_SEASON_PACE ? "17-game pace" : weekFilter === "ALL" ? "Individual weekly performances" : `Week ${weekFilter}`} · {displayedColumns.length} columns · Click a player for weekly detail</p></div>
          <div className="flex rounded-lg border border-slate-700 bg-slate-950 p-1">{[["overview","Overview"],["detailed","Detailed"],["custom","Custom"]].map(([value,label]) => <button key={value} type="button" aria-pressed={tablePreset === value} onClick={() => {setTablePreset(value); if (value !== "custom") {const next = researchPresetColumns(tab, keys, value); setVisible(next); setSortState(current => next.includes(current.key) ? current : {key: next.find(key => researchStatGroup(tab,key) !== "Player") || next[0] || "", dir: "desc"});}}} className={`rounded-md px-3 py-1.5 text-xs font-bold ${tablePreset === value ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}>{label}</button>)}</div>
        </div>
        {tablePreset === "custom" && <div className="border-b border-slate-800 bg-slate-900/50 p-3">
          <div className="mb-3 flex flex-wrap gap-2"><input aria-label="Find a column" value={columnSearch} onChange={e => setColumnSearch(e.target.value)} placeholder="Find a stat…" className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs" /><button type="button" onClick={() => setVisible(keys)} className="rounded-lg bg-slate-800 px-3 py-2 text-xs">Select all</button><button type="button" onClick={() => setVisible(researchPresetColumns(tab, keys))} className="rounded-lg bg-slate-800 px-3 py-2 text-xs">Reset to overview</button></div>
          <div className="grid max-h-72 gap-4 overflow-auto sm:grid-cols-2 xl:grid-cols-3">{researchColumnGroups(tab, keys.filter(key => `${prettyHeader(key)} ${STAT_LABELS[key] || ""}`.toLowerCase().includes(columnSearch.toLowerCase()))).map(group => <fieldset key={group.label}><legend className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{group.label}</legend><div className="flex flex-wrap gap-1.5">{group.keys.map(key => <label key={key} className={`cursor-pointer rounded-md border px-2 py-1 text-[11px] ${visible.includes(key) ? "border-sky-500/60 bg-sky-500/10 text-sky-200" : "border-slate-700 text-slate-400"}`}><input type="checkbox" checked={visible.includes(key)} className="mr-1 accent-sky-500" onChange={() => setVisible(current => current.includes(key) ? current.filter(item => item !== key) : [...current, key])} />{prettyHeader(key)}</label>)}</div></fieldset>)}</div>
        </div>}
        <div className="max-h-[68vh] w-full overflow-auto">
          <GroupedStatTable groups={researchTableGroups(tab, displayedColumns)} rows={filteredRows.slice(0,pageSize)} sort={sortState} onSort={key => setSortState(current => ({key, dir: current.key === key && current.dir === "desc" ? "asc" : "desc"}))} onOpen={openPlayer} loading={loading} caption={`${tab} — ${season}`} />
        </div>
        <div className="border-t border-slate-800 px-3 py-2 text-[11px] text-slate-500">Showing {Math.min(pageSize, filteredRows.length).toLocaleString()} of {filteredRows.length.toLocaleString()} rows · Hover a heading for the full stat name</div>
      </section>
      {filteredRows.length>pageSize && <button onClick={()=>setPageSize((n)=>n+500)} className="mx-auto block rounded-full border border-slate-700 bg-slate-900 px-5 py-2 text-xs font-bold">Load 500 more</button>}
    </> : <div className="rounded-2xl border border-slate-800 bg-slate-900/95 shadow-2xl">
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-800 p-3">
        {qualificationControls}
        {chartSourceRows && <label className="text-[11px] text-slate-400">Player pool<select value={chartScope} onChange={e => { setChartScope(e.target.value); setPosition("ALL"); }} className="mt-1 block rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white"><option value="all">All player stats</option><option value="tab">{tab} players</option></select></label>}
        {mode === "chart" && <ResearchMetricSelector label="X Axis" value={xKeyLocal} onChange={setXKeyLocal} keys={chartMetricKeys} />}
        <ResearchMetricSelector label={mode === "bar" ? "Stat" : "Y Axis"} value={yKeyLocal} onChange={setYKeyLocal} keys={chartMetricKeys} />
        <ResearchMetricSelector label="Minimum Gate" value={gateKeyLocal} onChange={setGateKeyLocal} keys={chartMetricKeys} allowNone />
        <label className="text-[11px] text-slate-400">{gateUsesTotal ? "Gate value (season total)" : "Gate value"}<input type="number" value={gateMinLocal} onChange={(e)=>setGateMinLocal(e.target.value)} className="mt-1 block w-28 rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white"/></label>
        <label className="text-[11px] text-slate-400">Show<select value={chartLimit} onChange={(e)=>setChartLimit(e.target.value)} className="mt-1 block min-w-[130px] rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white">{CHART_LIMIT_OPTIONS.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></label>
        <div className="ml-auto self-end text-xs text-slate-400">Showing {plotted.length.toLocaleString()} of {plottedAll.length.toLocaleString()} points</div>
      </div>
      {qualification && <div className="px-3">{qualificationSummary}</div>}
      {weekFilter === SYNTHETIC_SEASON_AVG && <p className="px-3 pt-2 text-[11px] text-slate-400">In Season Avg, attempts, targets, carries, receptions, completions, plays, and snaps gates use totals across available regular season weeks. Chart axes still show averages; rate gates use averages.</p>}
      <div className={mode === "bar"
        ? "mx-auto w-full max-w-[1120px] lg:w-[85%] max-h-[76vh] overflow-auto rounded-xl bg-slate-900 p-3"
        : "mx-auto w-full max-w-[1120px] lg:w-[85%] aspect-[49/27] min-h-[320px] rounded-xl bg-slate-900 p-3"}>
        <div style={mode === "bar" ? { height: Math.max(360, plotted.length * 30 + 80) } : { height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
        {mode === "bar" ? <BarChart data={plotted} layout="vertical" margin={{top:20,right:35,bottom:35,left:4}}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="4 4" />
          <XAxis type="number" domain={playerBarDomain} allowDataOverflow tickFormatter={(value)=>formatStat(value,yKeyLocal)} tick={{fill:'#cbd5e1',fontSize:11,fontWeight:700}} label={{value:prettyHeader(yKeyLocal),position:'bottom',fill:'#94a3b8',fontWeight:700}} />
          <YAxis type="category" dataKey="_label" width={180} tick={<PlayerBarTick rows={plotted} />} tickLine={false} axisLine={false} interval={0} />
          <Tooltip contentStyle={{background:'#020617',border:'1px solid #475569'}} formatter={(value)=>[formatStat(value, yKeyLocal),prettyHeader(yKeyLocal)]} labelFormatter={(_,payload)=>payload?.[0]?.payload?._fullName || "Player"} />
          <ReferenceLine x={0} stroke="#64748b" />
          <Bar dataKey={yKeyLocal} name={prettyHeader(yKeyLocal)} radius={[0,5,5,0]} maxBarSize={22} onClick={(p)=>playerCapable&&openPlayer(p)}>
            {plotted.map((row, index) => <Cell key={`${row._fullName}-${row._team}-${index}`} fill={colorForTeam(normalizeTeamAbbr(row._team))} stroke="#94a3b8" strokeOpacity={0.5} strokeWidth={0.5} />)}
          </Bar>
        </BarChart> : <ScatterChart margin={{top:35,right:40,bottom:55,left:35}}><CartesianGrid stroke="#1e293b" strokeDasharray="4 4"/><XAxis type="number" dataKey={xKeyLocal} domain={playerXDomain} allowDataOverflow tickFormatter={(value)=>formatStat(value,xKeyLocal)} tick={{fill:'#cbd5e1',fontSize:11,fontWeight:700}} label={{value:prettyHeader(xKeyLocal),position:'bottom',fill:'#94a3b8',fontWeight:700}}/><YAxis type="number" dataKey={yKeyLocal} domain={playerYDomain} allowDataOverflow tickFormatter={(value)=>formatStat(value,yKeyLocal)} tick={{fill:'#cbd5e1',fontSize:11,fontWeight:700}} label={{value:prettyHeader(yKeyLocal),angle:-90,position:'insideLeft',fill:'#94a3b8',fontWeight:700}}/><Tooltip content={(p)=><ResearchTooltip {...p} xKey={xKeyLocal} yKey={yKeyLocal}/>} />
          {plotted.length>0 && <><ReferenceLine x={plotted.reduce((a,r)=>a+r[xKeyLocal],0)/plotted.length} stroke="#64748b" strokeDasharray="5 5"/><ReferenceLine y={plotted.reduce((a,r)=>a+r[yKeyLocal],0)/plotted.length} stroke="#64748b" strokeDasharray="5 5"/></>}
          <Scatter data={plotted} shape={<PlayerLogoPoint />} onClick={(p)=>playerCapable&&openPlayer(p)} /></ScatterChart>}
        </ResponsiveContainer>
        </div>
      </div>
    </div>}

    {selectedPlayer && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-3 backdrop-blur-sm" onMouseDown={(e)=>{if(e.target===e.currentTarget)closePlayerModal()}}>
      <div role="dialog" aria-modal="true" aria-label={`${selectedPlayer.name} player research`} className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-slate-700 bg-[#050816] shadow-2xl">
        <div className="flex items-center gap-4 border-b border-slate-800 p-5">
          <img src={nflLogoUrl(selectedPlayer.team)} className="h-12 w-12 object-contain" onError={(e)=>{e.currentTarget.style.display='none'}} />
          <div><div className="text-xs font-black uppercase tracking-[.18em] text-sky-400">NFL Player Game Log • {tab}</div><div className="text-2xl font-extrabold">{selectedPlayer.name}</div><div className="text-sm text-slate-400">{selectedPlayer.team} • {selectedPlayer.position} • {season}</div></div>
          <button onClick={closePlayerModal} className="ml-auto rounded-full border border-slate-600 px-4 py-2 text-sm font-bold hover:bg-slate-800">Close</button>
        </div>
        <div className="overflow-y-auto p-4">
          <ResearchPlayerSummary tab={tab} rows={playerRows} />
          {playerRows.length>1 && modalMetric && <details className="mb-4 rounded-2xl border border-slate-800 bg-slate-950 p-3"><summary className="cursor-pointer text-sm font-bold text-slate-300">Trend & player comparison</summary><div className="mt-3">
            <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-end">
              <label className="text-xs font-bold text-slate-400">
                TREND
                <select value={modalMetric} onChange={(e)=>setModalMetric(e.target.value)} className="mt-1 block min-w-[260px] rounded border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white">
                  {playerMetrics.map((k)=><option key={k} value={k}>{prettyHeader(k)}</option>)}
                </select>
              </label>

              <div className="relative min-w-0 flex-1">
                <label className="text-xs font-bold text-slate-400">
                  ADD PLAYER TO COMPARE
                  <input
                    value={compareSearch}
                    onChange={(e)=>setCompareSearch(e.target.value)}
                    placeholder="Search player, team, or position…"
                    disabled={comparePlayers.length >= 6}
                    className="mt-1 block w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white outline-none focus:border-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </label>
                {compareSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-auto rounded-xl border border-slate-700 bg-slate-950 shadow-2xl">
                    {compareSuggestions.map((player) => (
                      <button
                        key={player.key}
                        type="button"
                        onClick={()=>addComparePlayer(player)}
                        className="flex w-full items-center gap-3 border-b border-slate-800 px-3 py-2 text-left text-xs hover:bg-sky-950/50 last:border-b-0"
                      >
                        <img src={nflLogoUrl(player.team)} className="h-7 w-7 object-contain" onError={(e)=>{e.currentTarget.style.display='none'}} />
                        <span className="font-bold text-white">{player.name}</span>
                        <span className="ml-auto text-slate-400">{player.team} • {player.position}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-bold text-slate-300">
                <input
                  type="checkbox"
                  checked={includePostseasonCompare}
                  onChange={(e)=>setIncludePostseasonCompare(e.target.checked)}
                  className="h-4 w-4 accent-sky-500"
                />
                Include postseason (Week 19+)
              </label>
              <div className="text-xs text-slate-500">{comparePlayers.length}/6 players</div>
            </div>

            <div className="mb-3 flex flex-wrap gap-2">
              {comparePlayers.map((player, index) => (
                <div key={player.key} className="flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs">
                  <img src={nflLogoUrl(player.team)} className="h-5 w-5 object-contain" onError={(e)=>{e.currentTarget.style.display='none'}} />
                  <span className="font-bold text-white">{player.name}</span>
                  <span className="text-slate-500">{player.team}</span>
                  {index > 0 && (
                    <button type="button" onClick={()=>removeComparePlayer(player.key)} className="ml-1 text-slate-400 hover:text-rose-400" aria-label={`Remove ${player.name}`}>
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            {comparisonStats.length > 1 && (
              <div className="mb-4">
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
                  {[
                    ["Best Average", comparisonLeaders.average, "average"],
                    ["Highest Median", comparisonLeaders.median, "median"],
                    ["Highest Ceiling", comparisonLeaders.ceiling, "ceiling"],
                    ["Best Floor", comparisonLeaders.floor, "floor"],
                    ["Standard Deviation", comparisonLeaders.consistency, "stdDev"],
                  ].map(([label, leader, key]) => (
                    <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/70 p-3">
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</div>
                      <div className="mt-1 truncate text-sm font-extrabold text-white">{leader?.name || "—"}</div>
                      <div className="mt-1 text-lg font-black text-sky-300">
                        {leader ? formatStat(leader[key], modalMetric) : "—"}
                      </div>
                      
                    </div>
                  ))}
                </div>

              </div>
            )}

            <div className="h-72 w-full rounded-xl bg-slate-900">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={comparisonChartData}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="4 4"/>
                  <XAxis dataKey="_week" tick={{fill:'#cbd5e1'}}/>
                  <YAxis domain={paddedChartDomain(comparisonChartData, comparisonSeries.map(series => series.dataKey))} allowDataOverflow tick={{fill:'#cbd5e1'}} tickFormatter={(value)=>formatStat(value,modalMetric)}/>
                  <Tooltip contentStyle={{background:'#020617',border:'1px solid #475569'}} formatter={(value)=>formatStat(value,modalMetric)}/>
                  <Legend />
                  {comparisonSeries.map((series, index) => {
                    const colors = ["#38bdf8", "#f472b6", "#34d399", "#f59e0b", "#a78bfa", "#fb7185"];
                    return (
                      <Line
                        key={series.key}
                        type="monotone"
                        dataKey={series.dataKey}
                        name={`${series.name} (${series.team})`}
                        stroke={colors[index % colors.length]}
                        strokeWidth={index === 0 ? 3.5 : 2.5}
                        dot={{r:index === 0 ? 4 : 3}}
                        connectNulls={false}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {comparisonStats.length > 0 && (
              <div className="mt-4">
                <div className="w-full overflow-x-auto rounded-xl border border-slate-800">
                  <table className="min-w-[760px] w-full text-xs">
                    <thead className="bg-slate-800 text-slate-300">
                      <tr>
                        <th className="px-3 py-2 text-left">Player</th>
                        <th className="px-3 py-2 text-center">Games</th>
                        <th className="px-3 py-2 text-center">Average</th>
                        <th className="px-3 py-2 text-center">Median</th>
                        <th className="px-3 py-2 text-center">Ceiling</th>
                        <th className="px-3 py-2 text-center">Floor</th>
                        <th className="px-3 py-2 text-center">Std Dev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonStats.map((player, index) => (
                        <tr key={player.key} className={index % 2 ? "bg-slate-900" : "bg-slate-950"}>
                          <td className="whitespace-nowrap px-3 py-2 font-bold text-white">
                            {player.name} <span className="text-slate-500">({player.team})</span>
                          </td>
                          <td className="px-3 py-2 text-center">{player.games}</td>
                          <td className="px-3 py-2 text-center">{formatStat(player.average, modalMetric)}</td>
                          <td className="px-3 py-2 text-center">{formatStat(player.median, modalMetric)}</td>
                          <td className="px-3 py-2 text-center">{formatStat(player.ceiling, modalMetric)}</td>
                          <td className="px-3 py-2 text-center">{formatStat(player.floor, modalMetric)}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={comparisonLeaders.consistency?.key === player.key ? "font-black text-emerald-400" : ""}>
                              {formatStat(player.stdDev, modalMetric)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div></details>}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><div><div className="text-sm font-bold text-white">Weekly stats</div><p className="text-[11px] text-slate-400">{playerRows.some(row => row._syntheticSeasonAverage) ? "Season average first, then each available game" : "Stats for each available game"}</p></div><button type="button" aria-pressed={modalDetailed} onClick={() => setModalDetailed(value => !value)} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-bold">{modalDetailed ? "Show overview" : "Show detailed stats"}</button></div>
          {playerTableScrollWidth > 0 && (
            <div className="mb-2 rounded-xl border border-slate-700 bg-slate-950 px-2 py-2">
              <div
                ref={playerTableMirrorRef}
                onScroll={syncPlayerTableFromMirror}
                className="w-full overflow-x-scroll overflow-y-hidden"
                aria-label="Player table horizontal scrollbar"
              >
                <div style={{ width: playerTableScrollWidth, height: 1 }} />
              </div>
              <div className="mt-1 text-center text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Scroll table left or right
              </div>
            </div>
          )}
          <div
            ref={playerTableScrollRef}
            onScroll={syncPlayerTableFromMain}
            className="player-game-log-scroller w-full max-w-full overflow-x-auto overflow-y-visible overscroll-x-contain rounded-2xl border border-slate-800"
          >
            <GroupedStatTable groups={researchTableGroups(tab, researchPresetColumns(tab, keys, modalDetailed ? "detailed" : "overview").filter(key => researchStatGroup(tab,key) !== "Player" || key === "week"))} rows={playerRows} caption={`${selectedPlayer.name} weekly research stats`} />
          </div>
        </div>
      </div>
    </div>}
  </>;
}



function pct(value) {
  return `${(Number(value || 0) * 100).toFixed(1)}%`;
}



function addCompetitionRanks(items = [], valueKey, outputKey) {
  const ranked = [...items]
    .filter((item) => Number.isFinite(Number(item?.[valueKey])))
    .sort((a, b) => Number(b?.[valueKey]) - Number(a?.[valueKey]));

  let previousValue = null;
  let previousRank = 0;

  ranked.forEach((item, index) => {
    const value = Number(item?.[valueKey]);
    const rank = previousValue !== null && value === previousValue
      ? previousRank
      : index + 1;

    item[outputKey] = rank;
    previousValue = value;
    previousRank = rank;
  });

  return items;
}

function addWeeklyFantasyRanks(rows = [], includePostseason = false) {
  const output = rows.map((row) => ({ ...row }));
  const groups = new Map();

  for (const row of output) {
    const week = researchWeek(row);
    if (week <= 0 || (!includePostseason && week > 18)) continue;
    const seasonValue = Number(row?.season || 0);
    const position = researchPos(row) || "UNK";
    const key = `${seasonValue}__${week}__${position}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }

  for (const groupRows of groups.values()) {
    addCompetitionRanks(groupRows, "dk_points", "dk_week_rank");
    addCompetitionRanks(groupRows, "fd_points", "fd_week_rank");
    addCompetitionRanks(groupRows, "custom_points", "custom_week_rank");
  }

  return output;
}

function buildSeasonFantasyRanks(rows = [], includePostseason = false) {
  const totals = new Map();

  for (const row of rows) {
    const week = researchWeek(row);
    if (week <= 0 || (!includePostseason && week > 18)) continue;

    const name = researchName(row);
    if (!name) continue;

    const team = researchTeam(row);
    const position = researchPos(row);
    const playerId = String(row.player_id || "");
    const key = playerId || `${name.toLowerCase()}__${team}__${position}`;
    const current = totals.get(key) || {
      key,
      name,
      team,
      position,
      dk_points: 0,
      fd_points: 0,
      custom_points: 0,
      games: 0,
    };

    current.dk_points += numberValue(row.dk_points);
    current.fd_points += numberValue(row.fd_points);
    current.custom_points += numberValue(row.custom_points);
    current.games += 1;
    totals.set(key, current);
  }

  const players = Array.from(totals.values());
  const byPosition = new Map();

  for (const player of players) {
    const position = player.position || "UNK";
    if (!byPosition.has(position)) byPosition.set(position, []);
    byPosition.get(position).push(player);
  }

  for (const positionPlayers of byPosition.values()) {
    addCompetitionRanks(positionPlayers, "dk_points", "dk_season_rank");
    addCompetitionRanks(positionPlayers, "fd_points", "fd_season_rank");
    addCompetitionRanks(positionPlayers, "custom_points", "custom_season_rank");
  }

  return new Map(players.map((player) => [player.key, player]));
}

function GameLogView({ rows, season, loading }) {
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [includePostseason, setIncludePostseason] = useState(false);
  const [receptionScoring, setReceptionScoring] = useState("1");
  const [passingTdScoring, setPassingTdScoring] = useState("4");
  const [sortState, setSortState] = useState({ key: "week", dir: "asc" });

  const enrichedBase = useMemo(() => enrichFantasyScoring(rows), [rows]);
  const customScoredRows = useMemo(
    () => enrichCustomScoring(
      enrichedBase,
      Number(receptionScoring),
      Number(passingTdScoring)
    ),
    [enrichedBase, receptionScoring, passingTdScoring]
  );
  const enriched = useMemo(
    () => addWeeklyFantasyRanks(customScoredRows, includePostseason),
    [customScoredRows, includePostseason]
  );
  const seasonRankMap = useMemo(
    () => buildSeasonFantasyRanks(customScoredRows, includePostseason),
    [customScoredRows, includePostseason]
  );

  const players = useMemo(() => {
    const map = new Map();
    for (const row of enriched) {
      const name = researchName(row);
      if (!name) continue;
      const team = researchTeam(row);
      const position = researchPos(row);
      const playerId = String(row.player_id || "");
      const key = playerId || `${name.toLowerCase()}__${team}__${position}`;
      if (!map.has(key)) map.set(key, { key, name, team, position, playerId });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [enriched]);

  const suggestions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return [];
    return players
      .filter((player) =>
        player.name.toLowerCase().includes(needle)
        || player.team.toLowerCase().includes(needle)
        || player.position.toLowerCase().includes(needle)
      )
      .slice(0, 12);
  }, [players, search]);

  const selectedPlayer = useMemo(
    () => players.find((player) => player.key === selectedKey) || null,
    [players, selectedKey]
  );

  const selectedSeasonRank = useMemo(
    () => selectedPlayer ? seasonRankMap.get(selectedPlayer.key) || null : null,
    [selectedPlayer, seasonRankMap]
  );

  const playerRows = useMemo(() => {
    if (!selectedPlayer) return [];
    let output = enriched.filter((row) => {
      const playerId = String(row.player_id || "");
      const fallbackKey = `${researchName(row).toLowerCase()}__${researchTeam(row)}__${researchPos(row)}`;
      const rowKey = playerId || fallbackKey;
      const week = researchWeek(row);
      return rowKey === selectedPlayer.key && week > 0 && (includePostseason || week <= 18);
    });

    const { key, dir } = sortState;
    const sign = dir === "asc" ? 1 : -1;
    return [...output].sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
      return String(av ?? "").localeCompare(String(bv ?? "")) * sign;
    });
  }, [selectedPlayer, enriched, includePostseason, sortState]);

  const seasonSummary = useMemo(() => {
    if (!playerRows.length) return null;
    const sum = (key) => playerRows.reduce((total, row) => total + numberValue(row?.[key]), 0);
    const passAttempts = sum("pass_attempt");
    const passCompletions = sum("pass_completions");
    const rushAttempts = sum("rush_attempt");
    const rushYards = sum("rush_yards_gained");
    return {
      games: playerRows.length,
      dk: sum("dk_points"),
      fd: sum("fd_points"),
      custom: sum("custom_points"),
      passYards: sum("pass_yards_gained"),
      passTds: sum("pass_touchdown"),
      rushYards,
      recYards: sum("rec_yards_gained"),
      touchdowns: sum("total_touchdown"),
      completionPct: passAttempts > 0 ? passCompletions / passAttempts * 100 : 0,
      ypc: rushAttempts > 0 ? rushYards / rushAttempts : 0,
    };
  }, [playerRows]);

  const playerPosition = String(selectedPlayer?.position || "").toUpperCase();
  const logGroups = [
    {label: "Game", columns: [{key: "week", label: "Wk"}, {key: "opponent", label: "Opp"}]},
    ...(playerPosition === "QB" ? [{label: "Passing", columns: [
      ["pass_completions","Cmp"], ["pass_attempt","Att"], ["completion_percentage","Cmp%"], ["pass_yards_gained","Yds"], ["pass_touchdown","TD"], ["pass_interception","INT"]
    ].map(([key,label]) => ({key,label}))}] : []),
    ...(["QB","RB"].includes(playerPosition) ? [{label: "Rushing", columns: [
      ["rush_attempt","Att"], ["rush_yards_gained","Yds"], ["rush_touchdown","TD"], ["yards_per_carry","YPC"]
    ].map(([key,label]) => ({key,label}))}] : []),
    ...(playerPosition !== "QB" ? [{label: "Receiving", columns: [
      ["rec_attempt","Tgt"], ["receptions","Rec"], ["rec_yards_gained","Yds"], ["rec_touchdown","TD"]
    ].map(([key,label]) => ({key,label}))}, {label: "Usage", columns: [
      ["target_share","Tgt%"], ["air_yards_share","Air%"]
    ].map(([key,label]) => ({key,label}))}] : []),
    {label: "Fantasy", columns: [["custom_points","Custom"],["dk_points","DK"],["fd_points","FD"],
      ...(["total_fantasy_points_exp","total_fantasy_points_diff"].filter(key => playerRows.some(row => row[key] != null)).map(key => [key, key.endsWith("_exp") ? "xFP" : "FPOE"]))
    ].map(([key,label]) => ({key,label, title: STAT_HELP[key] || prettyHeader(key)}))},
  ];

  const displayValue = (row, key) => {
    const value = row?.[key];
    if (value == null || value === "") return "—";
    if (key === "opponent") return <span className="inline-flex items-center gap-1.5"><img src={nflLogoUrl(String(value).replace(/^@/, ""))} alt="" className="h-5 w-5 object-contain" onError={e => {e.currentTarget.style.visibility = "hidden";}} />{value}</span>;
    if (["total_fantasy_points_exp", "total_fantasy_points_diff"].includes(key)) return <span className={key.endsWith("_diff") ? (numberValue(value) > 0 ? "text-emerald-300" : numberValue(value) < 0 ? "text-rose-300" : "text-slate-400") : "text-amber-300"}>{numberValue(value).toFixed(1)}</span>;
    if (["target_share", "air_yards_share"].includes(key)) return `${(numberValue(value) * 100).toFixed(1)}%`;
    if (key === "completion_percentage") return `${numberValue(value).toFixed(1)}%`;
    if (key === "yards_per_carry") return numberValue(value).toFixed(2);
    if (key === "dk_points") {
      const rank = row?.dk_week_rank;
      return <span>{numberValue(value).toFixed(2)}{rank ? <span className="ml-1 text-[10px] font-bold text-slate-500">({rank})</span> : null}</span>;
    }
    if (key === "fd_points") {
      const rank = row?.fd_week_rank;
      return <span>{numberValue(value).toFixed(2)}{rank ? <span className="ml-1 text-[10px] font-bold text-slate-500">({rank})</span> : null}</span>;
    }
    if (key === "custom_points") {
      const rank = row?.custom_week_rank;
      return <span>{numberValue(value).toFixed(2)}{rank ? <span className="ml-1 text-[10px] font-bold text-slate-500">({rank})</span> : null}</span>;
    }
    return fmt(value);
  };

  return (
    <div className="space-y-5">
      <section className="rounded-3xl border border-slate-800 bg-slate-900/95 p-4 shadow-xl md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-black uppercase tracking-[.2em] text-sky-400">NFL Player Game Log</div>
            <h2 className="mt-1 text-2xl font-black text-white">Weekly Box Scores</h2>
            <p className="mt-1 text-sm text-slate-400">Search any player to view basic weekly stats with calculated DraftKings and FanDuel scoring.</p>
            <div className="relative mt-4">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search player, team, or position…"
                className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-sky-500"
              />
              {suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-80 overflow-auto rounded-xl border border-slate-700 bg-slate-950 shadow-2xl">
                  {suggestions.map((player) => (
                    <button
                      key={player.key}
                      type="button"
                      onClick={() => {
                        setSelectedKey(player.key);
                        setSearch(player.name);
                      }}
                      className="flex w-full items-center gap-3 border-b border-slate-800 px-4 py-3 text-left hover:bg-sky-950/50 last:border-b-0"
                    >
                      <img
                        src={nflLogoUrl(player.team)}
                        className="h-9 w-9 object-contain"
                        onError={(event) => { event.currentTarget.style.display = "none"; }}
                      />
                      <span className="font-bold text-white">{player.name}</span>
                      <span className="ml-auto text-xs text-slate-400">{player.team} • {player.position}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid w-full gap-2 sm:grid-cols-2 lg:w-auto lg:min-w-[430px]">
            <label className="text-xs font-bold text-slate-400">
              Reception scoring
              <select
                value={receptionScoring}
                onChange={(event) => setReceptionScoring(event.target.value)}
                className="mt-1 block w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-white"
              >
                <option value="0">Standard</option>
                <option value="0.5">0.5 PPR</option>
                <option value="1">1 PPR</option>
              </select>
            </label>

            <label className="text-xs font-bold text-slate-400">
              Passing TD scoring
              <select
                value={passingTdScoring}
                onChange={(event) => setPassingTdScoring(event.target.value)}
                className="mt-1 block w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm text-white"
              >
                <option value="4">4 points</option>
                <option value="6">6 points</option>
              </select>
            </label>

            <label className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-bold text-slate-300 sm:col-span-2">
              <input
                type="checkbox"
                checked={includePostseason}
                onChange={(event) => setIncludePostseason(event.target.checked)}
                className="h-4 w-4 accent-sky-500"
              />
              Include postseason (Week 19+)
            </label>
          </div>
        </div>
      </section>

      {!selectedPlayer && !loading && (
        <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-950/40 p-12 text-center">
          <div className="text-lg font-black text-white">Search for a player</div>
          <div className="mt-2 text-sm text-slate-400">Their complete weekly game log will appear here.</div>
        </div>
      )}

      {loading && <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 text-sm text-slate-300">Loading game logs…</div>}

      {selectedPlayer && (
        <>
          <section className="overflow-hidden rounded-3xl border border-slate-800 bg-[#050816] shadow-2xl">
            <div className="flex flex-wrap items-center gap-4 border-b border-slate-800 p-5">
              <img
                src={nflLogoUrl(selectedPlayer.team)}
                className="h-14 w-14 object-contain"
                onError={(event) => { event.currentTarget.style.display = "none"; }}
              />
              <div>
                <div className="text-xs font-black uppercase tracking-[.18em] text-sky-400">{season} Game Log</div>
                <div className="text-2xl font-black text-white">{selectedPlayer.name}</div>
                <div className="text-sm text-slate-400">{selectedPlayer.team} • {selectedPlayer.position}</div>
                {selectedSeasonRank && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[11px] font-black text-sky-300">
                      DK {selectedPlayer.position} Rank #{selectedSeasonRank.dk_season_rank}
                    </span>
                    <span className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2.5 py-1 text-[11px] font-black text-indigo-300">
                      FD {selectedPlayer.position} Rank #{selectedSeasonRank.fd_season_rank}
                    </span>
                    <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-black text-emerald-300">
                      Custom {selectedPlayer.position} Rank #{selectedSeasonRank.custom_season_rank}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {seasonSummary && (
              <div className="grid grid-cols-2 gap-2 border-b border-slate-800 p-4 lg:grid-cols-4">
                {[
                  ["Games", seasonSummary.games],
                  ["Avg DK", (seasonSummary.dk / seasonSummary.games).toFixed(1)],
                  ["Avg FD", (seasonSummary.fd / seasonSummary.games).toFixed(1)],
                  playerPosition === "QB" ? ["Pass Yds/G", (seasonSummary.passYards / seasonSummary.games).toFixed(1)] : playerPosition === "RB" ? ["Rush Att + Tgt/G", (playerRows.reduce((sum,row) => sum + numberValue(row.rush_attempt) + numberValue(row.rec_attempt), 0) / seasonSummary.games).toFixed(1)] : ["Targets/G", (playerRows.reduce((sum,row) => sum + numberValue(row.rec_attempt), 0) / seasonSummary.games).toFixed(1)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/70 p-3">
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</div>
                    <div className="mt-1 text-xl font-black text-sky-300">{value}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="max-h-[68vh] overflow-auto">
              <GroupedStatTable groups={logGroups} rows={playerRows} sort={sortState} onSort={key => setSortState(current => ({key, dir: current.key === key && current.dir === "desc" ? "asc" : "desc"}))} renderCell={displayValue} caption={`${selectedPlayer.name} weekly game log — ${season}`} />
            </div>
          </section>

          <details className="rounded-2xl border border-slate-800 bg-slate-900/95 p-4 text-xs text-slate-400"><summary className="cursor-pointer font-bold text-slate-300">Scoring and rank details</summary><p className="mt-2 leading-relaxed">
            Custom scoring uses the selected Standard, 0.5 PPR, or 1 PPR reception setting and either 4- or 6-point passing touchdowns. Custom scoring does not add yardage bonuses. DK and FD retain their site-specific reception scoring and yardage bonuses. Parentheses beside each score show position rank for that week; header badges show season rank within the position. xFP and FPOE use the source opportunity model scoring basis, separate from the custom scoring controls.</p>
          </details>
        </>
      )}
    </div>
  );
}

/** FTN metrics retain their original scope: rates are flags / available charted values.
 * Pass flags are NOT advertised as per-attempt rates. Zero counts are valid.
 */
const FTN_METRICS = [
  ["plays", "Charted plays", "int"],
  ["games", "Games", "int"],
  ["motion", "Motion %", "rate", "is_motion"],
  ["playAction", "Play action %", "rate", "is_play_action"],
  ["rpo", "RPO %", "rate", "is_rpo"],
  ["screen", "Screen %", "rate", "is_screen_pass"],
  ["noHuddle", "No huddle %", "rate", "is_no_huddle"],
  ["outPocket", "Out of pocket %", "rate", "is_qb_out_of_pocket"],
  ["trick", "Trick play %", "rate", "is_trick_play"],
  ["sneak", "QB sneak %", "rate", "is_qb_sneak"],
  ["catchable", "Catchable flag %", "rate", "is_catchable_ball"],
  ["contested", "Contested flag %", "rate", "is_contested_ball"],
  ["intWorthy", "INT-worthy flag %", "rate", "is_interception_worthy"],
  ["drop", "Drop flag %", "rate", "is_drop"],
  ["throwAway", "Throwaway flag %", "rate", "is_throw_away"],
  ["qbFaultSack", "QB-fault sack flag %", "rate", "is_qb_fault_sack"],
  ["backfield", "Avg. backfield", "decimal", "n_offense_backfield"],
  ["box", "Avg. defenders in box", "decimal", "n_defense_box"],
  ["blitzers", "Avg. blitzers", "decimal", "n_blitzers"],
  ["rushers", "Avg. pass rushers", "decimal", "n_pass_rushers"],
];
const FTN_DEFAULT_COLUMNS = ["games", "plays", "motion", "playAction", "rpo", "screen", "noHuddle", "box", "backfield", "rushers", "blitzers"];
const ftnMetric = (key) => FTN_METRICS.find(([k]) => k === key);
function ftnNumber(value) {
  if (value == null || String(value).trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
function ftnFlag(value) {
  if (value === true || value === 1 || value === "1" || value === "true") return 1;
  if (value === false || value === 0 || value === "0" || value === "false") return 0;
  return null;
}
function ftnFormat(value, key) {
  if (value == null || !Number.isFinite(value)) return "—";
  const type = ftnMetric(key)?.[2];
  return type === "rate" ? `${(value * 100).toFixed(1)}%` : type === "int" ? value.toLocaleString() : value.toFixed(2);
}
function summarizeFtn(list) {
  const out = { plays: list.length, games: new Set(list.map(r => r._game).filter(Boolean)).size };
  for (const [key, , type, field] of FTN_METRICS) {
    if (!field) continue;
    let sum = 0, count = 0;
    for (const row of list) {
      const value = type === "rate" ? ftnFlag(row[field]) : ftnNumber(row[field]);
      if (value != null) { sum += value; count++; }
    }
    out[key] = count ? sum / count : null;
  }
  return out;
}
function ftnPlayKey(game, play) {
  const id = ftnNumber(play);
  return game && id != null ? `${String(game).trim()}__${id}` : null;
}
function enrichFtnRows(rows, pbpRows, season, situation) {
  const lookup = new Map();
  for (const play of pbpRows) {
    const key = ftnPlayKey(play.game_id, play.play_id);
    const team = normalizeTeamAbbr(play.posteam || "");
    if (key && team) lookup.set(key, play);
  }
  const seen = new Set();
  return rows.filter(row => row.season == null || Number(row.season) === Number(season)).flatMap(row => {
    const key = ftnPlayKey(row.nflverse_game_id, row.nflverse_play_id);
    if (key && seen.has(key)) return [];
    if (key) seen.add(key);
    const play = key ? lookup.get(key) : null;
    return [{ ...row, _team: normalizeTeamAbbr(play?.posteam || ""),
      _week: Number(row.week || play?.week || 0), _game: String(row.nflverse_game_id || ""),
      _playType: play?.play_type || "", _dropback: Number(play?.qb_dropback) === 1,
      _matchesSituation: !situation || (play && matchesSituation(play, situation)),
    }];
  });
}
function groupFtnRows(rows, groupBy) {
  const groups = new Map();
  for (const row of rows) {
    if (!row._team) continue;
    const key = groupBy === "team" ? row._team : `${row._game}__${row._team}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  return [...groups].map(([id, list]) => {
    const first = list[0];
    return { id, team: first._team, game: groupBy === "team" ? "" : first._game,
      week: groupBy === "team" ? null : first._week, ...summarizeFtn(list),
      _team: first._team, _logo: nflLogoUrl(first._team),
      _label: groupBy === "team" ? first._team : `${first._team} W${first._week}`,
    };
  });
}
function sortFtnRows(rows, key, dir) {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv != null) return 1;
    if (bv == null && av != null) return -1;
    const delta = av == null && bv == null ? 0 : typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
    return delta * sign || String(a.id).localeCompare(String(b.id));
  });
}
function TeamTendenciesView({ rows, season, loading, error = "", situation }) {
  const [pbpState, setPbpState] = useState({ season: null, rows: [], loading: true, error: "" });
  const [retry, setRetry] = useState(0);
  const [team, setTeam] = useState("ALL");
  const [week, setWeek] = useState("ALL");
  const [game, setGame] = useState("ALL");
  const [sample, setSample] = useState("all");
  const [groupBy, setGroupBy] = useState("team");
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [minPlays, setMinPlays] = useState(0);
  const [sort, setSort] = useState({ key: "motion", dir: "desc" });
  const [columns, setColumns] = useState(FTN_DEFAULT_COLUMNS);
  const [chartType, setChartType] = useState("scatter");
  const [xMetric, setXMetric] = useState("motion");
  const [yMetric, setYMetric] = useState("playAction");
  const [chartLimit, setChartLimit] = useState("all");
  const [showLabels, setShowLabels] = useState(true);
  const [showMeans, setShowMeans] = useState(true);
  const [diagonal, setDiagonal] = useState("off");
  const [axisColor, setAxisColor] = useState("#64748b");
  const [barColor, setBarColor] = useState("#38bdf8");

  useEffect(() => {
    let dead = false;
    setPbpState({ season, rows: [], loading: true, error: "" });
    fetchJson(`${BASE}/${season}/pbp.json`).then(data => {
      if (!Array.isArray(data)) throw new Error("Play-by-play file is not an array.");
      if (!dead) setPbpState({ season, rows: data, loading: false, error: "" });
    }).catch(error => {
      if (!dead) setPbpState({ season, rows: [], loading: false, error: String(error?.message || error) });
    });
    return () => { dead = true; };
  }, [season, retry]);
  useEffect(() => { setTeam("ALL"); setWeek("ALL"); setGame("ALL"); }, [season]);
  const busy = loading || pbpState.season !== season || pbpState.loading;
  const enriched = useMemo(() => enrichFtnRows(rows, pbpState.season === season ? pbpState.rows : [], season, situation), [rows, pbpState, season, situation]);
  const matched = useMemo(() => enriched.filter(r => r._team && r._matchesSituation), [enriched]);
  const teams = useMemo(() => [...new Set(matched.map(r => r._team))].sort(), [matched]);
  const weeks = useMemo(() => [...new Set(matched.map(r => r._week).filter(w => w > 0))].sort((a,b) => a-b), [matched]);
  const games = useMemo(() => [...new Set(matched.filter(r => (team === "ALL" || r._team === team) && (week === "ALL" || r._week === Number(week))).map(r => r._game).filter(Boolean))].sort(), [matched, team, week]);
  useEffect(() => {
    if (busy) return;
    if (team !== "ALL" && !teams.includes(team)) setTeam("ALL");
    if (week !== "ALL" && !weeks.includes(Number(week))) setWeek("ALL");
    if (game !== "ALL" && !games.includes(game)) setGame("ALL");
  }, [busy, teams, weeks, games, team, week, game]);
  const sampleRows = useMemo(() => matched.filter(r =>
    (week === "ALL" || r._week === Number(week)) && (game === "ALL" || r._game === game) &&
    (sample === "all" || (sample === "scrimmage" ? ["pass", "run"].includes(r._playType) : r._dropback))
  ), [matched, week, game, sample]);
  const selectedRows = useMemo(() => sampleRows.filter(r => team === "ALL" || r._team === team), [sampleRows, team]);
  const summary = useMemo(() => summarizeFtn(selectedRows), [selectedRows]);
  const league = useMemo(() => summarizeFtn(sampleRows), [sampleRows]);
  const query = search.trim().toLowerCase();
  const eligible = useMemo(() => groupFtnRows(selectedRows, groupBy).filter(r => r.plays >= minPlays &&
    (!query || `${r.team} ${TEAM_NAMES[r.team] || ""} ${r.game} ${r.week || ""}`.toLowerCase().includes(query))
  ), [selectedRows, groupBy, minPlays, query]);
  const tableRows = useMemo(() => sortFtnRows(eligible, sort.key, sort.dir), [eligible, sort]);
  const comparisonRows = useMemo(() => limitChartRows(sortFtnRows(eligible.filter(r => r[yMetric] != null && (chartType !== "scatter" || r[xMetric] != null)), yMetric, "desc"), chartLimit, yMetric), [eligible, xMetric, yMetric, chartType, chartLimit]);
  // Choose series over the entire selected range, then retain gaps for missing team-weeks.
  const trend = useMemo(() => {
    const qualified = groupFtnRows(selectedRows, "team").filter(r => r.plays >= minPlays &&
      (!query || `${r.team} ${TEAM_NAMES[r.team] || ""}`.toLowerCase().includes(query)) && r[yMetric] != null);
    const selected = limitChartRows(sortFtnRows(qualified, yMetric, "desc"), chartLimit, yMetric);
    const byWeek = new Map();
    for (const r of selectedRows) {
      if (!byWeek.has(r._week)) byWeek.set(r._week, []);
      byWeek.get(r._week).push(r);
    }
    const points = [...byWeek].sort(([a],[b]) => a-b).map(([w, list]) => {
      const point = { week: w };
      for (const t of selected) {
        const plays = list.filter(r => r._team === t.team);
        point[t.team] = plays.length ? summarizeFtn(plays)[yMetric] : null;
        point[`${t.team}__plays`] = plays.length;
      }
      return point;
    });
    return { teams: selected.map(r => r.team), points };
  }, [selectedRows, minPlays, query, yMetric, chartLimit]);
  const xDomain = paddedChartDomain(comparisonRows, xMetric);
  const yDomain = paddedChartDomain(comparisonRows, yMetric);
  const mean = key => comparisonRows.length ? comparisonRows.reduce((sum,r) => sum+r[key],0)/comparisonRows.length : null;
  const xMean = mean(xMetric), yMean = mean(yMetric);
  const segment = chartType === "scatter" && diagonal !== "off" && xMean != null
    ? clipLineToRect(1, diagonal === "means" ? yMean-xMean : 0, ...xDomain, ...yDomain) : null;
  const label = key => ftnMetric(key)?.[1] || key;
  const toggleSort = key => setSort(s => ({ key, dir: s.key === key ? (s.dir === "asc" ? "desc" : "asc") : ["team","game"].includes(key) ? "asc" : "desc" }));
  const resetChart = () => { setChartType("scatter"); setXMetric("motion"); setYMetric("playAction"); setChartLimit("all"); setShowLabels(true); setShowMeans(true); setDiagonal("off"); setAxisColor("#64748b"); setBarColor("#38bdf8"); };
  const resetFilters = () => { setTeam("ALL"); setWeek("ALL"); setGame("ALL"); setSample("all"); setGroupBy("team"); setSearch(""); setMinPlays(0); };
  const exportRows = () => {
    const isTrend = view === "chart" && chartType === "trend";
    const data = isTrend ? trend.points.flatMap(p => trend.teams.map(t => ({ team:t, week:p.week, plays:p[`${t}__plays`], [yMetric]:p[t] }))) : view === "chart" ? comparisonRows : tableRows;
    const keys = isTrend ? [...new Set(["team","week","plays",yMetric])] : ["team", ...(groupBy === "game" ? ["game","week"] : []), ...(view === "chart" ? FTN_METRICS.map(([k])=>k) : columns)];
    // Percentages are written as their displayed numeric percentage, without a percent sign.
    const csv = data.map(r => Object.fromEntries(keys.map(k => [k, ftnMetric(k)?.[2] === "rate" && r[k] != null ? +(r[k]*100).toFixed(3) : r[k]])));
    exportToCsv(`NFL_FTN_Tendencies_${season}_${view}_${view === "chart" ? chartType : groupBy}.csv`, csv, keys);
  };
  const inputClass = "mt-1 block w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500";
  const buttonClass = "rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800 disabled:opacity-40";
  const metricSelect = (title, value, setter) => <label className="min-w-0 text-xs text-slate-400">{title}<select className={inputClass} value={value} onChange={e=>setter(e.target.value)}>{FTN_METRICS.map(([key,name])=><option key={key} value={key}>{name}</option>)}</select></label>;
  const chartTooltip = ({active,payload}) => {
    if (!active || !payload?.length) return null;
    const r = payload[0]?.payload;
    if (!r) return null;
    return <div style={{background:"#020617",border:"1px solid #64748b",color:"#fff",padding:14,borderRadius:12,maxWidth:300,maxHeight:330,overflow:"auto",fontSize:12,boxShadow:"0 12px 28px #0008"}}>
      {chartType === "trend" ? <><b>Week {r.week} · {label(yMetric)}</b>{payload.filter(p=>p.value!=null).map(p=><div key={p.dataKey} className="mt-1 flex justify-between gap-5"><span>{p.dataKey}</span><b>{ftnFormat(p.value,yMetric)}</b></div>)}</> : <>
        <div className="mb-2 flex items-center gap-2"><img src={nflLogoUrl(r.team)} alt="" className="h-6 w-6 object-contain"/><b>{TEAM_NAMES[r.team] || r.team}</b></div>
        {r.game && <div className="mb-2 text-slate-400">Week {r.week} · {r.game}</div>}
        {chartType === "scatter" && <div>{label(xMetric)}: <b>{ftnFormat(r[xMetric],xMetric)}</b></div>}
        <div>{label(yMetric)}: <b>{ftnFormat(r[yMetric],yMetric)}</b></div><div className="mt-2 text-slate-400">{r.plays.toLocaleString()} charted plays · {r.games} games</div>
      </>}
    </div>;
  };
  const chartEmpty = chartType === "trend" ? !trend.teams.length || !trend.points.length : !comparisonRows.length;
  const cardKeys = ["motion","playAction","rpo","screen","noHuddle","outPocket"];
  const identityColumns = [["team","Team"], ...(groupBy === "game" ? [["game","Game"],["week","Week"]] : [])];
  const allColumns = [...identityColumns, ...columns.map(k => [k,label(k)])];
  const exportDisabled = busy || (view === "table" ? !tableRows.length : chartEmpty);
  return <div className="min-w-0 max-w-full space-y-4 text-slate-100">
    <section className="rounded-2xl border border-slate-800 bg-slate-900/95 p-4 md:p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-sky-400">FTN CHARTING · {season}</div><h2 className="mt-1 text-2xl font-black text-white">Team Tendencies</h2><p className="mt-1 text-xs text-slate-400">Explore offensive usage, compare teams, and follow weekly changes.</p></div>
        <div className="flex flex-wrap items-center gap-2"><span className="text-xs text-slate-400">{busy ? "Loading…" : `${summary.plays.toLocaleString()} plays · ${summary.games} games`}</span><button className={buttonClass} onClick={resetFilters}>Reset filters</button><button className={buttonClass} onClick={exportRows} disabled={exportDisabled}>Export CSV</button></div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <label className="text-xs text-slate-400">Team<select className={inputClass} value={team} onChange={e=>{setTeam(e.target.value);setGame("ALL");}}><option value="ALL">All teams</option>{teams.map(t=><option key={t} value={t}>{t} · {TEAM_NAMES[t] || t}</option>)}</select></label>
        <label className="text-xs text-slate-400">Week<select className={inputClass} value={week} onChange={e=>{setWeek(e.target.value);setGame("ALL");}}><option value="ALL">All available weeks</option>{weeks.map(w=><option key={w} value={w}>Week {w}</option>)}</select></label>
        <label className="text-xs text-slate-400">Game<select className={inputClass} value={game} onChange={e=>setGame(e.target.value)}><option value="ALL">All games</option>{games.map(g=><option key={g} value={g}>{g.replace(/_/g," · ")}</option>)}</select></label>
        <label className="text-xs text-slate-400">Play sample<select className={inputClass} value={sample} onChange={e=>setSample(e.target.value)}><option value="all">All charted plays</option><option value="scrimmage">PBP pass / run plays</option><option value="dropbacks">PBP quarterback dropbacks</option></select></label>
        <label className="text-xs text-slate-400">Group rows<select className={inputClass} value={groupBy} onChange={e=>setGroupBy(e.target.value)} disabled={view === "chart" && chartType === "trend"}><option value="team">Season / range by team</option><option value="game">Each team game</option></select></label>
      </div>
    </section>
    {error ? <div role="alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-5 text-sm text-amber-200">FTN charting could not load: {error}</div> : busy ? <div role="status" className="rounded-xl border border-slate-800 bg-slate-900 p-8 text-center text-sm text-slate-300">Matching FTN charted plays to play-by-play…</div> : pbpState.error ? <div role="alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-5 text-sm text-amber-200">Play-by-play could not load. It is needed to identify the offense on each FTN play.<div className="mt-2 break-words text-xs">{pbpState.error}</div><button className={`${buttonClass} mt-3`} onClick={()=>setRetry(r=>r+1)}>Retry play-by-play</button></div> : !enriched.length ? <div className="rounded-xl border border-slate-800 p-8 text-center text-sm text-slate-400">No FTN charting available for {season}.</div> : !matched.length && situation && enriched.some(r => r._team) ? <p className="p-8 text-center text-sm text-slate-400">No charted plays match the situation filters.</p> : !matched.length ? <div role="alert" className="rounded-xl border border-amber-500/40 p-6 text-sm text-amber-200">None of the {enriched.length.toLocaleString()} FTN plays matched a PBP game ID and play ID with an offensive team. Check that both files cover the same season and games.</div> : <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">{cardKeys.map(key=>{
        const value=summary[key], baseline=league[key], diff=value!=null && baseline!=null ? (value-baseline)*100 : null;
        return <div key={key} className="rounded-2xl border border-slate-800 bg-slate-900/95 p-4"><div className="text-[11px] font-semibold text-slate-400">{label(key)}</div><div className="mt-2 text-2xl font-black tabular-nums text-white">{ftnFormat(value,key)}</div><div className="mt-2 text-[11px] text-slate-400">{team === "ALL" ? "Selected charted sample" : diff == null ? "No comparison available" : `${diff>0?"+":""}${diff.toFixed(1)} pp vs sample average`}</div></div>;
      })}</div>
      <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/95">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 p-4"><div><h3 className="text-lg font-bold">{view === "table" ? "Tendency rankings" : chartType === "trend" ? "Weekly trends" : "Tendency explorer"}</h3><p className="mt-1 text-xs text-slate-400">{view === "chart" && chartType === "trend" ? "One series per team · missing weeks remain gaps" : groupBy === "team" ? "One row per team in the selected range" : "One row per offense per game"}</p></div><div className="flex rounded-full border border-slate-700 bg-slate-900 p-1">{["table","chart"].map(v=><button key={v} onClick={()=>setView(v)} className={`rounded-full px-4 py-2 text-xs font-bold ${view===v?"bg-sky-400 text-slate-950":"text-slate-300"}`}>{v === "table" ? "Table" : "Chart"}</button>)}</div></div>
        <div className="flex flex-wrap items-end gap-3 border-b border-slate-800 p-4">
          <label className="min-w-[180px] flex-1 text-xs text-slate-400">Search {view === "chart" && chartType === "trend" ? "teams" : "teams / games"}<input className={inputClass} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Team name, abbreviation…"/></label>
          <label className="text-xs text-slate-400">Minimum charted plays<input className={`${inputClass} !w-36`} type="number" min="0" step="1" value={minPlays} onChange={e=>setMinPlays(Math.max(0,Math.floor(Number(e.target.value)||0)))}/></label>
          {view === "table" && <details className="relative"><summary className={`${buttonClass} cursor-pointer list-none`}>Columns ({columns.length})</summary><div className="absolute right-0 z-40 mt-2 w-72 max-w-[80vw] rounded-xl border border-slate-700 bg-slate-950 p-3 shadow-xl"><div className="mb-3 flex gap-2"><button className={buttonClass} onClick={()=>setColumns(FTN_METRICS.map(([k])=>k))}>All</button><button className={buttonClass} onClick={()=>setColumns(FTN_DEFAULT_COLUMNS)}>Reset</button></div><div className="grid max-h-72 gap-2 overflow-auto">{FTN_METRICS.map(([k,name])=><label key={k} className="flex items-center gap-2 text-xs"><input type="checkbox" className="accent-sky-400" checked={columns.includes(k)} onChange={()=>setColumns(previous=>previous.includes(k)?previous.filter(c=>c!==k):FTN_METRICS.map(([key])=>key).filter(key=>key===k||previous.includes(key)))}/>{name}</label>)}</div></div></details>}
          <span className="text-xs text-slate-400">{view === "chart" ? chartType === "trend" ? `${trend.teams.length} team series` : `${comparisonRows.length} of ${eligible.length} points` : `${tableRows.length} rows`}</span>
        </div>
        {view === "table" ? <>
          <div className="max-h-[68vh] overflow-auto"><table className="w-full min-w-max border-collapse text-xs tabular-nums"><thead className="sticky top-0 z-20 bg-slate-900"><tr>{allColumns.map(([key,title],i)=><th key={key} scope="col" aria-sort={sort.key===key?(sort.dir==="asc"?"ascending":"descending"):"none"} className={`whitespace-nowrap border-b border-slate-700 bg-slate-900 ${i===0?"sticky left-0 z-30 text-left":"text-right"}`}><button type="button" onClick={()=>toggleSort(key)} className="w-full px-4 py-3 text-inherit hover:bg-slate-800 focus-visible:outline focus-visible:outline-sky-400" title={`Sort by ${title}`}>{title}<span className={`ml-2 ${sort.key===key?"text-sky-300":"text-slate-600"}`}>{sort.key===key?(sort.dir==="asc"?"↑":"↓"):"↕"}</span></button></th>)}</tr></thead><tbody>{tableRows.map((r,i)=><tr key={r.id} className={`group ${i%2?"bg-slate-900/70":"bg-slate-950"} hover:bg-slate-800/60`}>{allColumns.map(([key],j)=><td key={key} className={`whitespace-nowrap border-b border-slate-800/60 px-4 py-3 ${j===0?`sticky left-0 z-10 font-bold ${i%2?"bg-slate-900":"bg-slate-950"} group-hover:bg-slate-800`:"text-right"}`}>{key === "team" ? <span className="inline-flex items-center gap-2" title={TEAM_NAMES[r.team]}><img src={nflLogoUrl(r.team)} alt="" className="h-6 w-6 object-contain" onError={e=>{e.currentTarget.style.visibility="hidden";}}/>{r.team}</span> : key === "game" ? r.game.replace(/_/g," · ") : key === "week" ? r.week : ftnFormat(r[key],key)}</td>)}</tr>)}</tbody></table></div>
          {!tableRows.length && <div className="p-12 text-center text-sm text-slate-400">No rows match the filters. Reduce minimum plays or reset filters.</div>}
          <div className="border-t border-slate-800 px-4 py-3 text-xs text-slate-400">Click any header to sort; click again to reverse. Export follows the visible columns and sorted rows.</div>
        </> : <>
          <div className="grid gap-3 border-b border-slate-800 p-4 sm:grid-cols-2 xl:grid-cols-4">
            <label className="text-xs text-slate-400">Chart type<select className={inputClass} value={chartType} onChange={e=>setChartType(e.target.value)}><option value="scatter">Scatter · metric vs metric</option><option value="bar">Bar · ranked comparison</option><option value="trend">Line · weekly trends</option></select></label>
            {chartType === "scatter" && metricSelect("X axis",xMetric,setXMetric)}
            {metricSelect(chartType === "scatter" ? "Y axis / rank by" : "Metric",yMetric,setYMetric)}
            <label className="text-xs text-slate-400">{chartType === "trend"?"Team series (ranked over range)":"Show (ranked by metric)"}<select className={inputClass} value={chartLimit} onChange={e=>setChartLimit(e.target.value)}>{CHART_LIMIT_OPTIONS.map(([value,name])=><option key={value} value={value}>{name}</option>)}</select></label>
          </div>
          <div className="flex flex-wrap items-center gap-4 px-4 py-3">
            {chartType === "scatter" && <><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={showLabels} onChange={e=>setShowLabels(e.target.checked)} className="accent-sky-400"/>Team labels</label><label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={showMeans} onChange={e=>setShowMeans(e.target.checked)} className="accent-sky-400"/>Mean lines</label><label className="flex items-center gap-2 text-xs">Diagonal<select className="rounded border border-slate-700 bg-slate-900 px-2 py-1" value={diagonal} onChange={e=>setDiagonal(e.target.value)}><option value="off">Off</option><option value="yx">y = x</option><option value="means">Through means</option></select></label></>}
            <label className="flex items-center gap-2 text-xs">Axis color<input type="color" value={axisColor} onChange={e=>setAxisColor(e.target.value)} className="h-7 w-8 rounded border border-slate-700 bg-slate-900"/></label>
            {chartType === "bar" && <label className="flex items-center gap-2 text-xs">Bar color<input type="color" value={barColor} onChange={e=>setBarColor(e.target.value)} className="h-7 w-8 rounded border border-slate-700 bg-slate-900"/></label>}
            <button className={`${buttonClass} ml-auto`} onClick={resetChart}>Reset chart</button>
          </div>
          {chartEmpty ? <div className="p-12 text-center text-sm text-slate-400">No chart data matches these filters.</div> : chartType === "scatter" ? <>
            <div className="px-4 text-xs text-slate-400">Axes fit displayed points. Dashed lines show unweighted means of displayed points. Top selections rank by Y, descending.</div>
            <div className="mx-auto w-full max-w-[1120px] lg:w-[85%] aspect-[49/27] min-h-[320px] rounded-xl bg-slate-900 p-2"><ResponsiveContainer width="100%" height="100%"><ScatterChart margin={{top:40,right:42,bottom:60,left:28}}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="4 4"/>
              <XAxis type="number" dataKey={xMetric} domain={xDomain} allowDataOverflow tick={{fill:"#cbd5e1",fontSize:11}} tickFormatter={v=>ftnFormat(v,xMetric)} axisLine={{stroke:axisColor}} tickLine={{stroke:axisColor}} label={{value:label(xMetric),position:"bottom",offset:14,fill:"#94a3b8"}}/>
              <YAxis type="number" dataKey={yMetric} domain={yDomain} allowDataOverflow width={78} tick={{fill:"#cbd5e1",fontSize:11}} tickFormatter={v=>ftnFormat(v,yMetric)} axisLine={{stroke:axisColor}} tickLine={{stroke:axisColor}} label={{value:label(yMetric),angle:-90,position:"left",offset:6,fill:"#94a3b8"}}/>
              <Tooltip content={chartTooltip} wrapperStyle={{zIndex:1000,outline:"none"}}/>
              {showMeans && xMean!=null && <ReferenceLine x={xMean} stroke="#94a3b8" strokeDasharray="5 5"/>}{showMeans && yMean!=null && <ReferenceLine y={yMean} stroke="#94a3b8" strokeDasharray="5 5"/>}
              {segment && <ReferenceLine segment={segment} stroke="#38bdf8" strokeDasharray="6 6"/>}
              <Scatter data={comparisonRows} shape={<PlayerLogoPoint showLabel={showLabels}/>}/>
            </ScatterChart></ResponsiveContainer></div>
          </> : chartType === "bar" ? <div className="mx-auto w-full max-w-[1120px] lg:w-[85%] max-h-[76vh] overflow-y-auto rounded-xl bg-slate-900 p-3"><div style={{height:Math.max(400,comparisonRows.length*36+70)}}><ResponsiveContainer width="100%" height="100%"><BarChart layout="vertical" data={comparisonRows} margin={{top:12,right:24,left:4,bottom:30}}><CartesianGrid stroke="#1e293b" strokeDasharray="4 4" horizontal={false}/><XAxis type="number" domain={paddedChartDomain(comparisonRows,yMetric,true)} tickFormatter={v=>ftnFormat(v,yMetric)} tick={{fill:"#cbd5e1",fontSize:11}} axisLine={{stroke:axisColor}} label={{value:label(yMetric),position:"bottom",offset:6,fill:"#94a3b8"}}/><YAxis type="category" dataKey="_label" width={groupBy==="game"?96:54} interval={0} tick={{fill:"#cbd5e1",fontSize:11}} axisLine={{stroke:axisColor}}/><Tooltip content={chartTooltip} cursor={{fill:"#1e293b"}}/><Bar dataKey={yMetric} name={label(yMetric)} fill={barColor} radius={[0,4,4,0]} maxBarSize={22}/></BarChart></ResponsiveContainer></div></div> : <>
            <div className="px-4 text-xs text-slate-400">Minimum plays and series ranking use each team's full selected range. Hover for weekly values. Click a team in the filters to isolate its trend.</div>
            <div className="mx-auto w-full max-w-[1120px] lg:w-[85%] aspect-[49/27] min-h-[320px] rounded-xl bg-slate-900 p-3"><ResponsiveContainer width="100%" height="100%"><LineChart data={trend.points} margin={{top:24,right:24,left:8,bottom:28}}><CartesianGrid stroke="#1e293b" strokeDasharray="4 4"/><XAxis type="number" dataKey="week" domain={['dataMin','dataMax']} ticks={trend.points.map(p=>p.week)} tickFormatter={w=>`W${w}`} tick={{fill:"#cbd5e1",fontSize:11}} axisLine={{stroke:axisColor}}/><YAxis tickFormatter={v=>ftnFormat(v,yMetric)} tick={{fill:"#cbd5e1",fontSize:11}} width={70} axisLine={{stroke:axisColor}}/><Tooltip content={chartTooltip}/>{trend.teams.map(t=><Line key={t} dataKey={t} name={t} type="linear" stroke={colorForTeam(t)==="#000000"?"#a1a1aa":colorForTeam(t)} strokeWidth={2.5} dot={{r:3}} activeDot={{r:5}} connectNulls={false} isAnimationActive={false}/>)}</LineChart></ResponsiveContainer></div>
            <div className="flex max-h-28 flex-wrap gap-x-4 gap-y-2 overflow-auto px-5 pb-4">{trend.teams.map(t=><span key={t} className="inline-flex items-center gap-1.5 text-xs"><img src={nflLogoUrl(t)} alt="" className="h-5 w-5 object-contain"/><span style={{color:colorForTeam(t)==="#000000"?"#a1a1aa":colorForTeam(t)}}>●</span>{t}</span>)}</div>
          </>}
        </>}
      </section>
    </>}
  </div>;
}

/** Game-clock pace: consecutive eligible snaps, never across a quarter or drive. */
function paceNumber(value) {
  return value == null || String(value).trim() === "" || !Number.isFinite(Number(value)) ? null : Number(value);
}
function paceScrimmage(p) {
  return (p.play_type === "pass" || p.play_type === "run") &&
    Number(p.qb_kneel) !== 1 && Number(p.qb_spike) !== 1 && Number(p.two_point_attempt) !== 1;
}
function paceNeutral(p) {
  const q = paceNumber(p.qtr), clock = paceNumber(p.quarter_seconds_remaining);
  const score = paceNumber(p.score_differential);
  return q >= 1 && q <= 4 && clock != null && score != null && Math.abs(score) <= 7 &&
    !((q === 2 || q === 4) && clock <= 120);
}
const DEFAULT_SITUATION = {
  period: "all", clock: "all", score: "all", margin: "14",
  down: "all", distance: "all", field: "all",
};
const SITUATION_FILTERS = [
  ["period", "Period", [["all", "Any period"], ["first", "First half"], ["second", "Second half"],
    ["1", "1st quarter"], ["2", "2nd quarter"], ["3", "3rd quarter"], ["4", "4th quarter"], ["ot", "Overtime"]]],
  ["clock", "Clock", [["all", "Any time"], ["half", "Last 2 min of either half"], ["game", "Last 2 min of regulation"]]],
  ["score", "Score state", [["all", "Any score"], ["neutral", "Neutral"], ["tied", "Tied"],
    ["trailing", "Trailing by at least"], ["trailingExact", "Trailing by exactly"],
    ["leading", "Leading by at least"], ["leadingExact", "Leading by exactly"]]],
  ["down", "Down", [["all", "Any down"], ["1", "1st"], ["2", "2nd"], ["3", "3rd"], ["4", "4th"]]],
  ["distance", "Yards to go", [["all", "Any distance"], ["short", "1–3 yards"], ["medium", "4–6 yards"], ["long", "7+ yards"]]],
  ["field", "Field position", [["all", "Anywhere"], ["redZone", "Red zone (opp. 20 or closer)"],
    ["opp", "Opposing territory"], ["own", "Own territory"], ["goal", "Goal to go"]]],
];

function matchesSituation(play, situation) {
  if (!situation) return true;
  const { period, clock, score, margin, down, distance, field } = situation;
  const quarter = paceNumber(play.qtr);
  if (period === "first" && !(quarter === 1 || quarter === 2)) return false;
  if (period === "second" && !(quarter === 3 || quarter === 4)) return false;
  if (period === "ot" && !(quarter > 4)) return false;
  if (period !== "all" && /^[1-4]$/.test(period) && quarter !== Number(period)) return false;

  if (clock !== "all") {
    const remaining = paceNumber(play.quarter_seconds_remaining);
    if (remaining == null || remaining < 0 || remaining > 120) return false;
    if (quarter !== 4 && !(clock === "half" && quarter === 2)) return false;
  }

  if (score === "neutral" && !paceNeutral(play)) return false;
  if (score === "tied" && paceNumber(play.score_differential) !== 0) return false;
  if (score.startsWith("trailing") || score.startsWith("leading")) {
    const differential = paceNumber(play.score_differential);
    if (differential == null) return false;
    const points = score.startsWith("trailing") ? -differential : differential;
    const threshold = Math.max(1, Number(margin) || 1);
    if (score.endsWith("Exact") ? points !== threshold : points < threshold) return false;
  }

  if (down !== "all" && paceNumber(play.down) !== Number(down)) return false;
  if (distance !== "all") {
    const yards = paceNumber(play.ydstogo);
    if (yards == null || yards <= 0) return false;
    const band = yards <= 3 ? "short" : yards <= 6 ? "medium" : "long";
    if (distance !== band) return false;
  }
  if (field === "goal" && Number(play.goal_to_go) !== 1) return false;
  if (field !== "all" && field !== "goal") {
    const yardline = paceNumber(play.yardline_100);
    if (yardline == null) return false;
    if (field === "redZone" && yardline > 20) return false;
    if (field === "opp" && yardline >= 50) return false;
    if (field === "own" && yardline <= 50) return false;
  }
  return true;
}

function SituationFilters({ value, onChange, active }) {
  const control = "mt-1 block rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white";
  const set = (key, next) => onChange(previous => ({ ...previous, [key]: next }));
  const scoreMargin = value.score.startsWith("leading") || value.score.startsWith("trailing");
  return (
    <details open={active || undefined} className="rounded-2xl border border-slate-800 bg-slate-900/95 px-4 py-3">
      <summary className="cursor-pointer text-sm font-bold text-slate-100">
        Situation filters <span className={`ml-2 text-xs ${active ? "text-sky-300" : "text-slate-400"}`}>{active ? "Active" : "All plays"}</span>
      </summary>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        {SITUATION_FILTERS.map(([key, label, options]) => (
          <React.Fragment key={key}>
            <label className="text-xs text-slate-400">{label}
              <select className={control} value={value[key]} onChange={event => set(key, event.target.value)}>
                {options.map(([id, text]) => <option key={id} value={id}>{text}</option>)}
              </select>
            </label>
            {key === "score" && scoreMargin && <label className="text-xs text-slate-400">Points
              <input type="number" min="1" step="1" className={`${control} w-20`} value={value.margin}
                onChange={event => set("margin", event.target.value)} />
            </label>}
          </React.Fragment>
        ))}
        <button type="button" className={`${control} disabled:opacity-40`} disabled={!active}
          onClick={() => onChange(DEFAULT_SITUATION)}>Reset to all plays</button>
      </div>
      <p className="mt-3 text-xs text-slate-400">
        Filters combine and update as you change them. Score and clock are measured before the play, from the offense’s perspective.
        Neutral means within 7 points in regulation, outside the final two minutes of each half.
      </p>
    </details>
  );
}

function prepareTeamPacePlays(rows, situation) {
  const games = new Map();
  for (const p of rows || []) {
    if (!p.game_id || (p.season_type && p.season_type !== "REG")) continue;
    if (!games.has(p.game_id)) games.set(p.game_id, []);
    games.get(p.game_id).push(p);
  }
  const out = [];
  for (const game of games.values()) {
    // Do not mutate cached source rows. IDs order inserted penalty/administrative records too.
    const ordered = [...game].sort((a, b) => Number(a.play_id) - Number(b.play_id));
    const seen = new Set();
    let previous = null;
    for (const p of ordered) {
      if (p.play_id != null) {
        const id = String(p.play_id);
        if (seen.has(id)) continue;
        seen.add(id);
      }
      if (!paceScrimmage(p) || !p.posteam || !p.defteam || !matchesSituation(p, situation)) {
        previous = null;
        continue;
      }
      const clock = paceNumber(p.game_seconds_remaining), prevClock = paceNumber(previous?.game_seconds_remaining);
      const q = paceNumber(p.qtr);
      const seconds = clock != null && prevClock != null ? prevClock - clock : null;
      const valid = previous && p.drive != null && previous.drive != null &&
        String(p.drive) === String(previous.drive) && q >= 1 && q <= 4 && q === paceNumber(previous.qtr) &&
        normalizeTeamAbbr(p.posteam) === normalizeTeamAbbr(previous.posteam) &&
        normalizeTeamAbbr(p.defteam) === normalizeTeamAbbr(previous.defteam) &&
        Number(previous.touchdown) !== 1 && Number(previous.interception) !== 1 &&
        Number(previous.fumble_lost) !== 1 && seconds > 0 && seconds <= 60;
      const score = paceNumber(p.score_differential), prevScore = paceNumber(previous?.score_differential);
      out.push({ ...p,
        _explosive: Number(p.yards_gained) >= (p.play_type === "pass" ? 20 : 10) ? 1 : 0,
        _paceSeconds: valid ? seconds : null,
        _neutralPaceSeconds: valid && paceNeutral(p) && paceNeutral(previous) ? seconds : null,
        _leadingPaceSeconds: valid && score > 0 && prevScore > 0 ? seconds : null,
        _trailingPaceSeconds: valid && score < 0 && prevScore < 0 ? seconds : null,
      });
      previous = p;
    }
  }
  return out;
}
function summarizeTeamPace(plays) {
  const values = key => plays.map(p => p[key]).filter(v => typeof v === "number" && Number.isFinite(v));
  const all = values("_paceSeconds"), neutral = values("_neutralPaceSeconds");
  const mean = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
  const huddle = plays.filter(p => paceNumber(p.no_huddle) != null);
  return {
    secondsPerPlay: mean(all), neutralSecondsPerPlay: mean(neutral),
    paceSamples: all.length, neutralPaceSamples: neutral.length,
    leadingSecondsPerPlay: mean(values("_leadingPaceSeconds")),
    trailingSecondsPerPlay: mean(values("_trailingPaceSeconds")),
    noHuddleRate: huddle.length ? huddle.filter(p => Number(p.no_huddle) === 1).length / huddle.length : null,
  };
}
const PACE_COLUMNS = [
  ["secondsPerPlay", "Sec/Play", "decimal"],
  ["neutralSecondsPerPlay", "Neutral Sec/Play", "decimal"],
  ["paceSamples", "Pace Samples", "int"], ["neutralPaceSamples", "Neutral Samples", "int"],
  ["noHuddleRate", "No Huddle %", "percent"],
];
const PACE_METHOD = "Sec/Play is elapsed game-clock time between consecutive pass/run snaps in the same drive and quarter (regulation only). Lower = faster. This includes play duration, not real-world time between snaps. Special teams, kneels, spikes, two-point attempts, penalty/administrative interruptions, missing clocks, and intervals outside 1–60 seconds break the sample. Neutral requires both snaps within 7 points, excluding the final 2 minutes of either half. Averages weight valid intervals, not games; sample counts show coverage. Leading/trailing require both snaps in that score state. Plays/G and no-huddle rates cover all eligible scrimmage plays, including overtime.";

function buildTeamPaceRows(items, grouping) {
    const groups = new Map();
    for (const p of items) {
      const t = normalizeTeamAbbr(p.posteam);
      const key = grouping === "game" ? `${p.game_id}__${t}` : t;
      if (!groups.has(key)) groups.set(key, { id: key, team: t, game: p.game_id, week: Number(p.week), items: [] });
      groups.get(key).items.push(p);
    }
    return [...groups.values()].map(g => ({ id: g.id, team: g.team, game: g.game, week: g.week,
      ...summarizeTeamPlays(g.items, new Set(g.items.map(p => p.game_id))),
    }));
  }

function TeamPaceView({ rows, season, loading, error, situation }) {
  const [team, setTeam] = useState("ALL");
  const [startWeek, setStartWeek] = useState(1);
  const [endWeek, setEndWeek] = useState(22);
  const [groupBy, setGroupBy] = useState("team");
  const [view, setView] = useState("table");
  const [chartType, setChartType] = useState("bar");
  const [metric, setMetric] = useState("neutralSecondsPerPlay");
  const [minSamples, setMinSamples] = useState(0);
  const [sort, setSort] = useState({ key: "neutralSecondsPerPlay", dir: "asc" });
  const plays = useMemo(() => prepareTeamPacePlays(rows, situation), [rows, situation]);
  const teams = useMemo(() => [...new Set(plays.map(p => normalizeTeamAbbr(p.posteam)))].sort(), [plays]);
  const weeks = useMemo(() => [...new Set(plays.map(p => Number(p.week)).filter(Number.isFinite))].sort((a,b) => a-b), [plays]);
  const selected = useMemo(() => plays.filter(p => Number(p.week) >= startWeek && Number(p.week) <= endWeek &&
    (team === "ALL" || normalizeTeamAbbr(p.posteam) === team)), [plays, team, startWeek, endWeek]);
  const columns = [
    ["games", "Games", "int"], ["plays", "Plays", "int"], ["playsPerGame", "Plays/G", "decimal"],
    ...PACE_COLUMNS,
    ["leadingSecondsPerPlay", "Leading Sec/Play", "decimal"],
    ["trailingSecondsPerPlay", "Trailing Sec/Play", "decimal"],
    ["passRate", "Pass %", "percent"],
  ];
  const fmtPace = (v, key) => {
    if (v == null || !Number.isFinite(Number(v))) return "—";
    const type = columns.find(c => c[0] === key)?.[2];
    return type === "percent" ? `${(v * 100).toFixed(1)}%` : type === "int" ? Number(v).toLocaleString() : Number(v).toFixed(1);
  };
  const metricLabel = columns.find(c => c[0] === metric)?.[1] || metric;
  const sampleKey = metric === "neutralSecondsPerPlay" ? "neutralPaceSamples" : "paceSamples";
  const grouped = useMemo(() => buildTeamPaceRows(selected, groupBy), [selected, groupBy]);
  const tableRows = useMemo(() => [...grouped].filter(r => r[sampleKey] >= minSamples).sort((a,b) => {
    const av = a[sort.key], bv = b[sort.key], sign = sort.dir === "asc" ? 1 : -1;
    if (av == null) return bv == null ? a.team.localeCompare(b.team) : 1;
    if (bv == null) return -1;
    return (typeof av === "number" ? (av-bv)*sign : String(av).localeCompare(String(bv))*sign) || a.team.localeCompare(b.team);
  }), [grouped, sampleKey, minSamples, sort]);
  const chartRows = useMemo(() => [...tableRows].filter(r => r[metric] != null).sort((a,b) => a[metric]-b[metric])
    .map(r => ({ ...r, label: groupBy === "game" ? `${r.team} W${r.week}` : r.team })), [tableRows, metric, groupBy]);
  const totals = useMemo(() => summarizeTeamPlays(selected, new Set(selected.map(p => `${p.game_id}__${normalizeTeamAbbr(p.posteam)}`))), [selected]);
  // Show up to eight fastest teams for a readable league view; the team filter isolates any team.
  const trend = useMemo(() => {
    const ranked = buildTeamPaceRows(selected, "team").filter(r => r[sampleKey] >= minSamples && r[metric] != null)
      .sort((a,b) => a[metric]-b[metric]).slice(0,8).map(r => r.team);
    const weekly = buildTeamPaceRows(selected, "game");
    const points = weeks.filter(w => w >= startWeek && w <= endWeek).map(w => {
      const point = { week: w };
      for (const t of ranked) {
        const r = weekly.find(r => r.team === t && r.week === w);
        point[t] = r && r[sampleKey] >= minSamples ? r[metric] : null;
      }
      return point;
    });
    return { teams: ranked, points };
  }, [selected, weeks, startWeek, endWeek, metric, sampleKey, minSamples]);
  const control = "rounded-xl border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white";
  const toggleSort = key => setSort(s => ({ key, dir: s.key === key ? (s.dir === "asc" ? "desc" : "asc") : "asc" }));
  const teamColor = t => ["#000000", "#002244", "#003C7D", "#03202F", "#0B162A"].includes(colorForTeam(t)) ? "#94a3b8" : colorForTeam(t);
  const tooltip = ({ active, payload }) => {
    const r = active && payload?.[0]?.payload;
    if (!r) return null;
    return <div className="rounded-xl border border-slate-600 bg-slate-950 p-3 text-xs text-white shadow-xl"><b>{r.team} {groupBy === "game" ? `· Week ${r.week}` : ""}</b><div className="mt-1">{metricLabel}: {fmtPace(r[metric], metric)}s</div><div className="mt-1 text-slate-400">{r[sampleKey]} intervals · {r.plays} plays · {r.games} games</div></div>;
  };
  return <div className="min-w-0 space-y-5 text-slate-100">
    <section className="rounded-3xl border border-slate-800 bg-slate-900/95 p-5 shadow-xl">
      <div className="flex flex-wrap items-start justify-between gap-3"><div><div className="text-xs font-extrabold tracking-[0.22em] text-sky-400">TEAM TEMPO</div><h2 className="mt-1 text-2xl font-black">Pace</h2><p className="mt-1 text-xs text-slate-400">{season} · Regular season · Lower seconds per play = faster</p></div>
        <button disabled={!tableRows.length || loading || !!error} className={`${control} disabled:opacity-40`} onClick={() => exportToCsv(`nfl_pace_${season}_weeks_${startWeek}-${endWeek}.csv`, tableRows, ["team", ...(groupBy === "game" ? ["game","week"] : []), ...columns.map(c=>c[0])])}>Export CSV</button></div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-400">Team<select className={`${control} mt-1 block`} value={team} onChange={e=>setTeam(e.target.value)}><option value="ALL">All teams</option>{team !== "ALL" && !teams.includes(team) && <option value={team}>{team}</option>}{teams.map(t=><option key={t}>{t}</option>)}</select></label>
        <label className="text-xs text-slate-400">From week<select className={`${control} mt-1 block`} value={startWeek} onChange={e=>{ const w=Number(e.target.value); setStartWeek(w); setEndWeek(v=>Math.max(v,w)); }}>{!weeks.includes(startWeek) && <option value={startWeek}>Week {startWeek}</option>}{weeks.map(w=><option key={w} value={w}>Week {w}</option>)}</select></label>
        <label className="text-xs text-slate-400">Through week<select className={`${control} mt-1 block`} value={endWeek} onChange={e=>{ const w=Number(e.target.value); setEndWeek(w); setStartWeek(v=>Math.min(v,w)); }}><option value={22}>Latest available</option>{endWeek !== 22 && !weeks.includes(endWeek) && <option value={endWeek}>Week {endWeek}</option>}{weeks.map(w=><option key={w} value={w}>Week {w}</option>)}</select></label>
        <label className="text-xs text-slate-400">Group<select className={`${control} mt-1 block`} value={groupBy} onChange={e=>setGroupBy(e.target.value)}><option value="team">By team</option><option value="game">By game</option></select></label>
        <label className="text-xs text-slate-400">Pace metric<select className={`${control} mt-1 block`} value={metric} onChange={e=>setMetric(e.target.value)}><option value="neutralSecondsPerPlay">Neutral Sec/Play</option><option value="secondsPerPlay">Overall Sec/Play</option></select></label>
        <label className="text-xs text-slate-400">Minimum {metric === "neutralSecondsPerPlay" ? "neutral " : ""}samples<input className={`${control} mt-1 block w-28`} type="number" min="0" step="1" value={minSamples} onChange={e=>setMinSamples(Math.max(0,Math.floor(Number(e.target.value)||0)))}/></label>
      </div>
    </section>
    {loading ? <p className="p-6 text-slate-400">Loading pace…</p> : error ? <p role="alert" className="rounded-xl border border-amber-500/40 p-5 text-amber-200">Could not load play-by-play: {error}</p> : !plays.length ? <p className="p-6 text-slate-400">{situation && rows.length ? "No plays match the situation filters." : `No regular season play-by-play available for ${season}.`}</p> : <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{[["Overall Sec/Play","secondsPerPlay"],["Neutral Sec/Play","neutralSecondsPerPlay"],["Plays per game","playsPerGame"],["No Huddle %","noHuddleRate"]].map(([title,key])=><div key={key} className="rounded-2xl border border-slate-800 bg-slate-900/95 p-4"><div className="text-xs font-bold uppercase text-slate-400">{title}</div><div className="mt-2 text-3xl font-black">{fmtPace(totals[key],key)}</div><div className="mt-1 text-xs text-slate-500">{key === "neutralSecondsPerPlay" ? `${totals.neutralPaceSamples} intervals` : key === "secondsPerPlay" ? `${totals.paceSamples} intervals` : "Selected teams and weeks"}</div></div>)}</div>
      <section className="min-w-0 overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/95 p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div><h3 className="text-lg font-black">{view === "table" ? "Team pace comparison" : chartType === "bar" ? "Fastest to slowest" : "Weekly pace"}</h3><p className="text-xs text-slate-400">{view === "table" ? `${tableRows.length} rows · click headers to sort` : chartType === "bar" ? "Bars rank the selected pace metric, fastest first" : team === "ALL" ? "Up to 8 fastest teams over the selected range · select a team to isolate it" : `${team} weekly trend`}</p></div><div className="flex flex-wrap gap-2">{["table","chart"].map(v=><button className={`${control} ${view===v?"border-sky-400 text-sky-300":""}`} key={v} onClick={()=>setView(v)}>{v === "table" ? "Table" : "Chart"}</button>)}{view === "chart" && <select aria-label="Pace chart type" className={control} value={chartType} onChange={e=>setChartType(e.target.value)}><option value="bar">Rankings</option><option value="trend">Weekly trends</option></select>}</div></div>
        {view === "table" ? <div className="max-h-[65vh] overflow-auto rounded-2xl border border-slate-800"><table className="w-full min-w-max text-xs tabular-nums"><thead className="sticky top-0 z-20 bg-slate-900"><tr>{[["team","Team"],...(groupBy === "game" ? [["game","Game"],["week","Week"]] : []),...columns].map(([key,title],i)=><th key={key} aria-sort={sort.key===key?(sort.dir==="asc"?"ascending":"descending"):"none"} className={`whitespace-nowrap bg-slate-900 ${i===0?"sticky left-0 z-30 text-left":"text-right"}`}><button className="px-4 py-3" onClick={()=>toggleSort(key)}>{title} {sort.key===key?(sort.dir==="asc"?"↑":"↓"):"↕"}</button></th>)}</tr></thead><tbody>{tableRows.map((r,i)=><tr key={r.id} className={i%2?"bg-slate-900":"bg-slate-950"}><td className={`sticky left-0 px-4 py-3 font-bold ${i%2?"bg-slate-900":"bg-slate-950"}`}><span className="inline-flex items-center gap-2"><img src={nflLogoUrl(r.team)} alt="" className="h-6 w-6 object-contain"/>{r.team}</span></td>{groupBy === "game" && <><td className="px-4 py-3">{r.game}</td><td className="px-4 py-3 text-right">{r.week}</td></>}{columns.map(([key])=><td key={key} className="px-4 py-3 text-right">{fmtPace(r[key],key)}</td>)}</tr>)}</tbody></table>{!tableRows.length && <p className="p-8 text-center text-slate-400">No teams match these filters.</p>}</div> : chartType === "bar" ? chartRows.length ? <div className="max-h-[72vh] overflow-y-auto rounded-xl bg-slate-900"><div style={{height:Math.max(360,chartRows.length*33+60)}}><ResponsiveContainer width="100%" height="100%"><BarChart layout="vertical" data={chartRows} margin={{top:12,right:30,left:8,bottom:32}}><CartesianGrid stroke="#334155" strokeDasharray="4 4" horizontal={false}/><XAxis type="number" domain={[0,"auto"]} tick={{fill:"#cbd5e1",fontSize:11}} label={{value:`${metricLabel} (seconds)`,position:"bottom",fill:"#94a3b8"}}/><YAxis type="category" dataKey="label" width={80} interval={0} tick={{fill:"#cbd5e1",fontSize:11}}/><Tooltip content={tooltip} cursor={{fill:"#334155"}}/><Bar dataKey={metric} radius={[0,4,4,0]} maxBarSize={22}>{chartRows.map(r=><Cell key={r.id} fill={teamColor(r.team)}/>)}</Bar></BarChart></ResponsiveContainer></div></div> : <p className="p-8 text-center text-slate-400">No valid pace samples match these filters.</p> : trend.teams.length ? <>
          <div className="mx-auto h-[430px] w-full max-w-[1120px] rounded-xl bg-slate-900"><ResponsiveContainer width="100%" height="100%"><LineChart data={trend.points} margin={{top:20,right:25,left:12,bottom:30}}><CartesianGrid stroke="#334155" strokeDasharray="4 4"/><XAxis dataKey="week" type="number" domain={["dataMin","dataMax"]} ticks={trend.points.map(p=>p.week)} tickFormatter={w=>`W${w}`} tick={{fill:"#cbd5e1",fontSize:11}}/><YAxis unit="s" domain={["auto","auto"]} tick={{fill:"#cbd5e1",fontSize:11}}/><Tooltip labelFormatter={w=>`Week ${w}`} formatter={(v,name)=>[`${Number(v).toFixed(1)}s`,name]} contentStyle={{background:"#020617",borderColor:"#475569",color:"#fff"}}/>{trend.teams.map(t=><Line key={t} dataKey={t} stroke={teamColor(t)} strokeWidth={2.5} dot={{r:3}} connectNulls={false} isAnimationActive={false}/>)}</LineChart></ResponsiveContainer></div><div className="mt-3 flex flex-wrap gap-4">{trend.teams.map(t=><span key={t} className="inline-flex items-center gap-2 text-xs"><img src={nflLogoUrl(t)} alt="" className="h-5 w-5 object-contain"/><span style={{color:teamColor(t)}}>●</span>{t}</span>)}</div><p className="mt-3 text-xs text-slate-400">Lower = faster. Missing weeks remain gaps. Minimum samples applies to each weekly point and each team's selected range.</p>
        </> : <p className="p-8 text-center text-slate-400">No valid weekly pace samples match these filters.</p>}
      </section>
      <p className="text-xs leading-relaxed text-slate-400">{PACE_METHOD}</p>
      <p className="text-xs text-slate-500">{situation && "Games and plays per game count only games with a matching play. "}Summary cards cover all selected plays before the minimum-sample filter. CSV follows the table's columns, filters, and sorting.</p>
    </>}
  </div>;
}

/** Team stats use scrimmage plays from PBP; ratios always use their own eligible play counts. */
function summarizeTeamPlays(plays, games) {
  const n = plays.length;
  const count = (key) => plays.reduce((sum, p) => sum + (Number(p?.[key]) === 1 ? 1 : 0), 0);
  const sum = (key) => plays.reduce((total, p) => {
    const v = Number(p?.[key]);
    return total + (Number.isFinite(v) ? v : 0);
  }, 0);
  // Match the existing pass/run play classification; each EPA split uses
  // only plays in that category with a usable EPA value.
  const splitEpaPerPlay = (playType) => {
    const eligible = plays.filter((p) => p.play_type === playType &&
      p.epa != null && String(p.epa).trim() !== "" && Number.isFinite(Number(p.epa)));
    return eligible.length
      ? eligible.reduce((total, p) => total + Number(p.epa), 0) / eligible.length
      : null;
  };
  const third = plays.filter((p) => Number(p.down) === 3);
  const fourth = plays.filter((p) => Number(p.down) === 4);
  const rate = (a, b) => b ? a / b : null;
  const neutralPlays = plays.filter(paceNeutral);
  return {
    ...summarizeTeamPace(plays),
    games: games.size,
    plays: n,
    playsPerGame: rate(n, games.size),
    passRate: rate(plays.filter((p) => p.play_type === "pass").length, n),
    neutralPassRate: rate(neutralPlays.filter((p) => p.play_type === "pass").length, neutralPlays.length),
    runRate: rate(plays.filter((p) => p.play_type === "run").length, n),
    yards: sum("yards_gained"),
    passYards: sum("passing_yards"),
    rushYards: sum("rushing_yards"),
    passTds: count("pass_touchdown"),
    rushTds: count("rush_touchdown"),
    yardsPerPlay: rate(sum("yards_gained"), n),
    epaPerPlay: rate(sum("epa"), plays.filter((p) => Number.isFinite(Number(p.epa)) && p.epa != null).length),
    passEpaPerPlay: splitEpaPerPlay("pass"),
    rushEpaPerPlay: splitEpaPerPlay("run"),
    successRate: rate(count("success"), plays.filter((p) => p.success != null).length),
    thirdRate: rate(third.filter((p) => Number(p.third_down_converted) === 1).length, third.length),
    fourthRate: rate(fourth.filter((p) => Number(p.fourth_down_converted) === 1).length, fourth.length),
    sacks: count("sack"),
    turnovers: count("interception") + count("fumble_lost"),
    explosive: count("_explosive"),
    explosiveRate: rate(count("_explosive"), n),
    redZonePlays: plays.filter((p) => Number(p.yardline_100) <= 20 && p.yardline_100 != null).length,
  };
}

function buildCombinedTeamChartRows(plays, groupBy = "team", week = "ALL", team = "ALL", side = "offense") {
  const groups = new Map();
  for (const play of plays) {
    if (week !== "ALL" && Number(play.week) !== Number(week)) continue;
    for (const [scope, teamCode] of [["offense", play.posteam], ["defense", play.defteam]]) {
      const t = normalizeTeamAbbr(teamCode);
      if (!t || (team !== "ALL" && t !== team)) continue;
      const key = groupBy === "team" ? t : `${play.game_id}__${t}`;
      if (!groups.has(key)) groups.set(key, { team: t, game: play.game_id, week: Number(play.week), offense: [], defense: [] });
      groups.get(key)[scope].push(play);
    }
  }
  return [...groups.values()].map(group => {
    const row = { team: group.team, game: group.game, week: group.week };
    for (const scope of ["offense", "defense"]) {
      const scopePlays = group[scope];
      const stats = summarizeTeamPlays(scopePlays, new Set(scopePlays.map(play => play.game_id)));
      for (const [key, value] of Object.entries(stats)) row[`${scope}_${key}`] = scopePlays.length ? value : null;
      if (scope === side) Object.assign(row, stats);
    }
    return row;
  });
}

function teamChartAxisReversed(key, side) {
  const epaKeys = ["epaPerPlay", "passEpaPerPlay", "rushEpaPerPlay"];
  return epaKeys.some((metric) => key === `defense_${metric}` ||
    (key === metric && side === "defense"));
}

const SITUATION_PLAYER_METRICS = {
  passing: [["attempts", "Att"], ["completions", "Cmp"], ["completion_percentage", "Cmp %"],
    ["passing_yards", "Pass Yds"], ["passing_tds", "Pass TD"], ["passing_interceptions", "INT"],
    ["passing_epa", "Total EPA"], ["passing_epa_per_play", "EPA/Play"], ["passing_success_rate", "Success %"]],
  receiving: [["targets", "Tgt"], ["receptions", "Rec"], ["receiving_yards", "Rec Yds"], ["receiving_tds", "Rec TD"],
    ["receiving_air_yards", "Air Yds"], ["receiving_epa", "Total EPA"], ["receiving_epa_per_play", "EPA/Tgt"],
    ["receiving_success_rate", "Success %"]],
  rushing: [["carries", "Att"], ["rushing_yards", "Rush Yds"], ["rushing_tds", "Rush TD"], ["yards_per_carry", "Yds/Att"],
    ["rushing_epa", "Total EPA"], ["rushing_epa_per_play", "EPA/Att"], ["rushing_success_rate", "Success %"]],
};

function SituationPlayerView({ tab, category, pbp, lookup, situation, season, loading, error }) {
  const metrics = SITUATION_PLAYER_METRICS[category];
  const volumeKey = metrics[0][0];
  const [week, setWeek] = useState("all");
  const [team, setTeam] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: volumeKey, direction: -1 });
  const weekly = useMemo(() => {
    const plays = prepareTeamPacePlays(pbp, situation);
    return buildUnifiedPlayerRows([[], [], [], plays], category, lookup, true);
  }, [pbp, category, lookup, situation]);
  const totals = useMemo(() => addResearchSeasonTotals(weekly, tab).filter(row => row._syntheticSeasonTotal), [weekly, tab]);
  const weeks = [...new Set(weekly.map(row => row.week))].sort((a, b) => a - b);
  const teams = [...new Set(weekly.map(row => row.team))].sort();
  const filtered = (week === "all" ? totals : weekly.filter(row => row.week === Number(week)))
    .filter(row => (team === "all" || row.team === team) && row.full_name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null && bv != null) return 1;
      if (bv == null && av != null) return -1;
      const delta = typeof av === "number" && typeof bv === "number" ? av - bv : String(av ?? "").localeCompare(String(bv ?? ""));
      return delta * sort.direction || a.full_name.localeCompare(b.full_name);
    });
  const columns = [["full_name", "Player"], ["team", "Team"], ...metrics];
  const control = "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white";
  const cell = (row, key) => {
    const value = row[key];
    if (value == null) return "—";
    if (key === "completion_percentage") return `${value.toFixed(1)}%`;
    if (key.endsWith("_success_rate")) return `${(value * 100).toFixed(1)}%`;
    if (key.includes("epa")) return value.toFixed(2);
    if (key === "yards_per_carry") return value.toFixed(1);
    return typeof value === "number" ? value.toLocaleString() : value;
  };
  return (
    <section className="min-w-0 rounded-2xl border border-slate-800 bg-slate-900/95 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-black text-white">Situational {tab}</h2>
          <p className="text-xs text-slate-400">Regular season play-by-play · {season} · {filtered.length} players</p>
        </div>
        <button type="button" className={`${control} disabled:opacity-40`} disabled={!filtered.length || loading}
          onClick={() => exportToCsv(`nfl_${category}_situation_${season}_${week}.csv`, filtered, columns.map(([key]) => key))}>
          Export CSV
        </button>
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <input aria-label="Search players" className={control} value={search} placeholder="Search player…" onChange={event => setSearch(event.target.value)} />
        <select aria-label="Team" className={control} value={team} onChange={event => setTeam(event.target.value)}>
          <option value="all">All teams</option>
          {team !== "all" && !teams.includes(team) && <option value={team}>{team}</option>}
          {teams.map(value => <option key={value}>{value}</option>)}
        </select>
        <select aria-label="Week" className={control} value={week} onChange={event => setWeek(event.target.value)}>
          <option value="all">All available weeks · totals</option>
          {week !== "all" && !weeks.includes(Number(week)) && <option value={week}>Week {week}</option>}
          {weeks.map(value => <option key={value} value={value}>Week {value}</option>)}
        </select>
      </div>
      {loading ? <p className="p-4 text-sm text-slate-400">Loading play-by-play…</p> : error ?
        <p role="alert" className="p-4 text-sm text-amber-300">{error}</p> : !filtered.length ?
        <p className="p-4 text-sm text-slate-400">No player plays match these filters.</p> : (
          <div className="max-h-[65vh] overflow-auto rounded-xl border border-slate-800">
            <table className="w-full min-w-max text-center text-xs tabular-nums">
              <thead className="sticky top-0 bg-slate-900"><tr>{columns.map(([key, label]) => (
                <th key={key} scope="col" aria-sort={sort.key === key ? (sort.direction === 1 ? "ascending" : "descending") : "none"}
                  className={`px-3 py-3 ${key === "full_name" ? "text-left" : ""}`}>
                  <button type="button" onClick={() => setSort(previous => ({
                    key, direction: previous.key === key ? -previous.direction : (key === "full_name" || key === "team" ? 1 : -1),
                  }))}>{label}{sort.key === key ? (sort.direction === 1 ? " ↑" : " ↓") : ""}</button>
                </th>
              ))}</tr></thead>
              <tbody>{filtered.map((row, index) => (
                <tr key={`${row.full_name}__${row.team}`} className={index % 2 ? "bg-slate-900" : "bg-slate-950"}>
                  {columns.map(([key]) => <td key={key} className={`whitespace-nowrap px-3 py-3 ${key === "full_name" ? "text-left font-bold" : ""}`}>
                    {key === "team" ? <span className="inline-flex items-center gap-2"><img src={nflLogoUrl(row.team)} alt="" className="h-5 w-5 object-contain" />{row.team}</span> : cell(row, key)}
                  </td>)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      <p className="mt-3 text-xs text-slate-400">
        These stats use matching plays only. EPA and success rate use plays with recorded EPA.
        {category === "passing" && " Sacks count toward EPA and success rate. Pass attempts exclude sacks."}
      </p>
    </section>
  );
}

function TeamStatsView({ rows, season, loading, situation }) {
  const [side, setSide] = useState("offense");
  const [team, setTeam] = useState("ALL");
  const [week, setWeek] = useState("ALL");
  const [groupBy, setGroupBy] = useState("team");
  const [sort, setSort] = useState({ key: "epaPerPlay", dir: "desc" });
  const [view, setView] = useState("table");
  const [xMetric, setXMetric] = useState("offense_yardsPerPlay");
  const [yMetric, setYMetric] = useState("defense_epaPerPlay");
  const [minPlays, setMinPlays] = useState(0);
  const [chartLimit, setChartLimit] = useState("all");
  const [showTeamLabels, setShowTeamLabels] = useState(true);

  const plays = useMemo(() => prepareTeamPacePlays(rows, situation), [rows, situation]);
  const weeks = useMemo(() => [...new Set(plays.map((p) => Number(p.week)).filter(Number.isFinite))].sort((a, b) => a - b), [plays]);
  const teams = useMemo(() => [...new Set(plays.flatMap((p) => [normalizeTeamAbbr(p.posteam), normalizeTeamAbbr(p.defteam)]))].sort(), [plays]);
  const teamOf = (p) => normalizeTeamAbbr(side === "offense" ? p.posteam : p.defteam);
  const filtered = useMemo(() => plays.filter((p) =>
    (week === "ALL" || Number(p.week) === Number(week)) &&
    (team === "ALL" || teamOf(p) === team)
  ), [plays, week, team, side]);
  const games = useMemo(() => new Set(filtered.map((p) => `${p.game_id}__${teamOf(p)}`)), [filtered, side]);
  const summary = useMemo(() => summarizeTeamPlays(filtered, games), [filtered, games]);
  const tableRows = useMemo(() => {
    const groups = new Map();
    for (const p of filtered) {
      const t = teamOf(p);
      const key = groupBy === "team" ? t : `${p.game_id}__${t}`;
      if (!groups.has(key)) groups.set(key, { team: t, game: p.game_id, week: Number(p.week), plays: [], games: new Set() });
      const g = groups.get(key);
      g.plays.push(p);
      g.games.add(`${p.game_id}__${t}`);
    }
    const out = [...groups.values()].map((g) => ({
      team: g.team, game: g.game, week: g.week,
      ...summarizeTeamPlays(g.plays, g.games),
    }));
    const sign = sort.dir === "asc" ? 1 : -1;
    return out.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null && bv == null) return a.team.localeCompare(b.team);
      if (av == null) return 1;
      if (bv == null) return -1;
      return (typeof av === "number" ? (av - bv) * sign : String(av).localeCompare(String(bv)) * sign) || a.team.localeCompare(b.team);
    });
  }, [filtered, groupBy, sort, side]);
  const columns = [
    ["games", "Games", "int"], ["plays", "Plays", "int"], ["playsPerGame", "Plays/G", "decimal"],
    ...(side === "offense" ? PACE_COLUMNS.filter(([key]) => key === "secondsPerPlay" || key === "neutralSecondsPerPlay") : []),
    ["passRate", "Pass %", "percent"], ["neutralPassRate", "Neutral Pass %", "percent"], ["runRate", "Run %", "percent"], ["yards", "Net Yards", "int"], ["passYards", "Pass Yds", "int"], ["rushYards", "Rush Yds", "int"], ["passTds", "Pass TD", "int"], ["rushTds", "Rush TD", "int"], ["yardsPerPlay", "Yds/Play", "decimal"],
    ["epaPerPlay", "EPA/Play", "signed"],
    ["passEpaPerPlay", "Pass EPA/Play", "signed"], ["rushEpaPerPlay", "Rush EPA/Play", "signed"],
    ["successRate", "Success %", "percent"],
    ["thirdRate", "3rd Down %", "percent"], ["fourthRate", "4th Down %", "percent"],
    ["explosiveRate", "Explosive %", "percent"], ["sacks", "Sacks", "int"],
    ["turnovers", "Turnovers", "int"], ["redZonePlays", "RZ Plays", "int"],
  ];
  const display = (value, type) => value == null ? "—" : type === "percent" ? `${(value * 100).toFixed(1)}%` : type === "signed" ? value.toFixed(3) : type === "decimal" ? value.toFixed(1) : Number(value).toLocaleString();
  const chartColumns = [
    ...columns.map(([key, label, type]) => [key, `${side === "offense" ? "Offense" : "Defense"}: ${label} (selected view)`, type]),
    ...["offense", "defense"].flatMap(scope => columns.map(([key, label, type]) => [`${scope}_${key}`, `${scope === "offense" ? "Offense" : "Defense allowed"}: ${label}`, type])),
  ];
  const metric = (key) => chartColumns.find(([name]) => name === key);
  const combinedTeamRows = useMemo(() => buildCombinedTeamChartRows(plays, groupBy, week, team, side), [plays, groupBy, week, team, side]);
  const chartFormat = (value, key) => display(value, metric(key)?.[2]);
  const reverseX = teamChartAxisReversed(xMetric, side);
  const reverseY = teamChartAxisReversed(yMetric, side);
  const chartRows = useMemo(() => limitChartRows(combinedTeamRows.filter((r) =>
    r.plays >= minPlays && r[xMetric] != null && Number.isFinite(Number(r[xMetric])) &&
    r[yMetric] != null && Number.isFinite(Number(r[yMetric]))
  ).map(r => ({ ...r, _chartRankScore: reverseY ? -Number(r[yMetric]) : Number(r[yMetric]) })), chartLimit, "_chartRankScore").map((r) => ({ ...r, _team: r.team, _label: r.team, _logo: nflLogoUrl(r.team) })),
  [combinedTeamRows, minPlays, xMetric, yMetric, chartLimit, reverseY]);
  const teamXDomain = useMemo(() => paddedChartDomain(chartRows, xMetric), [chartRows, xMetric]);
  const teamYDomain = useMemo(() => paddedChartDomain(chartRows, yMetric), [chartRows, yMetric]);
  const axisTitle = key => metric(key)?.[1].replace(" (selected view)", "").replace("Defense allowed:", "Defense:");
  const xMean = chartRows.length ? chartRows.reduce((sum, r) => sum + Number(r[xMetric]), 0) / chartRows.length : null;
  const yMean = chartRows.length ? chartRows.reduce((sum, r) => sum + Number(r[yMetric]), 0) / chartRows.length : null;
  const teamChartTooltip = ({ active, payload }) => {
    const r = active && payload?.[0]?.payload;
    if (!r) return null;
    return <div className="rounded-xl border border-slate-600 bg-slate-950 px-4 py-3 text-xs text-white shadow-2xl">
      <div className="mb-2 flex items-center gap-2 text-sm font-black"><img src={nflLogoUrl(r.team)} alt="" className="h-6 w-6 object-contain" />{r.team}{groupBy === "game" ? ` · Week ${r.week}` : ""}</div>
      {groupBy === "game" && <div className="mb-2 text-slate-400">{r.game}</div>}
      <div>{metric(xMetric)?.[1]}: <b>{chartFormat(r[xMetric], xMetric)}</b></div>
      <div>{metric(yMetric)?.[1]}: <b>{chartFormat(r[yMetric], yMetric)}</b></div>
      <div className="mt-1 text-slate-400">{r.plays.toLocaleString()} plays · {r.games} game{r.games === 1 ? "" : "s"}</div>
    </div>;
  };
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const exportRows = () => {
    const keys = ["team", ...(groupBy === "game" ? ["game", "week"] : []), ...(view === "chart" ? chartColumns : columns).map(([key]) => key)];
    exportToCsv(`nfl_team_stats_${season}_${side}_${week === "ALL" ? "all_weeks" : `week_${week}`}.csv`, view === "chart" ? combinedTeamRows : tableRows, keys);
  };

  return <div className="min-w-0 max-w-full space-y-5">
    <section className="rounded-3xl border border-slate-800 bg-slate-900/95 p-4 md:p-5 shadow-xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><div className="text-xs font-extrabold tracking-[0.22em] text-sky-400">TEAM & GAME INTELLIGENCE</div><h2 className="mt-1 text-2xl font-black text-white">Team Stats</h2><p className="mt-1 text-xs text-slate-400">Regular season scrimmage plays · {season} · {side === "offense" ? "team production" : "production allowed"}</p></div>
        <button onClick={exportRows} disabled={!tableRows.length} className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-2 text-xs font-bold text-sky-300 disabled:opacity-40">Export CSV</button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <select value={side} onChange={(e) => setSide(e.target.value)} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm"><option value="offense">Offense</option><option value="defense">Defense (allowed)</option></select>
        <select value={team} onChange={(e) => setTeam(e.target.value)} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm"><option value="ALL">All teams</option>{team !== "ALL" && !teams.includes(team) && <option value={team}>{team}</option>}{teams.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <select value={week} onChange={(e) => setWeek(e.target.value)} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm"><option value="ALL">All available weeks</option>{week !== "ALL" && !weeks.includes(Number(week)) && <option value={week}>Week {week}</option>}{weeks.map((w) => <option key={w} value={w}>Week {w}</option>)}</select>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-sm"><option value="team">By team</option><option value="game">By game</option></select>
      </div>
    </section>
    {loading ? <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300">Loading team stats…</div> : !plays.length ? <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300">{situation && rows.length ? "No plays match the situation filters." : `No regular season play-by-play available for ${season}.`}</div> : <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{[
        ["Team games", summary.games, "int"], ["Plays per game", summary.playsPerGame, "decimal"],
        ["Yards per play", summary.yardsPerPlay, "decimal"], ["EPA per play", summary.epaPerPlay, "signed"],
        ["Pass EPA per play", summary.passEpaPerPlay, "signed"], ["Rush EPA per play", summary.rushEpaPerPlay, "signed"],
        ["Success rate", summary.successRate, "percent"],
      ].map(([label, value, type]) => <div key={label} className="rounded-2xl border border-slate-800 bg-slate-950/75 p-4"><div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div><div className="mt-2 text-3xl font-black text-white">{display(value, type)}</div></div>)}</div>
      <section className="min-w-0 max-w-full overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/95 p-3 sm:p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div><h3 className="text-lg font-black">{view === "chart" ? "Offense & defense chart" : side === "offense" ? "Offensive production" : "Defensive production allowed"}</h3><p className="text-xs text-slate-400">{groupBy === "team" ? "One point per team" : "One point per team game"} · {week === "ALL" ? "All available weeks" : `Week ${week}`}</p></div>
          <div className="flex rounded-full border border-slate-700 bg-slate-900 p-1">{[["table", "Table"], ["chart", "Chart"]].map(([mode, label]) => <button key={mode} onClick={() => setView(mode)} className={`rounded-full px-4 py-2 text-xs font-bold ${view === mode ? "bg-sky-500 text-slate-950" : "text-slate-300"}`}>{label}</button>)}</div>
        </div>
        {view === "chart" ? <>
          <p className="mb-3 text-xs text-slate-400">Choose offense or defense allowed independently on either axis. Minimum plays uses the selected offense/defense view. Defense EPA axes are reversed: lower is better (up/right). Offense EPA: higher is better. Axes fit the displayed teams with a 10% buffer; dashed lines mark their averages.</p>
          <div className="mb-3 flex flex-wrap items-end gap-3">
            {[["X axis", xMetric, setXMetric], ["Y axis", yMetric, setYMetric]].map(([label, value, setter]) => <label key={label} className="text-xs text-slate-400">{label}<select value={value} onChange={(e) => setter(e.target.value)} className="mt-1 block min-w-[160px] rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white">{chartColumns.map(([key, name]) => <option key={key} value={key}>{name}</option>)}</select></label>)}
            <label className="text-xs text-slate-400">Minimum plays<input type="number" min="0" step="1" value={minPlays} onChange={(e) => setMinPlays(Math.max(0, Number(e.target.value) || 0))} className="mt-1 block w-28 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white" /></label>
            <label className="text-xs text-slate-400">Show<select value={chartLimit} onChange={(e) => setChartLimit(e.target.value)} className="mt-1 block rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white">{[["all", "All points"], ["10", "Top 10"], ["25", "Top 25"], ["50", "Top 50"], ["100", "Top 100"]].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-300"><input type="checkbox" checked={showTeamLabels} onChange={e => setShowTeamLabels(e.target.checked)} className="accent-sky-500" />Team labels</label>
            <div className="ml-auto text-xs text-slate-400">Showing {chartRows.length} of {combinedTeamRows.length} points</div>
          </div>
          {chartRows.length ? <div className="mx-auto w-full max-w-[1120px] lg:w-[85%] aspect-[49/27] min-h-[320px] rounded-xl bg-slate-900 p-2"><ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 40, right: 35, bottom: 65, left: 56 }}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="4 4" />
              <XAxis type="number" dataKey={xMetric} reversed={reverseX} domain={teamXDomain} allowDataOverflow tick={{ fill: "#cbd5e1", fontSize: 11, fontWeight: 700 }} tickFormatter={(v) => chartFormat(v, xMetric)} label={{ value: axisTitle(xMetric), position: "bottom", offset: 12, fill: "#94a3b8", fontWeight: 700 }} />
              <YAxis type="number" dataKey={yMetric} reversed={reverseY} domain={teamYDomain} allowDataOverflow width={84} tick={{ fill: "#cbd5e1", fontSize: 11, fontWeight: 700 }} tickFormatter={(v) => chartFormat(v, yMetric)} label={{ value: axisTitle(yMetric), angle: -90, position: "left", offset: 18, fill: "#94a3b8", fontWeight: 700 }} />
              <Tooltip content={teamChartTooltip} wrapperStyle={{ zIndex: 1000, outline: "none" }} allowEscapeViewBox={{ x: true, y: true }} />
              {xMean != null && <ReferenceLine x={xMean} stroke="#94a3b8" strokeDasharray="5 5" />}
              {yMean != null && <ReferenceLine y={yMean} stroke="#94a3b8" strokeDasharray="5 5" />}
              <Scatter data={chartRows} shape={<PlayerLogoPoint showLabel={showTeamLabels} />} />
            </ScatterChart>
          </ResponsiveContainer></div> : <p className="p-8 text-center text-sm text-slate-400">No teams match the chart filters.</p>}
        </> : <>
        <p className="mb-3 text-xs text-slate-400">Click a column to sort. Explosive = 20+ pass yards or 10+ run yards. Sacks and turnovers are committed by the offense; the defense view shows those forced or recorded against opponents. Red zone plays start inside the opponent 20. Neutral Pass % uses pass/run plays within 7 points, excluding the final 2 minutes of either half and overtime; it does not require a valid pace interval. Pass/run classification matches Pass %.</p>
        <div className="max-h-[65vh] w-full max-w-full overflow-auto overscroll-contain rounded-2xl border border-slate-800"><table className="w-full min-w-[1700px] text-center text-xs tabular-nums"><thead className="sticky top-0 z-10 bg-slate-900"><tr><th className="sticky left-0 z-20 bg-slate-900 px-3 py-3 text-left"><button onClick={() => toggleSort("team")}>Team{sort.key === "team" ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</button></th>{groupBy === "game" && <><th className="px-3 py-3 text-left">Game</th><th className="px-3 py-3 text-center">Week</th></>}{columns.map(([key, label]) => <th key={key} className="whitespace-nowrap px-3 py-3 text-center"><button onClick={() => toggleSort(key)}>{label}{sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</button></th>)}</tr></thead><tbody>{tableRows.map((r, i) => <tr key={`${r.game}__${r.team}`} className={i % 2 ? "bg-slate-900/70" : "bg-slate-950"}><td className={`sticky left-0 px-3 py-3 font-bold ${i % 2 ? "bg-slate-900" : "bg-slate-950"}`}><span className="inline-flex items-center gap-2"><img src={nflLogoUrl(r.team)} alt="" className="h-5 w-5 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }}/>{r.team}</span></td>{groupBy === "game" && <><td className="whitespace-nowrap px-3 py-3">{r.game}</td><td className="px-3 py-3 text-center">{r.week}</td></>}{columns.map(([key, , type]) => <td key={key} className="whitespace-nowrap px-3 py-3 text-center">{display(r[key], type)}</td>)}</tr>)}</tbody></table></div>
        {!tableRows.length && <p className="p-4 text-sm text-slate-400">No plays match these filters.</p>}
        </>}
      </section>
      <p className="text-xs text-slate-400">{PACE_METHOD}</p>
      <p className="text-xs text-slate-400">{situation ? "Games and plays per game count only games with a matching play." : "Play and game counts cover the available file only."} Rates use eligible plays; third and fourth down rates use plays on those downs. Net yards include sacks; pass yards are credited passing yards and rush yards include quarterback scrambles. Special teams, kneels, spikes, and nullified plays are excluded. Defense rates are from the opposing offense’s plays. Pass and rush EPA use the file’s pass/run play types and average only valid EPA values within each type; sacks classified as pass count toward pass EPA, and scrambles classified as run count toward rush EPA. Lower EPA allowed is better for defense.</p>
    </>}
  </div>;
}

const FLOW_POSITIONS = ["QB", "RB", "WR", "TE"];
const FLOW_COLUMNS = [...FLOW_POSITIONS, "Total"];

function fantasyFlowPoints(row, scoring) {
  if (scoring === "dk") return calculateDkPoints(row);
  const receptionPoints = scoring === "standard" ? 0 : scoring === "half" ? 0.5 : 1;
  return calculateCustomFantasyPoints(row, receptionPoints, 4);
}

function buildFantasyFlowRows(rows = [], season, scoring = "ppr", startWeek = 1, endWeek = 18) {
  const teams = Object.keys(TEAM_NAMES).sort();
  const defenses = new Map(teams.map(team => [team, { team, games: new Set(), totals: {} }]));
  const seen = new Set();
  let skipped = 0;
  for (const row of rows) {
    const week = researchWeek(row);
    if (Number(row.season) !== Number(season) || week < startWeek || week > endWeek || week < 1 || week > 18) continue;
    if (row.season_type && String(row.season_type).toUpperCase() !== "REG") continue;
    const opponent = gameLogOpponent(row);
    const game = String(row.game_id || "");
    if (!game || !defenses.has(opponent)) { skipped += 1; continue; }
    const defense = defenses.get(opponent);
    defense.games.add(game);
    const pos = researchPos(row);
    if (!FLOW_POSITIONS.includes(pos)) continue;
    const player = row.player_id || researchName(row);
    if (!player) { skipped += 1; continue; }
    const key = `${game}__${researchTeam(row)}__${player}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const points = fantasyFlowPoints(row, scoring);
    if (!Number.isFinite(points)) { skipped += 1; continue; }
    defense.totals[pos] = (defense.totals[pos] || 0) + points;
  }
  const output = [...defenses.values()].map(defense => ({
    team: defense.team,
    games: defense.games.size,
    positions: Object.fromEntries(FLOW_POSITIONS.map(pos => {
      // This usage-based feed omits players with no recorded activity.
      // Each observed offensive team-game counts for every position, including
      // positions with zero activity. Never create games for absent weeks/byes.
      const covered = defense.games.size;
      const points = covered ? (defense.totals[pos] || 0) / covered : null;
      return [pos, { points, covered, rank: null }];
    })),
  }));
  for (const pos of FLOW_POSITIONS) {
    const available = output.filter(team => team.positions[pos].points !== null);
    for (const team of available) {
      const points = team.positions[pos].points;
      team.positions[pos].rank = 1 + available.filter(other => other.positions[pos].points < points - 1e-9).length;
    }
  }
  for (const team of output) {
    const ranks = FLOW_POSITIONS.map(pos => team.positions[pos].rank);
    team.positions.Total = {
      // Require all four positions; missing data must not change the weights.
      averageRank: ranks.every(rank => rank != null)
        ? ranks.reduce((sum, rank) => sum + rank, 0) / FLOW_POSITIONS.length : null,
      rank: null,
    };
  }
  const totalsAvailable = output.filter(team => team.positions.Total.averageRank != null);
  for (const team of totalsAvailable) {
    team.positions.Total.rank = totalsAvailable.length - totalsAvailable.filter(other => other.positions.Total.averageRank > team.positions.Total.averageRank + 1e-9).length;
  }
  return { rows: output, skipped };
}

function fantasyFlowColor(rank) {
  if (rank == null) return { backgroundColor: "#1e293b", color: "#94a3b8" };
  const stops = [[242, 139, 130], [255, 242, 204], [99, 190, 123]];
  const value = Math.max(0, Math.min(1, (rank - 1) / 31));
  const segment = value <= 0.5 ? 0 : 1;
  const t = segment === 0 ? value * 2 : (value - 0.5) * 2;
  const rgb = stops[segment].map((v, i) => Math.round(v + (stops[segment + 1][i] - v) * t));
  return { backgroundColor: `rgb(${rgb.join(",")})`, color: "#142334" };
}

function buildFantasyFlowGameLog(rows, season, scoring, startWeek, endWeek, team, position) {
  const games = new Map();
  const seen = new Set();
  for (const row of rows) {
    const week = researchWeek(row);
    if (Number(row.season) !== Number(season) || week < startWeek || week > endWeek || week < 1 || week > 18) continue;
    if (row.season_type && String(row.season_type).toUpperCase() !== "REG") continue;
    if (gameLogOpponent(row) !== team || !row.game_id) continue;
    if (!games.has(row.game_id)) games.set(row.game_id, { game: row.game_id, week, opponent: researchTeam(row), players: [], points: 0 });
    if (researchPos(row) !== position) continue;
    const player = row.player_id || researchName(row);
    if (!player) continue;
    const key = `${row.game_id}__${researchTeam(row)}__${player}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const points = fantasyFlowPoints(row, scoring);
    if (!Number.isFinite(points)) continue;
    const game = games.get(row.game_id);
    game.players.push({ ...row, flow_points: points });
    game.points += points;
  }
  return [...games.values()].sort((a, b) => a.week - b.week || a.game.localeCompare(b.game)).map(game => {
    game.players.sort((a, b) => b.flow_points - a.flow_points || researchName(a).localeCompare(researchName(b)));
    const sum = key => game.players.reduce((total, row) => total + numberValue(row[key]), 0);
    return { ...game, passYards: sum("pass_yards_gained"), rushYards: sum("rush_yards_gained"), recYards: sum("rec_yards_gained"), targets: sum("rec_attempt"), receptions: sum("receptions"), touchdowns: sum("pass_touchdown") + sum("rush_touchdown") + sum("rec_touchdown") };
  });
}

function FantasyFlowChartView({ rows, season, loading }) {
  const [selectedCell, setSelectedCell] = useState(null);
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  useEffect(() => { setSelectedCell(null); }, [season]);
  useEffect(() => {
    if (!selectedCell) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const closeOnEscape = event => { if (event.key === "Escape") setSelectedCell(null); };
    document.addEventListener("keydown", closeOnEscape);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", closeOnEscape); returnFocusRef.current?.focus(); };
  }, [selectedCell]);
  const [scoring, setScoring] = useState("ppr");
  const [startWeek, setStartWeek] = useState(1);
  const [endWeek, setEndWeek] = useState(18);
  const [search, setSearch] = useState("");
  const [sortPosition, setSortPosition] = useState("Team");
  const [sortDirection, setSortDirection] = useState("asc");
  const onFlowHeaderClick = column => {
    if (sortPosition === column) setSortDirection(direction => direction === "asc" ? "desc" : "asc");
    else { setSortPosition(column); setSortDirection(column === "Team" ? "asc" : "desc"); }
  };
  const flowSortLabel = column => sortPosition === column ? (sortDirection === "asc" ? " ↑" : " ↓") : "";
  const [layout, setLayout] = useState("vertical");
  const isVertical = layout === "vertical";
  const panelCount = isVertical ? 1 : 4;
  const scoringLabels = { ppr: "PPR", half: "Half PPR", standard: "Standard", dk: "DraftKings" };
  const weeks = useMemo(() => [...new Set(rows.filter(r => Number(r.season) === Number(season)).map(researchWeek).filter(w => w >= 1 && w <= 18))].sort((a, b) => a - b), [rows, season]);
  const result = useMemo(() => buildFantasyFlowRows(rows, season, scoring, startWeek, endWeek), [rows, season, scoring, startWeek, endWeek]);
  const displayed = useMemo(() => result.rows.filter(r => `${r.team} ${TEAM_NAMES[r.team]}`.toLowerCase().includes(search.toLowerCase().trim())).sort((a, b) => {
    if (sortPosition === "Team") return (sortDirection === "asc" ? 1 : -1) * a.team.localeCompare(b.team);
    const aRank = a.positions[sortPosition].rank, bRank = b.positions[sortPosition].rank;
    if (aRank == null || bRank == null) return aRank == null && bRank == null ? a.team.localeCompare(b.team) : aRank == null ? 1 : -1;
    return (sortDirection === "asc" ? aRank - bRank : bRank - aRank) || a.team.localeCompare(b.team);
  }), [result, search, sortPosition, sortDirection]);
  const detailGames = useMemo(() => selectedCell ? buildFantasyFlowGameLog(rows, season, scoring, startWeek, endWeek, selectedCell.team, selectedCell.position) : [], [rows, season, scoring, startWeek, endWeek, selectedCell]);
  const selectedRank = selectedCell ? result.rows.find(r => r.team === selectedCell.team)?.positions[selectedCell.position] : null;
  const exportRows = () => {
    const csvRows = displayed.map(r => ({ Team: r.team, Games: r.games, "Total Rank": r.positions.Total.rank ?? "", "Total Average Rank": r.positions.Total.averageRank ?? "", ...Object.fromEntries(FLOW_POSITIONS.flatMap(pos => [[`${pos} Rank`, r.positions[pos].rank ?? ""], [`${pos} Points Allowed Per Game`, r.positions[pos].points == null ? "" : Number(r.positions[pos].points.toFixed(2))]])) }));
    exportToCsv(`fantasy_flow_${season}_${scoring}_weeks_${startWeek}-${endWeek}.csv`, csvRows, ["Team", "Games", ...FLOW_POSITIONS.flatMap(pos => [`${pos} Rank`, `${pos} Points Allowed Per Game`]), "Total Rank", "Total Average Rank"]);
  };
  const control = "rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100";
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="text-lg font-black text-white">Fantasy Flow Chart</h2><p className="mt-1 text-xs text-slate-400">{season} regular season · Fantasy points allowed per game by opposing player position</p></div>
        <button className={control} onClick={exportRows} disabled={loading || !displayed.some(r => r.games)}>Export CSV</button>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-slate-400">Layout<select className={control} value={layout} onChange={e => setLayout(e.target.value)}><option value="vertical">Compact vertical</option><option value="panels">Four panels</option></select></label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">Scoring<select className={control} value={scoring} onChange={e => setScoring(e.target.value)}>{Object.entries(scoringLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">From week<select className={control} value={startWeek} onChange={e => { const w = Number(e.target.value); setStartWeek(w); setEndWeek(end => Math.max(end, w)); }}>{[...new Set([1, startWeek, ...weeks])].sort((a, b) => a - b).map(w => <option key={w} value={w}>Week {w}</option>)}</select></label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">Through week<select className={control} value={endWeek} onChange={e => setEndWeek(Number(e.target.value))}>{[...new Set([...weeks, endWeek, 18])].filter(w => w >= startWeek).sort((a, b) => a - b).map(w => <option key={w} value={w}>{w === 18 ? "All remaining weeks" : `Week ${w}`}</option>)}</select></label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">Sort<select className={control} value={sortPosition} onChange={e => { setSortPosition(e.target.value); setSortDirection(e.target.value === "Team" ? "asc" : "desc"); }}><option value="Team">{sortPosition === "Team" && sortDirection === "desc" ? "Team Z–A" : "Team A–Z"}</option>{FLOW_COLUMNS.map(pos => <option key={pos} value={pos}>{pos}: {pos === "Total" ? (sortPosition === pos && sortDirection === "asc" ? "lowest" : "highest") + " rank first" : (sortPosition === pos && sortDirection === "asc" ? "fewest" : "most") + " allowed first"}</option>)}</select></label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">Team<input className={control} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search teams…" /></label>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-300"><span className="rounded px-2 py-1 font-bold" style={fantasyFlowColor(1)}>1 · Fewest allowed</span><div className="h-2 w-24 rounded-full" style={{ background: "linear-gradient(to right, #f28b82, #fff2cc, #63be7b)" }} /><span className="rounded px-2 py-1 font-bold" style={fantasyFlowColor(32)}>32 · Most allowed</span><span className="ml-1 text-slate-400">Click a position cell for the game log. Ties share a rank. Total = overall rank of the QB/RB/WR/TE average.</span></div>
      {result.skipped > 0 && <p className="mt-2 text-xs text-amber-300">{result.skipped} records excluded because game, opponent, or player data was unavailable.</p>}
      {loading || !result.rows.some(r => r.games) || displayed.length === 0 ? (
        <div className="mt-3 rounded-lg border border-slate-800 p-6 text-center text-sm text-slate-400">{loading ? "Loading fantasy points…" : displayed.length === 0 ? "No matching teams." : "No completed game data for these weeks."}</div>
      ) : (
        <div className={isVertical ? "mt-3" : "mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"}>
          {Array.from({ length: Math.min(panelCount, displayed.length) }, (_, panel) => {
            const perPanel = Math.ceil(displayed.length / panelCount);
            const panelRows = displayed.slice(panel * perPanel, (panel + 1) * perPanel);
            if (!panelRows.length) return null;
            return <div key={panel} className={isVertical ? "overflow-auto rounded-lg border border-slate-800" : "overflow-hidden rounded-lg border border-slate-800"} style={isVertical ? { maxHeight: "max(320px, calc(100dvh - 300px))" } : undefined}>
              <table className="w-full table-fixed" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 12 }}>
                <thead className={isVertical ? "sticky top-0 z-10 bg-slate-900 text-slate-200" : "bg-slate-900 text-slate-200"}><tr><th aria-sort={sortPosition === "Team" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"} style={{ width: isVertical ? "20%" : "28%", padding: "6px 8px", textAlign: "left", fontSize: 11 }}><button type="button" className="w-full text-left hover:text-sky-300 focus-visible:outline focus-visible:outline-sky-400" onClick={() => onFlowHeaderClick("Team")} title="Sort teams alphabetically">Team{flowSortLabel("Team")}</button></th>{FLOW_COLUMNS.map(pos => <th key={pos} aria-sort={sortPosition === pos ? (sortDirection === "asc" ? "ascending" : "descending") : "none"} style={{ width: isVertical ? "16%" : "14.4%", padding: "6px 2px", textAlign: "center", fontSize: 11 }}><button type="button" className="w-full hover:text-sky-300 focus-visible:outline focus-visible:outline-sky-400" onClick={() => onFlowHeaderClick(pos)} title={pos === "Total" ? "Sort by overall rank from average QB/RB/WR/TE rank; click again to reverse" : `Sort by points allowed to ${pos}; click again to reverse`}>{pos}{flowSortLabel(pos)}</button></th>)}</tr></thead>
                <tbody>{panelRows.map(team => <tr key={team.team} className="border-t border-slate-800/60" style={{ height: isVertical ? 24 : 38 }}>
                  <td className="bg-slate-900/70" style={{ padding: isVertical ? "1px 8px" : "3px 6px" }} title={`${TEAM_NAMES[team.team]} · ${team.games} games`}><div className="flex items-center gap-1.5"><img src={nflLogoUrl(team.team)} alt="" style={{ width: isVertical ? 16 : 20, height: isVertical ? 16 : 20, objectFit: "contain", flexShrink: 0 }} onError={e => { e.currentTarget.style.visibility = "hidden"; }} /><span className="font-extrabold text-white" style={{ fontSize: 12 }}>{team.team}</span></div></td>
                  {FLOW_POSITIONS.map(pos => { const cell = team.positions[pos]; return <td key={pos} style={{ ...fantasyFlowColor(cell.rank), padding: 0, textAlign: "center" }} title={cell.points == null ? `${pos}: records available for ${cell.covered} of ${team.games} games` : `${TEAM_NAMES[team.team]} vs ${pos}: ${cell.points.toFixed(2)} points allowed per game (${scoringLabels[scoring]}), rank ${cell.rank} · ${team.games} games`}><button type="button" className="w-full transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-900" style={{ display: isVertical ? "flex" : "block", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "3px 0", lineHeight: 1, color: "inherit" }} onClick={e => { returnFocusRef.current = e.currentTarget; setSelectedCell({ team: team.team, position: pos }); }} aria-label={`View ${team.team} points allowed to ${pos} game log`}><div className="font-black tabular-nums" style={{ fontSize: isVertical ? 13 : 15, lineHeight: "17px" }}>{cell.rank ?? "—"}</div><div className="tabular-nums" style={{ fontSize: isVertical ? 10 : 9, lineHeight: "12px", opacity: isVertical ? 0.75 : 1 }}>{cell.points == null ? "No data" : cell.points.toFixed(1)}</div></button></td>; })}
                  <td style={{ ...fantasyFlowColor(team.positions.Total.rank), padding: "3px 2px", textAlign: "center" }} title={team.positions.Total.rank == null ? "Total requires complete QB, RB, WR, and TE data" : `Average position rank: (${FLOW_POSITIONS.map(pos => team.positions[pos].rank).join(" + ")}) / 4 = ${team.positions.Total.averageRank.toFixed(2)}`}>
                    <span className="font-black tabular-nums" style={{ fontSize: isVertical ? 13 : 15, lineHeight: "17px" }}>{team.positions.Total.rank ?? "—"}</span>
                  </td>
                </tr>)}</tbody>
              </table>
            </div>;
          })}
        </div>
      )}
      <p className="mt-2 text-[10px] text-slate-500">{isVertical ? "Bold = rank. Beside it = points allowed/game. Click for game logs. Missing data shows —." : "Bold = rank. Below = points allowed/game. Read down each panel, then across. Missing data shows —."}</p>
      <p className="mt-1 text-[10px] text-slate-500">Total ranks teams by (QB rank + RB rank + WR rank + TE rank) / 4. 1 = best defense; highest rank = worst defense (32 when all teams have complete data). Tied averages share the higher rank. All four positions require complete data.</p>
      {selectedCell && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-3 sm:p-6" onClick={e => { if (e.target === e.currentTarget) setSelectedCell(null); }}>
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="fantasy-flow-detail-title" tabIndex={-1} onKeyDown={e => {
          if (e.key !== "Tab") return;
          const items = Array.from(e.currentTarget.querySelectorAll("button, summary, [tabindex='0']")).filter(el => el.getClientRects().length);
          if (!items.length) { e.preventDefault(); return; }
          const first = items[0], last = items[items.length - 1];
          if (e.shiftKey && (document.activeElement === first || document.activeElement === e.currentTarget)) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && (document.activeElement === last || document.activeElement === e.currentTarget)) { e.preventDefault(); first.focus(); }
        }} className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-2xl border border-slate-700 bg-slate-950 p-4 shadow-2xl sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div><h3 id="fantasy-flow-detail-title" className="text-xl font-black text-white">{selectedCell.team} vs {selectedCell.position}</h3><p className="mt-1 text-xs text-slate-400">{TEAM_NAMES[selectedCell.team]} · {season} · {scoringLabels[scoring]} · Weeks {startWeek}–{Math.min(endWeek, weeks[weeks.length - 1] || endWeek)}</p></div>
            <button type="button" className={control} onClick={() => setSelectedCell(null)} aria-label="Close game log">Close</button>
          </div>
          <p className="mt-4 text-sm text-slate-200">{selectedRank?.rank == null ? "Incomplete position data" : `Rank ${selectedRank.rank} · ${selectedRank.points.toFixed(2)} points allowed per game`} · {detailGames.length} {detailGames.length === 1 ? "game" : "games"}</p>
          <p className="mt-1 text-xs text-slate-400">Expand a game to see every opposing {selectedCell.position} and their box score. Points use the selected scoring format.</p>
          <div className="mt-4 space-y-3">
            {detailGames.length === 0 && <p className="rounded-lg bg-slate-900 p-6 text-center text-slate-400">No game data for these weeks.</p>}
            {detailGames.map(game => <details key={game.game} className="rounded-xl border border-slate-800 bg-slate-900/60">
              <summary className="cursor-pointer p-4 text-slate-100"><span className="font-bold">Week {game.week} vs {game.opponent}</span><span className="ml-3 font-black text-sky-300">{`${game.points.toFixed(2)} pts allowed`}</span><span className="mt-1 block text-xs text-slate-400">{game.passYards} pass yds · {game.rushYards} rush yds · {game.recYards} rec yds · {game.receptions}/{game.targets} catches/targets · {game.touchdowns} TDs</span></summary>
              <div className="overflow-x-auto border-t border-slate-800 p-3">
                <table className="w-full min-w-[760px] text-xs tabular-nums"><thead><tr className="text-slate-400">{["Player", "Pass Yds", "Pass TD", "INT", "Rush Att", "Rush Yds", "Rush TD", "Targets", "Rec", "Rec Yds", "Rec TD", "Fum Lost", "Points"].map(label => <th key={label} className={`px-2 py-2 whitespace-nowrap ${label === "Player" ? "text-left" : "text-right"}`}>{label}</th>)}</tr></thead><tbody>
                  {game.players.length === 0 ? <tr><td colSpan={13} className="p-4 text-slate-400">No recorded {selectedCell.position} activity in this game — 0.00 points allowed. This game counts in the average.</td></tr> : game.players.map(player => <tr key={player.player_id || researchName(player)} className="border-t border-slate-800 text-slate-200"><td className="px-2 py-2 whitespace-nowrap font-bold">{researchName(player)}</td>{["pass_yards_gained", "pass_touchdown", "pass_interception", "rush_attempt", "rush_yards_gained", "rush_touchdown", "rec_attempt", "receptions", "rec_yards_gained", "rec_touchdown"].map(key => <td key={key} className="px-2 py-2 text-right">{numberValue(player[key])}</td>)}<td className="px-2 py-2 text-right">{numberValue(player.rec_fumble_lost) + numberValue(player.rush_fumble_lost)}</td><td className="px-2 py-2 text-right font-black text-sky-300">{player.flow_points.toFixed(2)}</td></tr>)}
                </tbody></table>
              </div>
            </details>)}
          </div>
        </div>
      </div>}

    </section>
  );
}


/** ================== PAGE ================== */
export default function NflAdvancedStats() {
  const [season, setSeason] = useState(2026);
  const [tab, setTab] = useState("Team Box Score");
  const [workspace, setWorkspace] = useState("tendencies");
  const [situation, setSituation] = useState(DEFAULT_SITUATION);
  const situationActive = SITUATION_FILTERS.some(([key]) => situation[key] !== "all");
  const activeSituation = situationActive ? situation : undefined;

  // cache: dataBySeason[season][tabKey] = rows
  const [dataBySeason, setDataBySeason] = useState({});
  const [updatedAtBySeason, setUpdatedAtBySeason] = useState({});
  // Shared raw-file promise cache prevents large sources such as pbp.json from
  // being downloaded again when moving between Passing/Receiving/Rushing.
  const sourceCacheRef = useRef(new Map());

  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const activeMeta = useMemo(() => TABS.find((t) => t.key === tab), [tab]);
  const kind = activeMeta?.kind || "ngs";
  const supportsSituation = ["unified", "teamstats", "pace"].includes(kind) || tab === "Team Tendencies";
  const activeDataKey = kind === "unified" ? `${tab}__${UNIFIED_CACHE_VERSION}` : tab;
  const activeWorkspace = useMemo(() => WORKSPACES.find((w) => w.key === workspace) || WORKSPACES[0], [workspace]);
  const workspaceTabs = activeWorkspace.tabs;
  const rowsRaw = dataBySeason?.[season]?.[activeDataKey] || [];

  const isSnapTab = kind === "snaps";
  const isDepthTab = kind === "depth";
  const isPbpTab = kind === "pbp";
  const isGameTab = kind === "games";
  const isResearchTab = kind === "research" || kind === "unified";
  const isGameLogTab = kind === "gamelog";
  const isChartable = kind === "ngs";
  const [view, setView] = useState("table");

  // Table columns visibility (NGS only)
  const [visibleBySeasonTab, setVisibleBySeasonTab] = useState({});

  // Chart controls (NGS only)
  const [xKey, setXKey] = useState("");
  const [yKey, setYKey] = useState("");
  const [gateKey, setGateKey] = useState("");
  const [gateMin, setGateMin] = useState("");
  const [ngsChartLimit, setNgsChartLimit] = useState("25");

  // Axis & diagonal styling
  const [axisColor, setAxisColor] = useState("#111827");
  const [diagMode, setDiagMode] = useState("off"); // "off" | "yx" | "means"
  const [diagColor, setDiagColor] = useState("#0ea5e9");
  const [diagDash, setDiagDash] = useState("6 6");

  // Week filter for NGS (week 0 = season-long)
  const [week, setWeek] = useState(0);

  // Snap Counts controls
  const [snapTeam, setSnapTeam] = useState("");
  const [snapGameType, setSnapGameType] = useState("REG");
  const [snapPhase, setSnapPhase] = useState("auto"); // auto | offense | defense | st
  const [snapWeekView, setSnapWeekView] = useState("all"); // all | number
  const [snapSearch, setSnapSearch] = useState("");

  // Depth Charts controls
  const [depthTeam, setDepthTeam] = useState("");
  const [depthPhase, setDepthPhase] = useState("auto"); // auto | offense | defense | st
  const [depthGroup, setDepthGroup] = useState(""); // pos_grp selection
  const [depthSearch, setDepthSearch] = useState("");


// Game Data controls
const [gameType, setGameType] = useState("ALL"); // ALL | REG | POST | PRE
const [gameWeek, setGameWeek] = useState("ALL"); // ALL | 1..18 etc
const [gameTeam, setGameTeam] = useState("ALL"); // ALL | KC | BUF | ...
const [gameSearch, setGameSearch] = useState("");
const [gameHideMissingLines, setGameHideMissingLines] = useState(false);

// Game Data columns visibility
const [visibleGamesBySeason, setVisibleGamesBySeason] = useState({});

// Game Analyzer (Play-by-Play) controls
const [pbpGameId, setPbpGameId] = useState("");
const [pbpTeam, setPbpTeam] = useState("ALL"); // posteam filter
const [pbpQtr, setPbpQtr] = useState("ALL");
const [pbpPlayType, setPbpPlayType] = useState("ALL");
const [pbpSearch, setPbpSearch] = useState("");
const [pbpHideNan, setPbpHideNan] = useState(true);
const [pbpTake, setPbpTake] = useState(500);

// Game Analyzer (PBP) advanced views
const [pbpSubtab, setPbpSubtab] = useState("Plays"); // Plays | Key Plays | Leaders | Situations
const [pbpEpaKeyMin, setPbpEpaKeyMin] = useState(1.5); // abs(epa) threshold
const [pbpWpKeyMin, setPbpWpKeyMin] = useState(0.08);  // abs(wp_delta) threshold
const [pbpKeyOnlyRedZone, setPbpKeyOnlyRedZone] = useState(false);


  // Keep the UI consistent: snap counts is table-only
  useEffect(() => {
    if (!isChartable) setView("table");
  }, [isChartable]);

  /** lazy fetch updated.json per season */
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const u = await fetchJson(`${BASE}/${season}/updated.json`);
        if (!dead && u?.last_updated) {
          setUpdatedAtBySeason((p) => ({ ...p, [season]: u.last_updated }));
        }
      } catch {
        // not fatal
      }
    })();
    return () => {
      dead = true;
    };
  }, [season]);

  /** lazy fetch only the active tab file (so pbp/depth charts etc never auto-load here) */
  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const meta = TABS.find((t) => t.key === tab);
        if (!meta) throw new Error("Unknown tab");

        // Already cached?
        const cached = dataBySeason?.[season]?.[activeDataKey];
        if (Array.isArray(cached)) {
          if (!dead) setLoading(false);
          return;
        }

        const loadSource = (file) => {
          const cacheKey = `${season}__${file}`;
          if (!sourceCacheRef.current.has(cacheKey)) {
            const request = fetchJson(`${BASE}/${season}/${file}`).catch((error) => {
              sourceCacheRef.current.delete(cacheKey);
              throw error;
            });
            sourceCacheRef.current.set(cacheKey, request);
          }
          return sourceCacheRef.current.get(cacheKey);
        };

        let arr = [];
        let combinedCharts = null;
        let combinedEntries = {};
        if (meta.kind === "unified" || meta.key === "Advanced Defense") {
          const chartTabs = TABS.filter(item => item.kind === "unified");
          const files = [...new Set([...chartTabs.flatMap(item => item.files), "pfr_advanced_def_weekly.json"])];
          const datasets = await Promise.all(files.map(file => loadSource(file).catch(error => {
            console.warn(`Optional player chart source failed: ${file}`, error);
            return [];
          })));
          const byFile = new Map(files.map((file, i) => [file, Array.isArray(datasets[i]) ? datasets[i] : []]));
          const lookupRows = files.filter(file => file !== "pbp.json").flatMap(file => byFile.get(file));
          const categoryRows = chartTabs.map(item => [item.category, buildUnifiedPlayerRows(item.files.map(file => byFile.get(file)), item.category, lookupRows)]);
          categoryRows.push(["defense", byFile.get("pfr_advanced_def_weekly.json") || []]);
          arr = categoryRows.find(([category]) => category === (meta.category || "defense"))?.[1] || [];
          combinedCharts = combinePlayerResearchRows(categoryRows, byFile.get("ff_opportunity.json") || []);
          for (const item of chartTabs) {
            const key = `${item.key}__${UNIFIED_CACHE_VERSION}`;
            combinedEntries[key] = categoryRows.find(([category]) => category === item.category)[1];
            combinedEntries[`${key}__charts`] = combinedCharts;
          }
          combinedEntries.__situationPbp = byFile.get("pbp.json") || [];
          combinedEntries.__situationPlayers = lookupRows;
          combinedEntries["Advanced Defense"] = byFile.get("pfr_advanced_def_weekly.json") || [];
          combinedEntries["Advanced Defense__charts"] = combinedCharts;
        } else if (Array.isArray(meta.files)) {
          const datasets = await Promise.all(
            meta.files.map((file) => loadSource(file).catch((error) => {
              console.warn(`Optional unified source failed: ${file}`, error);
              return [];
            }))
          );
          const sources = datasets.map((rows) => Array.isArray(rows) ? rows : []);
          arr = meta.kind === "teambox"
            ? buildTeamBoxRows(...sources)
            : buildUnifiedPlayerRows(sources, meta.category);
        } else {
          const rows = await loadSource(meta.file);
          arr = Array.isArray(rows) ? rows : [];
        }

        if (dead) return;
        setDataBySeason((prev) => ({
          ...prev,
          [season]: { ...(prev?.[season] || {}), ...combinedEntries, [activeDataKey]: arr, ...(combinedCharts ? { [`${activeDataKey}__charts`]: combinedCharts } : {}) },
        }));
      } catch (e) {
        if (!dead) setErr(String(e?.message || e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [season, tab, activeDataKey]); // eslint-disable-line react-hooks/exhaustive-deps

  
/** ---------- NGS: week filter + hidden cols ---------- */
  const ngsRows = useMemo(() => {
    if (kind !== "ngs") return [];
    const wkCol = rowsRaw[0]?.WEEK != null ? "WEEK" : rowsRaw[0]?.week != null ? "week" : "WEEK";
    const seasonTypeCol =
      rowsRaw[0]?.SEASON_TYPE != null ? "SEASON_TYPE" : rowsRaw[0]?.season_type != null ? "season_type" : null;

    let out = rowsRaw;

    // Prefer REG; if column exists, still allow anything if data isn't REG
    if (seasonTypeCol) {
      // keep all season types; your NGS set already includes season_type; feel free to tighten later
      out = out;
    }

    // week filter (week 0 = season-long)
    if (wkCol && out.length) {
      out = out.filter((r) => Number(r?.[wkCol]) === Number(week));
    }

    return out;
  }, [rowsRaw, kind, week]);

  /** ---------- Columns + defaults (NGS) ---------- */
  const allColumns = useMemo(() => {
    const baseCols = ngsRows[0] ? Object.keys(ngsRows[0]) : (rowsRaw[0] ? Object.keys(rowsRaw[0]) : []);
    let cols = orderColumns(baseCols, { kind });
    if (kind === "ngs") cols = cols.filter((c) => !HIDDEN_NGS_COLS.has(String(c).toUpperCase()));
    return cols;
  }, [ngsRows, rowsRaw, kind]);

  // initialize visible columns + sort for this season/tab (NGS only)
  useEffect(() => {
    if (kind !== "ngs") return;
    if (!allColumns.length) return;

    const key = `${season}__${tab}`;
    setVisibleBySeasonTab((prev) => (prev[key] ? prev : { ...prev, [key]: allColumns }));

    const defaultSort = allColumns.includes("PLAYER_DISPLAY_NAME")
      ? "PLAYER_DISPLAY_NAME"
      : allColumns.includes("name")
        ? "name"
        : allColumns[0] || "";
    setSort({ key: defaultSort, dir: "asc" });
  }, [season, tab, kind, allColumns]);

  const visibleKey = `${season}__${tab}`;
  const visibleColumns = (visibleBySeasonTab?.[visibleKey] || allColumns);

  /** ---------- Search + sort (NGS) ---------- */
  const filtered = useMemo(() => {
    if (kind !== "ngs") return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return ngsRows;
    return ngsRows.filter((r) => allColumns.some((c) => String(r?.[c] ?? "").toLowerCase().includes(needle)));
  }, [kind, ngsRows, allColumns, q]);

  const sorted = useMemo(() => {
    if (kind !== "ngs") return [];
    const { key, dir } = sort;
    if (!key) return filtered;
    const sign = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sign;
      return String(av ?? "").localeCompare(String(bv ?? "")) * sign;
    });
  }, [kind, filtered, sort]);

  function onHeaderClick(col) {
    setSort((p) => (p.key === col ? { key: col, dir: p.dir === "asc" ? "desc" : "asc" } : { key: col, dir: "asc" }));
  }
  function toggleColumn(col) {
    const key = `${season}__${tab}`;
    setVisibleBySeasonTab((prev) => {
      const cur = prev[key] || allColumns;
      const next = cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col];
      return { ...prev, [key]: next };
    });
  }
  const selectAll = () => setVisibleBySeasonTab((prev) => ({ ...prev, [visibleKey]: allColumns }));
  const clearAll = () => setVisibleBySeasonTab((prev) => ({ ...prev, [visibleKey]: [] }));
  const resetDefault = () => setVisibleBySeasonTab((prev) => ({ ...prev, [visibleKey]: allColumns }));

  /** ---------- numeric columns for chart (NGS) ---------- */
  const numericColumns = useMemo(() => {
    if (kind !== "ngs") return [];
    if (!sorted.length) return [];
    const sample = sorted.slice(0, Math.min(25, sorted.length));
    return allColumns.filter((c) => sample.every((r) => r[c] == null || typeof r[c] === "number"));
  }, [kind, sorted, allColumns]);

  // default chart picks per tab (NGS)
  useEffect(() => {
    if (kind !== "ngs") return;
    if (!numericColumns.length) return;

    const lower = (s) => (s || "").toLowerCase();
    const nLower = numericColumns.map(lower);

    const pickContains = (needles, fallback = "") => {
      for (const n of needles) {
        const idx = nLower.findIndex((c) => c.includes(lower(n)));
        if (idx !== -1) return numericColumns[idx];
      }
      return numericColumns[0] || fallback;
    };

    if (tab === "Passing") {
      setXKey((p) => p || pickContains(["avg_time_to_throw", "avg_completed_air_yards", "avg_intended_air_yards"]));
      setYKey((p) => p || pickContains(["passer_rating", "completion_percentage", "aggressiveness"]));
      setGateKey((p) => p || pickContains(["attempts", "passes", "games"]));
    } else if (tab === "Receiving") {
      setXKey((p) => p || pickContains(["targets", "avg_intended_air_yards", "avg_air_yards"]));
      setYKey((p) => p || pickContains(["yards", "receiving_yards", "avg_yac"]));
      setGateKey((p) => p || pickContains(["targets", "games"]));
    } else if (tab === "Rushing") {
      setXKey((p) => p || pickContains(["rush_attempts", "carries"]));
      setYKey((p) => p || pickContains(["rush_yards", "efficiency", "rush_yards_over_expected"]));
      setGateKey((p) => p || pickContains(["rush_attempts", "carries", "games"]));
    } else {
      setXKey((p) => p || numericColumns[0]);
      setYKey((p) => p || numericColumns[1] || numericColumns[0]);
      setGateKey((p) => p || pickContains(["attempts", "targets", "games"]));
    }
  }, [tab, kind, numericColumns]);

  function resetChartDefaults() {
    if (!numericColumns.length) return;

    const lower = (value) => String(value || "").toLowerCase();
    const normalizedColumns = numericColumns.map(lower);

    const pickContains = (needles, fallback = "") => {
      for (const needle of needles) {
        const index = normalizedColumns.findIndex((column) => column.includes(lower(needle)));
        if (index !== -1) return numericColumns[index];
      }
      return numericColumns[0] || fallback;
    };

    if (tab === "Passing") {
      setXKey(pickContains(["avg_time_to_throw", "avg_completed_air_yards", "avg_intended_air_yards"]));
      setYKey(pickContains(["passer_rating", "completion_percentage", "aggressiveness"]));
      setGateKey(pickContains(["attempts", "passes", "games"]));
    } else if (tab === "Receiving") {
      setXKey(pickContains(["targets", "avg_intended_air_yards", "avg_air_yards"]));
      setYKey(pickContains(["yards", "receiving_yards", "avg_yac"]));
      setGateKey(pickContains(["targets", "games"]));
    } else if (tab === "Rushing") {
      setXKey(pickContains(["rush_attempts", "carries"]));
      setYKey(pickContains(["rush_yards", "efficiency", "rush_yards_over_expected"]));
      setGateKey(pickContains(["rush_attempts", "carries", "games"]));
    } else {
      setXKey(numericColumns[0] || "");
      setYKey(numericColumns[1] || numericColumns[0] || "");
      setGateKey(pickContains(["attempts", "targets", "games"]));
    }

    setGateMin("");
    setAxisColor("#111827");
    setDiagMode("off");
    setDiagColor("#0ea5e9");
    setDiagDash("6 6");
    setNgsChartLimit("25");
  }

  /** ---------- Chart data + means & domains (NGS) ---------- */
  const { chartRows, xDomain, yDomain, xMean, yMean } = useMemo(() => {
    if (kind !== "ngs") return { chartRows: [], xDomain: ["auto", "auto"], yDomain: ["auto", "auto"], xMean: 0, yMean: 0 };

    let out = sorted;
    if (gateKey && gateMin !== "" && !isNaN(Number(gateMin))) {
      out = out.filter((r) => (typeof r[gateKey] === "number" ? r[gateKey] >= Number(gateMin) : true));
    }
    out = out.filter((r) => typeof r[xKey] === "number" && typeof r[yKey] === "number");

    const limitedOut = limitChartRows(out, ngsChartLimit, yKey);
    const withLabels = limitedOut.map((r) => {
      const playerName = getNgsName(r);
      const team = normalizeTeamAbbr(getNgsTeam(r));

      return {
        ...r,
        _label: shortPlayerName(playerName),
        _fullName: playerName,
        _team: team,
        _logo: nflLogoUrl(team),
        _fill: colorForTeam(team),
      };
    });

    const xs = withLabels.map((r) => r[xKey]);
    const ys = withLabels.map((r) => r[yKey]);
    const xMean = xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
    const yMean = ys.length ? ys.reduce((a, b) => a + b, 0) / ys.length : 0;

    return { chartRows: withLabels, xDomain: paddedChartDomain(withLabels, xKey), yDomain: paddedChartDomain(withLabels, yKey), xMean, yMean };
  }, [kind, sorted, gateKey, gateMin, xKey, yKey, ngsChartLimit]);

  /** ---------- Diagonal segment (NGS) ---------- */
  const diagSegment = useMemo(() => {
    if (kind !== "ngs") return null;
    if (!Array.isArray(xDomain) || !Array.isArray(yDomain)) return null;
    const [xMin, xMax] = xDomain;
    const [yMin, yMax] = yDomain;
    if (![xMin, xMax, yMin, yMax].every((v) => typeof v === "number" && isFinite(v))) return null;

    if (diagMode === "off") return null;

    let m = 1,
      b = 0;

    if (diagMode === "yx") {
      m = 1;
      b = 0; // y = x
    } else if (diagMode === "means") {
      if (Math.abs(xMean) > 1e-9) {
        m = yMean / xMean;
        b = 0;
      } else {
        m = 1;
        b = yMean - m * xMean;
      }
    }

    const seg = clipLineToRect(m, b, xMin, xMax, yMin, yMax);
    if (!seg) {
      if (diagMode === "means") {
        const m2 = 1;
        const b2 = yMean - m2 * xMean;
        return clipLineToRect(m2, b2, xMin, xMax, yMin, yMax);
      }
      return null;
    }
    return seg;
  }, [kind, diagMode, xDomain, yDomain, xMean, yMean]);


/** ================== GAME DATA: TABLE MODEL ================== */
const gameRows = useMemo(() => (kind === "games" ? rowsRaw : []), [kind, rowsRaw]);

const gameTypes = useMemo(() => {
  if (!gameRows.length) return ["ALL"];
  const set = new Set(
    gameRows.map((r) => String(r?.game_type ?? "").trim()).filter(Boolean)
  );
  const arr = Array.from(set).sort();
  return ["ALL", ...arr];
}, [gameRows]);

const gameWeeks = useMemo(() => {
  if (!gameRows.length) return ["ALL"];
  const set = new Set(gameRows.map((r) => Number(r?.week)).filter((n) => Number.isFinite(n)));
  return ["ALL", ...Array.from(set).sort((a, b) => a - b).map(String)];
}, [gameRows]);

const gameTeams = useMemo(() => {
  if (!gameRows.length) return ["ALL"];
  const set = new Set(
    gameRows
      .flatMap((r) => [String(r?.away_team ?? "").trim(), String(r?.home_team ?? "").trim()])
      .filter(Boolean)
  );
  return ["ALL", ...Array.from(set).sort()];
}, [gameRows]);

// init defaults once the tab is active + data exists
useEffect(() => {
  if (kind !== "games") return;
  if (!gameRows.length) return;

  if (!gameTypes.includes(gameType)) setGameType("ALL");
  if (!gameWeeks.includes(gameWeek)) setGameWeek("ALL");
  if (!gameTeams.includes(gameTeam)) setGameTeam("ALL");
}, [kind, gameRows, gameTypes, gameWeeks, gameTeams, gameType, gameWeek, gameTeam]);

// derived “matchup” + kickoff label
function gameMatchup(r) {
  const a = String(r?.away_team ?? "");
  const h = String(r?.home_team ?? "");
  return a && h ? `${a} @ ${h}` : "";
}
function gameKick(r) {
  const d = String(r?.gameday ?? "");
  const t = String(r?.gametime ?? "");
  return d && t ? `${d} ${t}` : d || "";
}

const gameAllColumns = useMemo(() => {
  if (!gameRows.length) return [];
  // keep it curated (good defaults) but still allow column picker
  const preferred = [
    "week",
    "game_type",
    "gameday",
    "weekday",
    "gametime",
    "away_team",
    "home_team",
    "away_score",
    "home_score",
    "away_moneyline",
    "home_moneyline",
    "spread_line",
    "total_line",
    "roof",
    "surface",
    "temp",
    "wind",
    "away_qb_name",
    "home_qb_name",
    "away_coach",
    "home_coach",
    "referee",
    "stadium",
  ];

  const keys = Object.keys(gameRows[0] || {});
  const set = new Set(keys);

  // start with preferred, then append everything else
  const cols = [
    ...preferred.filter((c) => set.has(c)),
    ...keys.filter((c) => !preferred.includes(c)),
  ];

  return cols;
}, [gameRows]);

const gameVisibleKey = `${season}__games`;
const gameVisibleColumns = useMemo(() => {
  const cur = visibleGamesBySeason?.[gameVisibleKey];
  return cur && Array.isArray(cur) ? cur : gameAllColumns;
}, [visibleGamesBySeason, gameVisibleKey, gameAllColumns]);

useEffect(() => {
  if (kind !== "games") return;
  if (!gameAllColumns.length) return;
  setVisibleGamesBySeason((prev) =>
    prev[gameVisibleKey] ? prev : { ...prev, [gameVisibleKey]: gameAllColumns }
  );
}, [kind, gameAllColumns, gameVisibleKey]);

function toggleGameColumn(col) {
  setVisibleGamesBySeason((prev) => {
    const cur = prev[gameVisibleKey] || gameAllColumns;
    const next = cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col];
    return { ...prev, [gameVisibleKey]: next };
  });
}

const gamesFilteredSorted = useMemo(() => {
  if (kind !== "games") return [];

  let out = gameRows;

  if (gameType !== "ALL") out = out.filter((r) => String(r?.game_type ?? "") === gameType);
  if (gameWeek !== "ALL") out = out.filter((r) => String(r?.week ?? "") === gameWeek);

  if (gameTeam !== "ALL") {
    out = out.filter(
      (r) => String(r?.away_team ?? "") === gameTeam || String(r?.home_team ?? "") === gameTeam
    );
  }

  if (gameHideMissingLines) {
    out = out.filter(
      (r) => Number.isFinite(Number(r?.spread_line)) || Number.isFinite(Number(r?.total_line))
    );
  }

  const needle = gameSearch.trim().toLowerCase();
  if (needle) {
    out = out.filter((r) => {
      const blob = [
        gameMatchup(r),
        r?.stadium,
        r?.referee,
        r?.away_qb_name,
        r?.home_qb_name,
        r?.away_coach,
        r?.home_coach,
        r?.roof,
        r?.surface,
        gameKick(r),
      ]
        .filter(Boolean)
        .join(" • ")
        .toLowerCase();
      return blob.includes(needle);
    });
  }

  // default sort: kickoff ascending
  return [...out].sort((a, b) => String(gameKick(a)).localeCompare(String(gameKick(b))));
}, [kind, gameRows, gameType, gameWeek, gameTeam, gameSearch, gameHideMissingLines]);

const exportGameDataCsv = () => {
  const cols = gameVisibleColumns;
  const rows = gamesFilteredSorted;

  const filename = `nfl_game_data_${season}_${new Date().toISOString().slice(0,10)}.csv`;

  const header = cols.join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          let val = r?.[c];
          if (c === "gametime") val = formatGameTime(val);
          if (c === "kickoff") val = formatKickoff(r);
          if (val == null) val = "";
          return '"' + String(val).replaceAll('"', '""') + '"';
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};


  /** ================== SNAP COUNTS: PIVOT ================== */
  const snapRows = useMemo(() => (kind === "snaps" ? rowsRaw : []), [kind, rowsRaw]);

  const snapTeams = useMemo(() => {
    if (!snapRows.length) return [];
    const set = new Set(snapRows.map((r) => String(r?.team ?? "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [snapRows]);

  const snapGameTypes = useMemo(() => {
    if (!snapRows.length) return ["REG"];
    const set = new Set(snapRows.map((r) => String(r?.game_type ?? "").trim()).filter(Boolean));
    const arr = Array.from(set).sort();
    return arr.length ? arr : ["REG"];
  }, [snapRows]);

  // initialize snap defaults once data exists
  useEffect(() => {
    if (kind !== "snaps") return;
    if (!snapRows.length) return;
    if (!snapTeam) setSnapTeam(snapTeams[0] || "");
    if (!snapGameTypes.includes(snapGameType)) setSnapGameType(snapGameTypes[0] || "REG");
  }, [kind, snapRows, snapTeam, snapTeams, snapGameType, snapGameTypes]);

  const snapWeeks = useMemo(() => {
    if (!snapRows.length) return [];
    const set = new Set(
      snapRows
        .filter((r) => !snapTeam || String(r?.team) === String(snapTeam))
        .filter((r) => !snapGameType || String(r?.game_type) === String(snapGameType))
        .map((r) => Number(r?.week))
        .filter((n) => Number.isFinite(n))
    );
    return Array.from(set).sort((a, b) => a - b);
  }, [snapRows, snapTeam, snapGameType]);

  const AUTO_SKILL_POS = new Set(["QB","RB","WR","TE"]); // used when Phase = Auto

  function pickPhaseCols(phase) {
    if (phase === "offense") return { snaps: "offense_snaps", pct: "offense_pct" };
    if (phase === "defense") return { snaps: "defense_snaps", pct: "defense_pct" };
    if (phase === "st") return { snaps: "st_snaps", pct: "st_pct" };
    // auto: pick the largest snaps column for each row
    return null;
  }

  const snapPivot = useMemo(() => {
    if (kind !== "snaps") return { positions: [], weeks: [], byPos: {} };
    if (!snapRows.length) return { positions: [], weeks: [], byPos: {} };

    const needle = snapSearch.trim().toLowerCase();

    let rows = snapRows;
    if (snapTeam) rows = rows.filter((r) => String(r?.team) === String(snapTeam));
    if (snapGameType) rows = rows.filter((r) => String(r?.game_type) === String(snapGameType));

    if (needle) {
      rows = rows.filter((r) => String(r?.player ?? "").toLowerCase().includes(needle) || String(r?.position ?? "").toLowerCase().includes(needle));
    }

    const weeks = snapWeeks;
    const showWeeks = snapWeekView === "all" ? weeks : [Number(snapWeekView)].filter((n) => Number.isFinite(n));

    const byPos = {}; // pos -> { players: { name -> { week -> {snaps,pct} } } }
    const phaseCols = pickPhaseCols(snapPhase);

    for (const r of rows) {
      const posRaw = String(r?.position ?? "").trim() || "—";
      const pos = posRaw.toUpperCase();
      // Phase = Auto: only show skill positions (QB/RB/WR/TE)
      if (snapPhase === "auto" && pos !== "—" && !AUTO_SKILL_POS.has(pos)) continue;
      const player = String(r?.player ?? "").trim() || "—";
      const wk = Number(r?.week);
      if (!showWeeks.includes(wk)) continue;

      let snaps = 0;
      let pct = 0;

      if (!phaseCols) {
        // auto: choose best of offense/defense/st by snaps
        const o = Number(r?.offense_snaps ?? 0);
        const d = Number(r?.defense_snaps ?? 0);
        const s = Number(r?.st_snaps ?? 0);
        if (o >= d && o >= s) {
          snaps = o;
          pct = Number(r?.offense_pct ?? 0);
        } else if (d >= o && d >= s) {
          snaps = d;
          pct = Number(r?.defense_pct ?? 0);
        } else {
          snaps = s;
          pct = Number(r?.st_pct ?? 0);
        }
      } else {
        snaps = Number(r?.[phaseCols.snaps] ?? 0);
        pct = Number(r?.[phaseCols.pct] ?? 0);
      }

      if (!byPos[pos]) byPos[pos] = { players: {} };
      if (!byPos[pos].players[player]) byPos[pos].players[player] = {};
      byPos[pos].players[player][wk] = { snaps: snaps || 0, pct: pct || 0 };
    }

    const positions = Object.keys(byPos).sort((a, b) => a.localeCompare(b));

    // sort players within each pos by selected week pct (or max pct if all)
    for (const pos of positions) {
      const players = Object.keys(byPos[pos].players);
      const targetWeek = snapWeekView === "all" ? Math.max(...showWeeks) : showWeeks[0];

      players.sort((pa, pb) => {
        const a = byPos[pos].players[pa] || {};
        const b = byPos[pos].players[pb] || {};

        const score = (obj) => {
          if (snapWeekView !== "all") {
            const v = obj[targetWeek];
            return (v?.pct ?? 0) * 1000 + (v?.snaps ?? 0);
          }
          // all: use max pct across weeks, tie by latest week snaps
          let maxPct = 0;
          for (const wk of showWeeks) maxPct = Math.max(maxPct, obj[wk]?.pct ?? 0);
          const latest = obj[targetWeek] || {};
          return maxPct * 1000 + (latest.snaps ?? 0);
        };

        return score(b) - score(a);
      });

      byPos[pos].sortedPlayers = players;
    }

    return { positions, weeks: showWeeks, byPos };
  }, [kind, snapRows, snapTeam, snapGameType, snapPhase, snapWeekView, snapWeeks, snapSearch]);

  
  /** ================== DEPTH CHARTS: VIEW MODEL ================== */
  const depthRows = useMemo(() => (kind === "depth" ? rowsRaw : []), [kind, rowsRaw]);

  const depthTeams = useMemo(() => {
    if (!depthRows.length) return [];
    const set = new Set(depthRows.map((r) => String(r?.team ?? "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [depthRows]);

  useEffect(() => {
    if (kind !== "depth") return;
    if (!depthRows.length) return;
    if (!depthTeam) setDepthTeam(depthTeams[0] || "");
  }, [kind, depthRows, depthTeam, depthTeams]);

  // The consolidated depth_charts.json is one current snapshot and no longer
  // includes the old `dt` field. Keep this value only for compatibility with
  // existing dependency arrays; depth-chart rows are now filtered by team.
  const depthDt = "";

  const AUTO_SKILL_POS_DEPTH = useMemo(() => new Set(["QB", "RB", "WR", "TE"]), []);

  const depthGroups = useMemo(() => {
    if (!depthRows.length || !depthTeam) return [];

    const subset = depthRows.filter(
      (r) => String(r?.team ?? "") === String(depthTeam)
    );

    const DEF_ABB = new Set([
      "CB","LCB","RCB","NB","S","FS","SS",
      "LB","MLB","WLB","SLB","ILB","OLB",
      "DE","DT","NT","LDE","RDE","LDT","RDT"
    ]);

    const byGrp = new Map(); // grp -> { grp, hasSkill, hasOff, hasDef, hasSt }
    for (const r of subset) {
      const grp = String(r?.pos_grp ?? "").trim() || "—";
      const pos = String(r?.pos_abb ?? "").trim().toUpperCase();
      const rec = byGrp.get(grp) || { grp, hasSkill: false, hasOff: false, hasDef: false, hasSt: false };
      const g = grp.toLowerCase();

      if (g.includes("special")) rec.hasSt = true;
      if (AUTO_SKILL_POS_DEPTH.has(pos)) rec.hasSkill = true;

      // offense-ish heuristics
      if (
        rec.hasSkill ||
        g.includes("off") ||
        g.includes("11") || g.includes("12") || g.includes("13") ||
        g.includes("3wr") || g.includes("2wr") ||
        g.includes("1te") || g.includes("2te") ||
        g.includes("shotgun") || g.includes("i-form") || g.includes("pistol")
      ) {
        rec.hasOff = true;
      }

      // defense-ish heuristics
      if (!rec.hasOff && (g.includes("base") || g.includes("nickel") || g.includes("dime") || g.includes("3-4") || g.includes("4-3") || g.includes("def") || g.includes("front"))) {
        rec.hasDef = true;
      }
      if (!rec.hasOff && DEF_ABB.has(pos)) rec.hasDef = true;

      byGrp.set(grp, rec);
    }

    let arr = Array.from(byGrp.values());

    // Filter groups by phase
    if (depthPhase === "offense") arr = arr.filter((g) => g.hasOff && !g.hasSt);
    else if (depthPhase === "defense") arr = arr.filter((g) => g.hasDef && !g.hasSt);
    else if (depthPhase === "st") arr = arr.filter((g) => g.hasSt);
    else arr = arr.filter((g) => g.hasOff && !g.hasSt); // auto => offense groups

        const list = arr.map((g) => g.grp).sort((a, b) => a.localeCompare(b));
    // Special option: ignore packages and show full position depth
    if (!list.includes("__ALL__")) list.unshift("__ALL__");
    return list;

  }, [depthRows, depthTeam, depthDt, depthPhase, AUTO_SKILL_POS_DEPTH]);

  useEffect(() => {
    if (kind !== "depth") return;
    if (!depthGroups.length) return;
    if (!depthGroup || !depthGroups.includes(depthGroup)) setDepthGroup(depthGroups[0] || "");
  }, [kind, depthGroups, depthGroup]);

  const depthTable = useMemo(() => {
    if (kind !== "depth") return { mode: "slot", rows: [], rankKeys: [] };
    if (!depthRows.length || !depthTeam) return { mode: "slot", rows: [], rankKeys: [] };

    const needle = depthSearch.trim().toLowerCase();

    const base = depthRows.filter(
      (r) => String(r?.team ?? "") === String(depthTeam)
    );

    // Phase filters
    const OFF_ABB = new Set(["QB", "RB", "FB", "WR", "TE", "LT", "LG", "C", "RG", "RT", "T", "G", "OL", "OT", "OG", "LS"]);
    const DEF_ABB = new Set([
      "CB",
      "LCB",
      "RCB",
      "NB",
      "S",
      "FS",
      "SS",
      "LB",
      "MLB",
      "WLB",
      "SLB",
      "ILB",
      "OLB",
      "DE",
      "DT",
      "NT",
      "LDE",
      "RDE",
      "LDT",
      "RDT",
      "DL",
      "EDGE",
    ]);
    const ST_ABB = new Set(["K", "P", "PK", "KR", "PR", "H"]);

    const phaseOk = (posAbb, grp) => {
      const p = String(posAbb || "").trim().toUpperCase();
      const g = String(grp || "").toLowerCase();

      if (depthPhase === "auto") return AUTO_SKILL_POS_DEPTH.has(p);
      if (depthPhase === "defense") return DEF_ABB.has(p) || g.includes("def");
      if (depthPhase === "st") return ST_ABB.has(p) || g.includes("special");

      // offense (default)
      if (OFF_ABB.has(p) || AUTO_SKILL_POS_DEPTH.has(p)) return true;
      if (DEF_ABB.has(p) || ST_ABB.has(p)) return false;
      if (g.includes("def") || g.includes("special")) return false;
      return true;
    };

    const displayName = (r) =>
      String(
        r?.player_name ??
          r?.player_display_name ??
          r?.player ??
          r?.name ??
          r?.full_name ??
          ""
      ).trim();

    // Apply phase + search filters (search hits either name or position)
    const filtered = base
      .filter((r) => phaseOk(r?.pos_abb, r?.pos_grp))
      .filter((r) => {
        if (!needle) return true;
        const nm = displayName(r).toLowerCase();
        const p = String(r?.pos_abb ?? "").toLowerCase();
        const g = String(r?.pos_grp ?? "").toLowerCase();
        return nm.includes(needle) || p.includes(needle) || g.includes(needle);
      });

    const MAX_RANK = 6;
    const posOrder = ["QB", "RB", "WR", "TE"];

    // --- FULL DEPTH (ignore package groups): one row per position, depth by rank ---
    if (depthGroup === "__ALL__") {
      const byPos = new Map();

      for (const r of filtered) {
        const posAbb = String(r?.pos_abb ?? "").trim().toUpperCase();
        const rank = Number(r?.pos_rank ?? r?.rank ?? 0);
        const nm = displayName(r);
        if (!posAbb || !nm || !isFinite(rank) || rank <= 0) continue;

        if (!byPos.has(posAbb)) byPos.set(posAbb, []);
        byPos.get(posAbb).push({ rank, name: nm });
      }

      const rows = [];
      let maxRank = 0;

      for (const [posAbb, arr] of byPos.entries()) {
        // keep best (lowest) rank for each player name
        const bestByName = new Map();
        for (const it of arr) {
          const k = it.name;
          const prev = bestByName.get(k);
          if (!prev || it.rank < prev.rank) bestByName.set(k, it);
        }

        const ordered = Array.from(bestByName.values()).sort((a, b) => a.rank - b.rank);
        const ranks = {};
        for (const it of ordered) {
          const rk = Math.min(MAX_RANK, it.rank);
          if (!ranks[rk]) ranks[rk] = it.name;
          maxRank = Math.max(maxRank, rk);
        }

        rows.push({
          key: posAbb,
          pos_abb: posAbb,
          pos_desc: POS_LONG[posAbb] || posAbb,
          ranks,
        });
      }

      // Order QB -> RB -> WR -> TE -> everything else
      rows.sort((a, b) => {
        const ai = posOrder.includes(a.pos_abb) ? posOrder.indexOf(a.pos_abb) : 999;
        const bi = posOrder.includes(b.pos_abb) ? posOrder.indexOf(b.pos_abb) : 999;
        if (ai !== bi) return ai - bi;
        return String(a.pos_abb).localeCompare(String(b.pos_abb));
      });

      const rankKeys = Array.from({ length: Math.max(1, Math.min(MAX_RANK, maxRank)) }, (_, i) => i + 1);
      return { mode: "pos", rows, rankKeys };
    }

    // --- PACKAGE VIEW (pos_slot rows inside a selected group) ---
    const subset = filtered.filter((r) => String(r?.pos_grp ?? "") === String(depthGroup));

    const bySlot = new Map();
    for (const r of subset) {
      const posSlot = Number(r?.pos_slot ?? 0);
      const posAbb = String(r?.pos_abb ?? "").trim().toUpperCase();
      const rank = Number(r?.pos_rank ?? r?.rank ?? 0);
      const nm = displayName(r);
      if (!isFinite(posSlot) || posSlot <= 0 || !isFinite(rank) || rank <= 0 || !nm) continue;

      if (!bySlot.has(posSlot)) bySlot.set(posSlot, { posSlot, posAbb, posDesc: POS_LONG[posAbb] || posAbb, ranks: {} });
      const entry = bySlot.get(posSlot);
      const rk = Math.min(MAX_RANK, rank);
      if (!entry.ranks[rk]) entry.ranks[rk] = nm;
    }

    const rows = Array.from(bySlot.values());

    // Sort by position hierarchy then slot
    rows.sort((a, b) => {
      const ai = posOrder.includes(a.posAbb) ? posOrder.indexOf(a.posAbb) : 999;
      const bi = posOrder.includes(b.posAbb) ? posOrder.indexOf(b.posAbb) : 999;
      if (ai !== bi) return ai - bi;
      return (a.posSlot || 0) - (b.posSlot || 0);
    });

    let maxRank = 0;
    for (const r of rows) {
      for (const k of Object.keys(r.ranks || {})) maxRank = Math.max(maxRank, Number(k));
    }
    const rankKeys = Array.from({ length: Math.max(1, Math.min(MAX_RANK, maxRank)) }, (_, i) => i + 1);

    return { mode: "slot", rows, rankKeys };
  }, [kind, depthRows, depthTeam, depthDt, depthGroup, depthPhase, depthSearch, AUTO_SKILL_POS_DEPTH]);



/** ================== GAME ANALYZER (PBP): VIEW MODEL ================== */
const pbpRows = useMemo(() => (kind === "pbp" ? rowsRaw : []), [kind, rowsRaw]);

const pbpGames = useMemo(() => {
  if (!pbpRows.length) return [];
  const map = new Map();
  for (const r of pbpRows) {
    const id = String(r?.game_id ?? "").trim();
    if (!id) continue;
    if (!map.has(id)) {
      map.set(id, {
        game_id: id,
        week: r?.week,
        game_date: r?.game_date,
        home_team: r?.home_team,
        away_team: r?.away_team,
        season: r?.season,
        old_game_id: r?.old_game_id,
      });
    }
  }
  const arr = Array.from(map.values());
  arr.sort((a, b) => String(a.game_date ?? "").localeCompare(String(b.game_date ?? "")) || String(a.game_id).localeCompare(String(b.game_id)));
  return arr;
}, [pbpRows]);

// initialize defaults once pbp data exists
useEffect(() => {
  if (kind !== "pbp") return;
  if (!pbpRows.length) return;
  if (!pbpGameId && pbpGames.length) setPbpGameId(pbpGames[0].game_id);
}, [kind, pbpRows, pbpGames, pbpGameId]);

const pbpActiveGame = useMemo(() => pbpGames.find((g) => String(g.game_id) === String(pbpGameId)) || null, [pbpGames, pbpGameId]);

const pbpPlayTypes = useMemo(() => {
  if (!pbpRows.length || !pbpGameId) return [];
  const set = new Set();
  for (const r of pbpRows) {
    if (String(r?.game_id) !== String(pbpGameId)) continue;
    const t = String(r?.play_type ?? "").trim();
    if (!t) continue;
    if (pbpHideNan && t.toLowerCase() === "nan") continue;
    set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}, [pbpRows, pbpGameId, pbpHideNan]);

const pbpFiltered = useMemo(() => {
  if (kind !== "pbp") return [];
  if (!pbpGameId) return [];
  const needle = pbpSearch.trim().toLowerCase();

  let out = pbpRows.filter((r) => String(r?.game_id) === String(pbpGameId));

  if (pbpHideNan) {
    out = out.filter((r) => String(r?.play_type ?? "").toLowerCase() !== "nan");
  }
  if (pbpTeam !== "ALL") {
    out = out.filter((r) => String(r?.posteam ?? "") === String(pbpTeam));
  }
  if (pbpQtr !== "ALL") {
    out = out.filter((r) => String(r?.qtr ?? "") === String(pbpQtr));
  }
  if (pbpPlayType !== "ALL") {
    out = out.filter((r) => String(r?.play_type ?? "") === String(pbpPlayType));
  }
  if (needle) {
    const keys = [
      "posteam",
      "defteam",
      "play_type",
      "time",
      "passer_player_name",
      "receiver_player_name",
      "rusher_player_name",
      "td_team",
    ];
    out = out.filter((r) => keys.some((k) => String(r?.[k] ?? "").toLowerCase().includes(needle)));
  }

  // sort chronologically (qtr asc, time desc within quarter, play_id asc as a stable tie-breaker)
  const timeToSec = (t) => {
    const m = String(t || "").match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return -1;
    return Number(m[1]) * 60 + Number(m[2]);
  };

  out = [...out].sort((a, b) => {
    const aq = Number(a?.qtr ?? 0);
    const bq = Number(b?.qtr ?? 0);
    if (aq !== bq) return aq - bq;
    const at = timeToSec(a?.time);
    const bt = timeToSec(b?.time);
    if (at !== bt) return bt - at; // 15:00 -> 0:00
    return Number(a?.play_id ?? 0) - Number(b?.play_id ?? 0);
  });

  return out;
}, [kind, pbpRows, pbpGameId, pbpTeam, pbpQtr, pbpPlayType, pbpSearch, pbpHideNan]);

const pbpSummary = useMemo(() => {
  if (kind !== "pbp") return null;
  if (!pbpFiltered.length) {
    return {
      plays: 0,
      epaSum: 0,
      epaAvg: 0,
      successRate: 0,
      passRate: 0,
      rushRate: 0,
      yardsAvg: 0,
      wpNet: 0,
    };
  }

  const nums = (arr) => arr.filter((n) => typeof n === "number" && isFinite(n));
  const epa = nums(pbpFiltered.map((r) => r?.epa));
  const plays = pbpFiltered.length;

  const epaSum = epa.reduce((a, b) => a + b, 0);
  const epaAvg = epa.length ? epaSum / epa.length : 0;
  const successRate = epa.length ? epa.filter((v) => v > 0).length / epa.length : 0;

  const passN = pbpFiltered.filter((r) => Number(r?.pass ?? 0) === 1).length;
  const rushN = pbpFiltered.filter((r) => Number(r?.rush ?? 0) === 1).length;
  const prDen = Math.max(1, passN + rushN);
  const passRate = passN / prDen;
  const rushRate = rushN / prDen;

  const yds = nums(pbpFiltered.map((r) => r?.yards_gained));
  const yardsAvg = yds.length ? yds.reduce((a, b) => a + b, 0) / yds.length : 0;

  // net win probability change across the filtered slice (approx)
  const wpFirst = typeof pbpFiltered[0]?.wp === "number" ? pbpFiltered[0].wp : null;
  const wpLast = typeof pbpFiltered[pbpFiltered.length - 1]?.wp === "number" ? pbpFiltered[pbpFiltered.length - 1].wp : null;
  const wpNet = wpFirst != null && wpLast != null ? wpLast - wpFirst : 0;

  return { plays, epaSum, epaAvg, successRate, passRate, rushRate, yardsAvg, wpNet };
}, [kind, pbpFiltered]);

const pbpWpSeries = useMemo(() => {
  if (kind !== "pbp") return [];
  if (!pbpFiltered.length) return [];
  // keep chart light; sample down if huge
  const maxPts = 650;
  const step = Math.max(1, Math.ceil(pbpFiltered.length / maxPts));
  const out = [];
  for (let i = 0; i < pbpFiltered.length; i += step) {
    const r = pbpFiltered[i];
    const wp = typeof r?.wp === "number" ? r.wp : null;
    if (wp == null) continue;
    out.push({
      idx: i + 1,
      wp,
      qtr: r?.qtr,
      time: r?.time,
      posteam: r?.posteam,
      play_type: r?.play_type,
    });
  }
  return out;
}, [kind, pbpFiltered]);

// Add sequential deltas + convenience flags (kept in-memory only)
const pbpAug = useMemo(() => {
  if (kind !== "pbp") return [];
  if (!pbpFiltered.length) return [];
  const out = new Array(pbpFiltered.length);
  let prevWp = null;
  for (let i = 0; i < pbpFiltered.length; i++) {
    const r = pbpFiltered[i];
    const wp = typeof r?.wp === "number" ? r.wp : null;
    const epa = typeof r?.epa === "number" ? r.epa : null;
    const wp_delta = wp != null && prevWp != null ? wp - prevWp : 0;
    if (wp != null) prevWp = wp;

    const yardline_100 = typeof r?.yardline_100 === "number" ? r.yardline_100 : null;
    const isRedZone = yardline_100 != null && yardline_100 <= 20;

    out[i] = {
      ...r,
      __idx: i + 1,
      __wp_delta: wp_delta,
      __abs_epa: epa != null ? Math.abs(epa) : 0,
      __abs_wpd: Math.abs(wp_delta),
      __is_red_zone: isRedZone,
      __is_3rd4th: Number(r?.down ?? 0) === 3 || Number(r?.down ?? 0) === 4,
    };
  }
  return out;
}, [kind, pbpFiltered]);

const pbpKeyPlays = useMemo(() => {
  if (kind !== "pbp") return [];
  if (!pbpAug.length) return [];
  const epaMin = Math.max(0, Number(pbpEpaKeyMin) || 0);
  const wpMin = Math.max(0, Number(pbpWpKeyMin) || 0);

  return pbpAug.filter((r) => {
    if (pbpKeyOnlyRedZone && !r.__is_red_zone) return false;

    const td = Number(r?.touchdown ?? 0) === 1;
    const t = String(r?.play_type ?? "").toLowerCase();
    const isTurnovery = t.includes("interception") || t.includes("fumble");
    const big = (r.__abs_epa ?? 0) >= epaMin || (r.__abs_wpd ?? 0) >= wpMin;
    return td || isTurnovery || big;
  });
}, [kind, pbpAug, pbpEpaKeyMin, pbpWpKeyMin, pbpKeyOnlyRedZone]);

const pbpKeySummary = useMemo(() => {
  if (kind !== "pbp") return null;
  const arr = pbpKeyPlays || [];
  if (!arr.length) return { plays: 0, epaSum: 0, epaAvg: 0, wpaSum: 0, td: 0 };
  const epa = arr.map((r) => (typeof r?.epa === "number" ? r.epa : 0));
  const wpa = arr.map((r) => (typeof r?.__wp_delta === "number" ? r.__wp_delta : 0));
  const td = arr.filter((r) => Number(r?.touchdown ?? 0) === 1).length;
  const epaSum = epa.reduce((a, b) => a + b, 0);
  const wpaSum = wpa.reduce((a, b) => a + b, 0);
  return { plays: arr.length, epaSum, epaAvg: epaSum / Math.max(1, arr.length), wpaSum, td };
}, [kind, pbpKeyPlays]);

const pbpLeaders = useMemo(() => {
  if (kind !== "pbp") return null;
  if (!pbpAug.length) return { passers: [], rushers: [], receivers: [] };

  const agg = () => new Map();
  const passers = agg();
  const rushers = agg();
  const receivers = agg();

  const bump = (map, name, r, role) => {
    const key = String(name || "").trim();
    if (!key || key.toLowerCase() === "nan") return;
    const cur = map.get(key) || { name: key, plays: 0, epa: 0, wpa: 0, yds: 0, air: 0, tds: 0 };
    cur.plays += 1;

    const epa = typeof r?.epa === "number" ? r.epa : 0;
    const wpa = typeof r?.__wp_delta === "number" ? r.__wp_delta : 0;
    const yds = typeof r?.yards_gained === "number" ? r.yards_gained : 0;
    const air = typeof r?.air_yards === "number" ? r.air_yards : 0;
    cur.epa += epa;
    cur.wpa += wpa;
    cur.yds += yds;
    cur.air += air;

    if (Number(r?.touchdown ?? 0) === 1) cur.tds += 1;

    map.set(key, cur);
  };

  for (const r of pbpAug) {
    const t = String(r?.play_type ?? "").toLowerCase();

    // Passers: any play with a passer name OR tagged as pass
    if (Number(r?.pass ?? 0) === 1 || t.includes("pass")) bump(passers, r?.passer_player_name, r, "passer");

    // Rushers: any rush flagged or run-like type
    if (Number(r?.rush ?? 0) === 1 || t.includes("run") || t.includes("rush")) bump(rushers, r?.rusher_player_name, r, "rusher");

    // Receivers: any complete/incomplete pass with receiver name, or play type pass
    if (t.includes("pass")) bump(receivers, r?.receiver_player_name, r, "receiver");
  }

  const sortBy = (field) => (a, b) => (b[field] ?? 0) - (a[field] ?? 0);

  const top = (map) =>
    Array.from(map.values())
      .sort(sortBy("epa"))
      .slice(0, 20)
      .map((x) => ({
        ...x,
        epa_per: x.plays ? x.epa / x.plays : 0,
        wpa_per: x.plays ? x.wpa / x.plays : 0,
        yds_per: x.plays ? x.yds / x.plays : 0,
      }));

  return {
    passers: top(passers),
    rushers: top(rushers),
    receivers: top(receivers),
  };
}, [kind, pbpAug]);

const pbpSituations = useMemo(() => {
  if (kind !== "pbp") return null;
  if (!pbpAug.length) return null;

  const slice = (pred) => pbpAug.filter(pred);

  const summarize = (arr) => {
    const plays = arr.length;
    if (!plays) return { plays: 0, epaAvg: 0, success: 0, ydsAvg: 0, wpaSum: 0 };
    const epa = arr.map((r) => (typeof r?.epa === "number" ? r.epa : null)).filter((v) => v != null);
    const yds = arr.map((r) => (typeof r?.yards_gained === "number" ? r.yards_gained : null)).filter((v) => v != null);
    const wpa = arr.map((r) => (typeof r?.__wp_delta === "number" ? r.__wp_delta : 0));
    const epaSum = epa.reduce((a, b) => a + b, 0);
    const epaAvg = epa.length ? epaSum / epa.length : 0;
    const success = epa.length ? epa.filter((v) => v > 0).length / epa.length : 0;
    const ydsAvg = yds.length ? yds.reduce((a, b) => a + b, 0) / yds.length : 0;
    const wpaSum = wpa.reduce((a, b) => a + b, 0);
    return { plays, epaAvg, success, ydsAvg, wpaSum };
  };

  const red = summarize(slice((r) => r.__is_red_zone));
  const nonRed = summarize(slice((r) => !r.__is_red_zone));

  const third = summarize(slice((r) => Number(r?.down ?? 0) === 3));
  const fourth = summarize(slice((r) => Number(r?.down ?? 0) === 4));

  const thirdFourth = summarize(slice((r) => r.__is_3rd4th));

  return { red, nonRed, third, fourth, thirdFourth };
}, [kind, pbpAug]);


/** ================== UI ================== */
  const updatedAt = updatedAtBySeason?.[season] || "";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-5 md:px-6">
      <div className="mx-auto w-full min-w-0 space-y-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-xl font-bold tracking-tight text-slate-100">NFL Advanced Stats</h1>
          <div className="text-xs font-normal italic text-slate-100">
            {updatedAt ? <>Updated: {new Date(updatedAt).toLocaleString()}</> : null}
          </div>
        </div>

        {/* Season + View */}
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/95 p-3 shadow-sm">
          <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
            <div className="text-[11px] text-slate-400 font-semibold tracking-wide">SEASON</div>
            <select
              className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={season}
              onChange={(e) => {
                setSeason(Number(e.target.value));
                setErr("");
                setQ("");
                setSnapSearch("");
                setPbpSearch("");
                setPbpGameId("");
                setPbpTake(500);
              }}
            >
              {SEASONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {isChartable && (
            <div className="ml-auto flex items-center gap-1">
              {["table", "chart"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`border-b-2 px-3 py-2 text-xs font-semibold transition ${
                    view === v ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"
                  }`}
                >
                  {v === "table" ? "Table" : "Chart"}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Compact workspace navigation stays on one row; content scrolls horizontally on narrow screens. */}
        <div className="flex gap-1.5 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/95 px-3 py-1 shadow-sm" aria-label="NFL research sections">
          {WORKSPACES.map((w) => (
            <button key={w.key} onClick={() => { setWorkspace(w.key); const next = w.tabs.includes(tab) ? tab : w.tabs[0]; setTab(next); setErr(""); }} className={`shrink-0 border-b-2 px-3 py-2.5 text-xs font-semibold transition ${workspace === w.key ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
              {w.label}
            </button>
          ))}
        </div>
        {workspaceTabs.length > 1 && <div className="flex gap-1.5 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/95 px-3 py-1 shadow-sm">
          {workspaceTabs.map((tabKey) => <button key={tabKey} onClick={() => { setTab(tabKey); setErr(""); }} className={`shrink-0 border-b-2 px-3 py-2.5 text-xs font-semibold transition ${tab === tabKey ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{tabKey.replace("Advanced ", "")}</button>)}
        </div>}


        
{supportsSituation && <SituationFilters value={situation} onChange={setSituation} active={situationActive} />}
{!supportsSituation && situationActive && (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900/95 px-4 py-2 text-xs text-slate-300">
    <span>This tab shows its original data. Situation filters apply on Team Stats, Pace, Team Tendencies, Passing, Receiving, and Rushing.</span>
    <button type="button" className="font-semibold text-sky-300" onClick={() => setSituation(DEFAULT_SITUATION)}>Reset to all plays</button>
  </div>
)}
{/* ================== EXPANDED RESEARCH ================== */}
{kind === "fantasyflow" ? (
  <FantasyFlowChartView rows={rowsRaw} season={season} loading={loading} />
) : kind === "teambox" ? (
  <TeamBoxScoreView rows={rowsRaw} season={season} loading={loading} />
) : isGameLogTab ? (
  <GameLogView rows={rowsRaw} season={season} loading={loading} />
) : tab === "Pace" ? (
  <TeamPaceView key={season} rows={rowsRaw} season={season} loading={loading} error={err} situation={activeSituation} />
) : tab === "Team Stats" ? (
  <TeamStatsView rows={rowsRaw} season={season} loading={loading} situation={activeSituation} />
) : tab === "Team Tendencies" ? (
  <TeamTendenciesView rows={rowsRaw} season={season} loading={loading} error={err} situation={activeSituation} />
) : kind === "unified" && activeSituation ? (
  <SituationPlayerView key={`${season}__${tab}`} tab={tab} category={activeMeta.category} season={season}
    pbp={dataBySeason?.[season]?.__situationPbp} lookup={dataBySeason?.[season]?.__situationPlayers}
    situation={activeSituation} loading={loading}
    error={err || (!dataBySeason?.[season]?.__situationPbp?.length ? "No play-by-play data is available for this season." : "")} />
) : isResearchTab ? (
  <ResearchDatasetView tab={tab} rows={rowsRaw} chartSourceRows={kind === "unified" || tab === "Advanced Defense" ? dataBySeason?.[season]?.[`${activeDataKey}__charts`] : undefined} season={season} loading={loading} />
) : isGameTab ? (
  <>
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TYPE</div>
        <select
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={gameType}
          onChange={(e) => setGameType(e.target.value)}
        >
          {gameTypes.map((gt) => (
            <option key={gt} value={gt}>
              {gt}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">WEEK</div>
        <select
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={gameWeek}
          onChange={(e) => setGameWeek(e.target.value)}
        >
          {gameWeeks.map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TEAM</div>
        <select
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={gameTeam}
          onChange={(e) => setGameTeam(e.target.value)}
        >
          {gameTeams.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">SEARCH</div>
        <input
          value={gameSearch}
          onChange={(e) => setGameSearch(e.target.value)}
          placeholder="team, stadium, QB, coach, roof…"
          className="w-56 border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
        />
      </div>

      <label className="flex items-center gap-2 text-xs text-slate-200 px-3 py-2 rounded-full border border-slate-700 bg-slate-900/80">
        <input
          type="checkbox"
          className="accent-sky-500"
          checked={gameHideMissingLines}
          onChange={(e) => setGameHideMissingLines(e.target.checked)}
        />
        Hide missing lines
      </label>

      {/* Column picker */}
      <details className="group relative">
        <summary className="cursor-pointer list-none select-none px-3 py-2 rounded-full border border-slate-700 bg-slate-900/80 text-xs text-slate-200 hover:bg-slate-800">
          Columns{" "}
          <span className="opacity-60">
            ({gameVisibleColumns.length}/{gameAllColumns.length})
          </span>
        </summary>
        <div className="absolute z-40 mt-2 w-[520px] max-w-[92vw] rounded-2xl border border-slate-800 bg-slate-950/95 backdrop-blur p-3 shadow-2xl">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-200">Show / Hide</div>
            <div className="flex gap-2">
              <button
                className="text-xs px-2 py-1 text-slate-300 hover:text-slate-100 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  setVisibleGamesBySeason((prev) => ({
                    ...prev,
                    [gameVisibleKey]: gameAllColumns,
                  }));
                }}
              >
                All
              </button>
              <button
                className="text-xs px-2 py-1 text-slate-300 hover:text-slate-100 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  setVisibleGamesBySeason((prev) => ({ ...prev, [gameVisibleKey]: [] }));
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3 max-h-72 overflow-auto pr-2">
            {gameAllColumns.map((c) => {
              const checked = gameVisibleColumns.includes(c);
              return (
                <label
                  key={c}
                  className="flex items-start gap-2 text-[13px] px-1 py-1 rounded hover:bg-slate-800/70 cursor-pointer text-slate-100"
                  title={c}
                >
                  <input
                    type="checkbox"
                    className="accent-sky-500 mt-0.5 w-3.5 h-3.5 flex-shrink-0"
                    checked={checked}
                    onChange={() => toggleGameColumn(c)}
                  />
                  <span className="leading-tight whitespace-nowrap">{abbrev(c)}</span>
                </label>
              );
            })}
          </div>
        </div>
      </details>

<button
  onClick={exportGameDataCsv}
  className="px-3 py-2 rounded-full border border-slate-700 bg-slate-900/80 text-xs text-slate-200 hover:bg-slate-800"
>
  Export CSV
</button>

    </div>

    <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-auto max-h-[70vh]">
      <table className="min-w-max text-xs leading-tight w-full">
        <thead className="bg-slate-900 sticky top-0 z-30">
          <tr>
            {gameVisibleColumns.map((c) => (
              <th
                key={c}
                className="text-center px-2 py-2 font-semibold whitespace-nowrap select-none text-slate-200"
                title={c}
              >
                {prettyHeader(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                className="px-2 py-2 text-center text-slate-300"
                colSpan={gameVisibleColumns.length}
              >
                Loading…
              </td>
            </tr>
          ) : gamesFilteredSorted.length === 0 ? (
            <tr>
              <td
                className="px-2 py-2 text-center text-slate-300"
                colSpan={gameVisibleColumns.length}
              >
                No games.
              </td>
            </tr>
          ) : (
            gamesFilteredSorted.map((r, i) => {
              const rowBg = i % 2 ? "bg-slate-900" : "bg-slate-950";
              return (
                <tr key={r?.game_id ?? i} className={rowBg}>
                  {gameVisibleColumns.map((c) => (
                    <td key={c} className="px-2 py-2 whitespace-nowrap text-center text-slate-100">
                      {c === "gametime"
                      ? formatGameTime(r?.[c])
                      : fmt(r?.[c])
                    }
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </>
) : isSnapTab ? (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
                <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TEAM</div>
                <select
                  className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={snapTeam}
                  onChange={(e) => setSnapTeam(e.target.value)}
                >
                  {snapTeams.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
                <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TYPE</div>
                <select
                  className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={snapGameType}
                  onChange={(e) => setSnapGameType(e.target.value)}
                >
                  {snapGameTypes.map((gt) => (
                    <option key={gt} value={gt}>
                      {gt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
                <div className="text-[11px] text-slate-400 font-semibold tracking-wide">PHASE</div>
                <select
                  className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={snapPhase}
                  onChange={(e) => setSnapPhase(e.target.value)}
                >
                  <option value="auto">Auto</option>
                  <option value="offense">Offense</option>
                  <option value="defense">Defense</option>
                  <option value="st">Special Teams</option>
                </select>
              </div>

              <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
                <div className="text-[11px] text-slate-400 font-semibold tracking-wide">WEEK</div>
                <select
                  className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={snapWeekView}
                  onChange={(e) => setSnapWeekView(e.target.value)}
                >
                  <option value="all">All</option>
                  {snapWeeks.map((w) => (
                    <option key={w} value={String(w)}>
                      Week {w}
                    </option>
                  ))}
                </select>
              </div>

              <input
                className="border border-slate-700 rounded-md px-3 py-2 text-xs w-full sm:w-80 bg-slate-900/70 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500 ml-auto"
                placeholder="Search player / position…"
                value={snapSearch}
                onChange={(e) => setSnapSearch(e.target.value)}
              />
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-auto max-h-[78vh]">
              <table className="min-w-max text-xs leading-tight w-full">
                <thead className="bg-slate-900 sticky top-0 z-30">
                  <tr>
                    <th className="text-center px-2 py-2 font-semibold whitespace-nowrap sticky left-0 z-40 bg-slate-900 border-r border-slate-800 text-slate-200">
                      Player
                    </th>
                    {snapPivot.weeks.map((w) => (
                      <th key={w} className="text-center px-2 py-2 font-semibold whitespace-nowrap text-slate-200">
                        Wk {w}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-2 py-2 text-center text-slate-300" colSpan={1 + snapPivot.weeks.length}>
                        Loading…
                      </td>
                    </tr>
                  ) : !snapTeam ? (
                    <tr>
                      <td className="px-2 py-2 text-center text-slate-300" colSpan={1 + snapPivot.weeks.length}>
                        No team selected.
                      </td>
                    </tr>
                  ) : snapPivot.positions.length === 0 ? (
                    <tr>
                      <td className="px-2 py-2 text-center text-slate-300" colSpan={1 + snapPivot.weeks.length}>
                        No rows.
                      </td>
                    </tr>
                  ) : (
                    snapPivot.positions.map((pos) => {
                      const players = snapPivot.byPos[pos]?.sortedPlayers || [];
                      if (!players.length) return null;

                      return (
                        <React.Fragment key={pos}>
                          {/* position header row */}
                          <tr className="bg-slate-900/90">
                            <td
                              className="px-2 py-2 font-semibold text-slate-200 sticky left-0 z-20 bg-slate-900/90 border-r border-slate-800"
                              colSpan={1 + snapPivot.weeks.length}
                            >
                              {pos}
                            </td>
                          </tr>

                          {players.map((player, idx) => {
                            const rowBg = idx % 2 ? "bg-slate-900" : "bg-slate-950";
                            const wkMap = snapPivot.byPos[pos].players[player] || {};
                            return (
                              <tr key={`${pos}-${player}`} className={rowBg}>
                                <td className={`px-2 py-2 whitespace-nowrap text-left font-medium sticky left-0 z-20 ${rowBg} border-r border-slate-800`}>
                                  {player}
                                </td>

                                {snapPivot.weeks.map((w) => {
                                  const v = wkMap[w];
                                  const snaps = v?.snaps ?? 0;
                                  const pct = v?.pct ?? 0;
                                  const pctTxt = pct ? `${Math.round(pct * 100)}%` : "";
                                  const snapsTxt = snaps ? String(Math.round(snaps)) : "";
                                  return (
                                    <td key={w} className="px-2 py-2 whitespace-nowrap text-center text-slate-100">
                                      <div className="leading-none">
                                        <div className="text-slate-200">{snapsTxt}</div>
                                        <div className="text-slate-50 font-semibold mt-1">{pctTxt}</div>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : isDepthTab ? (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-400 font-semibold tracking-wide">TEAM</span>
                <select
                  className="border border-slate-700 rounded-full px-3 py-2 text-xs bg-slate-900/70 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={depthTeam}
                  onChange={(e) => setDepthTeam(e.target.value)}
                >
                  {depthTeams.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-400 font-semibold tracking-wide">PHASE</span>
                <select
                  className="border border-slate-700 rounded-full px-3 py-2 text-xs bg-slate-900/70 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={depthPhase}
                  onChange={(e) => setDepthPhase(e.target.value)}
                >
                  <option value="auto">Auto</option>
                  <option value="offense">Offense</option>
                  <option value="defense">Defense</option>
                  <option value="st">Special Teams</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-400 font-semibold tracking-wide">GROUP</span>
                <select
                  className="border border-slate-700 rounded-full px-3 py-2 text-xs bg-slate-900/70 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 min-w-[14rem]"
                  value={depthGroup}
                  onChange={(e) => setDepthGroup(e.target.value)}
                >
                  {depthGroups.map((g) => (
                    <option key={g} value={g}>
                      {g === "__ALL__" ? "Full Depth (All Groups)" : g}
                    </option>
                  ))}
                </select>
              </div>

              <input
                className="border border-slate-700 rounded-md px-3 py-2 text-xs w-full sm:w-80 bg-slate-900/70 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500 ml-auto"
                placeholder="Search player / position…"
                value={depthSearch}
                onChange={(e) => setDepthSearch(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1">
              <div>
                {depthDt ? <>Snapshot: {new Date(depthDt).toLocaleString()}</> : null}
              </div>
              <div>Rows: {depthTable.rows.length.toLocaleString()}</div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-auto max-h-[78vh]">
              <table className="min-w-max text-xs leading-tight w-full">
                <thead className="bg-slate-900 sticky top-0 z-30">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold whitespace-nowrap sticky left-0 z-40 bg-slate-900 border-r border-slate-800 text-slate-200">
                      Position
                    </th>
                    {depthTable.rankKeys.map((rk) => (
                    <th key={rk} className="text-left px-3 py-2 font-semibold whitespace-nowrap text-slate-200">
                      {rk === 1 ? "Starter" : rk === 2 ? "2nd" : rk === 3 ? "3rd" : rk + "th"}
                    </th>
                  ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-3 py-3 text-center text-slate-300" colSpan={1 + depthTable.rankKeys.length}>
                        Loading…
                      </td>
                    </tr>
                  ) : depthTable.rows.length === 0 ? (
                    <tr>
                      <td className="px-3 py-3 text-center text-slate-300" colSpan={1 + depthTable.rankKeys.length}>
                        No rows.
                      </td>
                    </tr>
                  ) : (
                    depthTable.rows.map((r, i) => {
                      const rowBg = i % 2 ? "bg-slate-900" : "bg-slate-950";
                      const posAbb = r.posAbb ?? r.pos_abb ?? "";
                      const posName = r.posName ?? r.posDesc ?? r.pos_desc ?? "";
                      const ranks = r.ranks ?? { 1: r.r1, 2: r.r2, 3: r.r3 };
                      const key = r.key ?? r.posSlot ?? posAbb ?? i;

                      return (
                        <tr key={key} className={rowBg}>
                          <td
                            className={`px-3 py-2 whitespace-nowrap sticky left-0 z-20 ${rowBg} border-r border-slate-800 font-semibold`}
                          >
                            {posAbb}{" "}
                            <span className="text-slate-400 font-normal">— {posName}</span>
                          </td>

                          {depthTable.rankKeys.map((rk) => (
                            <td key={rk} className="px-3 py-2 whitespace-nowrap">
                              {ranks?.[rk] || ""}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : isPbpTab ? (
  /* ================== GAME ANALYZER (PBP) ================== */
  <>
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">GAME</div>
        <select
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={pbpGameId}
          onChange={(e) => {
            setPbpGameId(e.target.value);
            setPbpTake(500);
            setPbpPlayType("ALL");
            setPbpTeam("ALL");
            setPbpQtr("ALL");
            setPbpSearch("");
          }}
        >
          {pbpGames.map((g) => (
            <option key={g.game_id} value={g.game_id}>
              {g.game_date} • W{g.week} • {g.away_team} @ {g.home_team}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TEAM</div>
        <select
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={pbpTeam}
          onChange={(e) => {
            setPbpTeam(e.target.value);
            setPbpTake(500);
          }}
        >
          <option value="ALL">All</option>
          {pbpActiveGame?.away_team ? <option value={pbpActiveGame.away_team}>{pbpActiveGame.away_team}</option> : null}
          {pbpActiveGame?.home_team ? <option value={pbpActiveGame.home_team}>{pbpActiveGame.home_team}</option> : null}
        </select>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">QTR</div>
        <select
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={pbpQtr}
          onChange={(e) => {
            setPbpQtr(e.target.value);
            setPbpTake(500);
          }}
        >
          <option value="ALL">All</option>
          {[1, 2, 3, 4, 5].map((q) => (
            <option key={q} value={String(q)}>
              {q}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TYPE</div>
        <select
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
          value={pbpPlayType}
          onChange={(e) => {
            setPbpPlayType(e.target.value);
            setPbpTake(500);
          }}
        >
          <option value="ALL">All</option>
          {pbpPlayTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">SEARCH</div>
        <input
          className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500 w-[220px]"
          placeholder="player / team / type…"
          value={pbpSearch}
          onChange={(e) => {
            setPbpSearch(e.target.value);
            setPbpTake(500);
          }}
        />
      </div>

      <label className="ml-auto flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200 cursor-pointer select-none">
        <input type="checkbox" className="accent-sky-500" checked={pbpHideNan} onChange={(e) => setPbpHideNan(e.target.checked)} />
        Hide non-plays
      </label>

      <button
        onClick={() => {
          const cols = [
            "week",
            "game_id",
            "game_date",
            "home_team",
            "away_team",
            "qtr",
            "time",
            "posteam",
            "defteam",
            "play_type",
            "down",
            "ydstogo",
            "yardline_100",
            "score_differential",
            "pass",
            "rush",
            "complete_pass",
            "air_yards",
            "yards_gained",
            "epa",
            "wp",
            "passer_player_name",
            "receiver_player_name",
            "rusher_player_name",
            "td_team",
            "touchdown",
            "play_id",
            "old_game_id",
            "season",
          ];
          exportToCsv(`pbp_${pbpGameId}.csv`, pbpFiltered, cols);
        }}
        className="px-3 py-2 rounded-full border border-slate-700 bg-slate-900/80 hover:bg-slate-800 text-xs font-semibold"
      >
        Export CSV
      </button>
    </div>
    {/* Subtabs */}
    <div className="flex flex-wrap gap-2 items-center">
      {["Plays", "Key Plays", "Leaders", "Situations"].map((k) => (
        <button
          key={k}
          onClick={() => setPbpSubtab(k)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            pbpSubtab === k
              ? "bg-sky-500 text-slate-950 border-sky-400"
              : "bg-slate-900/70 text-slate-200 border-slate-700 hover:bg-slate-800"
          }`}
        >
          {k}
        </button>
      ))}
      <div className="ml-auto text-[11px] text-slate-400">
        {pbpActiveGame ? `Week ${fmt(pbpActiveGame.week)} • ${fmt(pbpActiveGame.game_date)} • ${pbpActiveGame.away_team} @ ${pbpActiveGame.home_team}` : ""}
      </div>
    </div>

    {pbpSubtab === "Plays" ? (


      <>
    {/* Summary */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">PLAYS</div>
        <div className="text-2xl font-extrabold">{pbpSummary?.plays ?? 0}</div>
        <div className="text-[11px] text-slate-500">Filtered plays</div>
      </div>
      <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">EPA</div>
        <div className="text-2xl font-extrabold">{fmt(pbpSummary?.epaSum ?? 0)}</div>
        <div className="text-[11px] text-slate-500">Avg {fmt(pbpSummary?.epaAvg ?? 0)}</div>
      </div>
      <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">SUCCESS</div>
        <div className="text-2xl font-extrabold">{Math.round((pbpSummary?.successRate ?? 0) * 100)}%</div>
        <div className="text-[11px] text-slate-500">EPA &gt; 0</div>
      </div>
      <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
        <div className="text-[11px] text-slate-400 font-semibold tracking-wide">PASS / RUSH</div>
        <div className="text-2xl font-extrabold">
          {Math.round((pbpSummary?.passRate ?? 0) * 100)}% / {Math.round((pbpSummary?.rushRate ?? 0) * 100)}%
        </div>
        <div className="text-[11px] text-slate-500">
          Yds/play {fmt(pbpSummary?.yardsAvg ?? 0)} • WP Δ {fmt(pbpSummary?.wpNet ?? 0)}
        </div>
      </div>
    </div>

    {/* Win Prob sparkline */}
    <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold">
          Win Probability • {pbpActiveGame ? `${pbpActiveGame.away_team} @ ${pbpActiveGame.home_team}` : ""}
        </div>
        <div className="text-[11px] text-slate-400">
          {pbpFiltered.length ? `${pbpFiltered.length} plays shown (sampled)` : "No plays"}
        </div>
      </div>
      <div className="h-[220px] w-full rounded-xl bg-slate-900">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pbpWpSeries} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="idx" tick={{ fontSize: 10 }} />
            <YAxis domain={[0, 1]} tick={{ fontSize: 10 }} />
            <Tooltip
              contentStyle={{ background: "rgba(2,6,23,0.92)", border: "1px solid rgba(148,163,184,0.25)", borderRadius: 12 }}
              labelStyle={{ color: "#e2e8f0" }}
              formatter={(value) => [fmt(value), "wp"]}
              labelFormatter={(v, payload) => {
                const p = payload?.[0]?.payload;
                if (!p) return `Play ${v}`;
                return `Play ${v} • Q${p.qtr} ${p.time} • ${p.posteam} • ${p.play_type}`;
              }}
            />
            <ReferenceLine y={0.5} stroke="#94a3b8" strokeDasharray="4 4" />
            <Line type="monotone" dataKey="wp" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>

    {/* Table */}
    <div className="rounded-2xl border border-slate-800 bg-slate-900/95 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
        <div className="text-sm font-semibold">Play-by-Play</div>
        <div className="flex items-center gap-2">
          <div className="text-[11px] text-slate-400">
            Showing {Math.min(pbpFiltered.length, pbpTake)} / {pbpFiltered.length}
          </div>
          {pbpFiltered.length > pbpTake ? (
            <button
              onClick={() => setPbpTake((v) => v + 500)}
              className="px-3 py-1.5 rounded-full border border-slate-700 bg-slate-900/80 hover:bg-slate-800 text-xs font-semibold"
            >
              Show more
            </button>
          ) : null}
        </div>
      </div>

      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800">
            <tr className="text-slate-300">
              {[
                "Q",
                "Time",
                "Down",
                "ToGo",
                "YdLn",
                "Off",
                "Def",
                "Type",
                "EPA",
                "WP",
                "Yds",
                "Air",
                "Passer",
                "Receiver",
                "Rusher",
                "TD",
              ].map((h) => (
                <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pbpFiltered.slice(0, pbpTake).map((r, i) => (
              <tr key={String(r?.play_id ?? i)} className={`border-b border-slate-900/60 ${i % 2 ? "bg-slate-950/10" : ""}`}>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.qtr)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.time)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.down)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.ydstogo)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.yardline_100)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.posteam)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.defteam)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.play_type)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.epa)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.wp)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.yards_gained)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.air_yards)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.passer_player_name)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.receiver_player_name)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.rusher_player_name)}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-200">
                  {Number(r?.touchdown ?? 0) === 1 ? <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200">TD</span> : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
      </>
    ) : pbpSubtab === "Key Plays" ? (
      <>
        {/* Key play controls */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
            <div className="text-[11px] text-slate-400 font-semibold tracking-wide">ABS EPA ≥</div>
            <input
              type="number"
              step="0.1"
              className="w-[90px] border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={pbpEpaKeyMin}
              onChange={(e) => setPbpEpaKeyMin(Number(e.target.value))}
            />
          </div>

          <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
            <div className="text-[11px] text-slate-400 font-semibold tracking-wide">ABS WPΔ ≥</div>
            <input
              type="number"
              step="0.01"
              className="w-[90px] border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={pbpWpKeyMin}
              onChange={(e) => setPbpWpKeyMin(Number(e.target.value))}
            />
          </div>

          <label className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200 cursor-pointer select-none">
            <input type="checkbox" className="accent-sky-500" checked={pbpKeyOnlyRedZone} onChange={(e) => setPbpKeyOnlyRedZone(e.target.checked)} />
            Red zone only
          </label>

          <div className="ml-auto text-[11px] text-slate-400">
            Key = TD / turnover-like / big EPA / big WPΔ
          </div>
        </div>

        {/* Key summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
            <div className="text-[11px] text-slate-400 font-semibold tracking-wide">KEY PLAYS</div>
            <div className="text-2xl font-extrabold">{pbpKeySummary?.plays ?? 0}</div>
            <div className="text-[11px] text-slate-500">Filtered key moments</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
            <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TOTAL EPA</div>
            <div className="text-2xl font-extrabold">{fmt(pbpKeySummary?.epaSum ?? 0)}</div>
            <div className="text-[11px] text-slate-500">Avg {fmt(pbpKeySummary?.epaAvg ?? 0)}</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
            <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TOTAL WPΔ</div>
            <div className="text-2xl font-extrabold">{fmt(pbpKeySummary?.wpaSum ?? 0)}</div>
            <div className="text-[11px] text-slate-500">Sum of play WPΔ</div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-3">
            <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TDS</div>
            <div className="text-2xl font-extrabold">{pbpKeySummary?.td ?? 0}</div>
            <div className="text-[11px] text-slate-500">Touchdowns in keys</div>
          </div>
        </div>

        {/* Key plays table */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/95 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
            <div className="text-sm font-semibold">Key Plays</div>
            <div className="text-[11px] text-slate-400">Showing {Math.min(pbpKeyPlays.length, pbpTake)} / {pbpKeyPlays.length}</div>
          </div>

          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800">
                <tr className="text-slate-300">
                  {["Q","Time","Down","ToGo","YdLn","Off","Type","EPA","WP","WPΔ","Yds","Passer","Receiver","Rusher","TD"].map((h) => (
                    <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pbpKeyPlays.slice(0, pbpTake).map((r, i) => (
                  <tr key={String(r?.play_id ?? i)} className={`border-b border-slate-900/60 ${i % 2 ? "bg-slate-950/10" : ""}`}>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.qtr)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.time)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.down)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.ydstogo)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.yardline_100)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.posteam)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.play_type)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.epa)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.wp)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.__wp_delta)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.yards_gained)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.passer_player_name)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.receiver_player_name)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">{fmt(r?.rusher_player_name)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-200">
                      {Number(r?.touchdown ?? 0) === 1 ? <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200">TD</span> : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    ) : pbpSubtab === "Leaders" ? (
      <>
        {/* Leaders */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {[
            { title: "Passers", rows: pbpLeaders?.passers ?? [] },
            { title: "Rushers", rows: pbpLeaders?.rushers ?? [] },
            { title: "Receivers", rows: pbpLeaders?.receivers ?? [] },
          ].map((blk) => (
            <div key={blk.title} className="rounded-2xl border border-slate-800 bg-slate-900/95 overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
                <div className="text-sm font-semibold">{blk.title}</div>
                <div className="text-[11px] text-slate-400">Top by EPA</div>
              </div>
              <div className="overflow-auto max-h-[70vh]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-950/95 backdrop-blur border-b border-slate-800">
                    <tr className="text-slate-300">
                      {["Player","Plays","EPA","EPA/Play","WPΔ","Yds","Yds/Play","TD"].map((h) => (
                        <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {blk.rows.map((r, i) => (
                      <tr key={r.name} className={`border-b border-slate-900/60 ${i % 2 ? "bg-slate-950/10" : ""}`}>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{r.name}</td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{r.plays}</td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{fmt(r.epa)}</td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{fmt(r.epa_per)}</td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{fmt(r.wpa)}</td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{fmt(r.yds)}</td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{fmt(r.yds_per)}</td>
                        <td className="px-2 py-2 text-slate-200 whitespace-nowrap">{r.tds}</td>
                      </tr>
                    ))}
                    {!blk.rows.length ? (
                      <tr>
                        <td colSpan={8} className="px-3 py-6 text-center text-slate-500">No leader data</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </>
    ) : (
      <>
        {/* Situations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { title: "Red Zone (≤20)", s: pbpSituations?.red },
            { title: "Non-Red Zone (>20)", s: pbpSituations?.nonRed },
            { title: "3rd Down", s: pbpSituations?.third },
            { title: "4th Down", s: pbpSituations?.fourth },
          ].map((blk) => (
            <div key={blk.title} className="rounded-2xl border border-slate-800 bg-slate-900/95 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{blk.title}</div>
                <div className="text-[11px] text-slate-400">{blk.s?.plays ?? 0} plays</div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
                  <div className="text-[11px] text-slate-400 font-semibold tracking-wide">EPA / PLAY</div>
                  <div className="text-xl font-extrabold">{fmt(blk.s?.epaAvg ?? 0)}</div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
                  <div className="text-[11px] text-slate-400 font-semibold tracking-wide">SUCCESS</div>
                  <div className="text-xl font-extrabold">{Math.round((blk.s?.success ?? 0) * 100)}%</div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
                  <div className="text-[11px] text-slate-400 font-semibold tracking-wide">YDS / PLAY</div>
                  <div className="text-xl font-extrabold">{fmt(blk.s?.ydsAvg ?? 0)}</div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
                  <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TOTAL WPΔ</div>
                  <div className="text-xl font-extrabold">{fmt(blk.s?.wpaSum ?? 0)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/95 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">3rd + 4th Down Combined</div>
            <div className="text-[11px] text-slate-400">{pbpSituations?.thirdFourth?.plays ?? 0} plays</div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
              <div className="text-[11px] text-slate-400 font-semibold tracking-wide">EPA / PLAY</div>
              <div className="text-xl font-extrabold">{fmt(pbpSituations?.thirdFourth?.epaAvg ?? 0)}</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
              <div className="text-[11px] text-slate-400 font-semibold tracking-wide">SUCCESS</div>
              <div className="text-xl font-extrabold">{Math.round((pbpSituations?.thirdFourth?.success ?? 0) * 100)}%</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
              <div className="text-[11px] text-slate-400 font-semibold tracking-wide">YDS / PLAY</div>
              <div className="text-xl font-extrabold">{fmt(pbpSituations?.thirdFourth?.ydsAvg ?? 0)}</div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/95 p-3">
              <div className="text-[11px] text-slate-400 font-semibold tracking-wide">TOTAL WPΔ</div>
              <div className="text-xl font-extrabold">{fmt(pbpSituations?.thirdFourth?.wpaSum ?? 0)}</div>
            </div>
          </div>
        </div>
      </>
    )}
  </>


        ) : (
          /* ================== NGS (TABLE/CHART) ================== */
          <>
            {/* Controls */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5">
                <div className="text-[11px] text-slate-400 font-semibold tracking-wide">WEEK</div>
                <select
                  className="border border-slate-700 rounded-full px-3 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  value={week}
                  onChange={(e) => setWeek(Number(e.target.value))}
                >
                  {/* Week 0 is season-long */}
                  {[0, ...Array.from({ length: 30 }, (_, i) => i + 1)].map((w) => (
                    <option key={w} value={w}>
                      {w === 0 ? "Week 0 (season-long)" : `Week ${w}`}
                    </option>
                  ))}
                </select>
              </div>

              <input
                className="border border-slate-700 rounded-md px-3 py-2 text-xs w-full sm:w-80 bg-slate-900/70 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                placeholder="Search player / team / pos…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <div className="text-[11px] text-slate-400">Rows: {(sorted.length || 0).toLocaleString()}</div>

              {view === "table" && (
                <>
                  <details className="relative">
                    <summary className="list-none cursor-pointer">
                      <span className="px-3 py-2 rounded-md border border-slate-700 bg-slate-900/80 text-xs font-medium text-slate-100 hover:bg-slate-800">
                        Columns
                      </span>
                    </summary>
                    <div className="absolute z-50 mt-2 rounded-xl border border-slate-700 bg-slate-950 shadow-xl shadow-slate-900/60 p-3 w-[26rem] sm:w-[34rem] md:w-[40rem]">
                      <div className="flex items-center justify-between mb-2">
                        <button
                          className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100"
                          onClick={(e) => {
                            e.preventDefault();
                            selectAll();
                          }}
                        >
                          Select all
                        </button>
                        <button
                          className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100"
                          onClick={(e) => {
                            e.preventDefault();
                            clearAll();
                          }}
                        >
                          Clear
                        </button>
                        <button
                          className="text-xs px-2 py-1 text-slate-300 hover:text-slate-100 hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            resetDefault();
                          }}
                        >
                          Reset
                        </button>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3 max-h-72 overflow-auto pr-2">
                        {allColumns.map((c) => {
                          const checked = visibleColumns.includes(c);
                          return (
                            <label
                              key={c}
                              className="flex items-start gap-2 text-[13px] px-1 py-1 rounded hover:bg-slate-800/70 cursor-pointer text-slate-100"
                              title={c}
                            >
                              <input
                                type="checkbox"
                                className="accent-sky-500 mt-0.5 w-3.5 h-3.5 flex-shrink-0"
                                checked={checked}
                                onChange={() => toggleColumn(c)}
                              />
                              <span className="leading-tight whitespace-nowrap">{abbrev(c)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </details>

                  <button
                    className="px-3 py-2 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400 transition border border-sky-400 shadow-sm"
                    onClick={() => exportToCsv(`NFL_${season}_${tab}_W${week}.csv`, sorted, visibleColumns)}
                  >
                    Export CSV
                  </button>
                </>
              )}
            </div>

            {view === "table" ? (
              /* ===== TABLE ===== */
              <div className="rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-auto max-h-[70vh]">
                <table className="min-w-max text-xs leading-tight w-full">
                  <thead className="bg-slate-900 sticky top-0 z-30">
                    <tr>
                      {visibleColumns.map((c) => (
                        <th
                          key={c}
                          className="text-center px-2 py-2 font-semibold whitespace-nowrap cursor-pointer select-none text-slate-200"
                          onClick={() => onHeaderClick(c)}
                          title={c}
                        >
                          <div className="inline-flex items-center gap-1">
                            <span>{prettyHeader(c)}</span>
                            {sort.key === c ? <span className="text-[9px] opacity-60">{sort.dir === "asc" ? "▲" : "▼"}</span> : null}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                          Loading…
                        </td>
                      </tr>
                    ) : sorted.length === 0 ? (
                      <tr>
                        <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                          No rows.
                        </td>
                      </tr>
                    ) : (
                      sorted.map((r, i) => {
                        const rowBg = i % 2 ? "bg-slate-900" : "bg-slate-950";
                        return (
                          <tr key={i} className={rowBg}>
                            {visibleColumns.map((c) => (
                              <td key={c} className="px-2 py-2 whitespace-nowrap text-center text-slate-100">
                                {fmt(r[c])}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              /* ===== CHART ===== */
              <div className="rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm">
                {/* Chart Controls */}
                <div className="p-3 border-b border-slate-800 flex flex-wrap items-end gap-3">
                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">X Axis</label>
                    <select
                      className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[12rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={xKey}
                      onChange={(e) => setXKey(e.target.value)}
                    >
                      {numericColumns.map((c) => (
                        <option key={c} value={c}>
                          {prettyHeader(c)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">Y Axis</label>
                    <select
                      className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[12rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={yKey}
                      onChange={(e) => setYKey(e.target.value)}
                    >
                      {numericColumns.map((c) => (
                        <option key={c} value={c}>
                          {prettyHeader(c)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-[11px] text-slate-400 mb-1">Gate metric (min)</label>
                    <div className="flex items-center gap-2">
                      <select
                        className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[12rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        value={gateKey}
                        onChange={(e) => setGateKey(e.target.value)}
                      >
                        <option value="">— none —</option>
                        {numericColumns.map((c) => (
                          <option key={c} value={c}>
                            {prettyHeader(c)}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        className="border border-slate-700 rounded-md px-2 py-1 text-xs w-24 bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        placeholder="min"
                        value={gateMin}
                        onChange={(e) => setGateMin(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Axis color only */}
                  <div className="flex items-end gap-2">
                    <div className="flex flex-col">
                      <label className="text-[11px] text-slate-400 mb-1">Axis color</label>
                      <input
                        type="color"
                        className="w-10 h-8 cursor-pointer rounded-md border border-slate-700 bg-slate-900"
                        value={axisColor}
                        onChange={(e) => setAxisColor(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Diagonal line controls */}
                  <div className="flex items-end gap-2 ml-auto">
                    <div className="flex flex-col">
                      <label className="text-[11px] text-slate-400 mb-1">Diagonal</label>
                      <select
                        className="border border-slate-700 rounded-md px-2 py-1 text-xs min-w-[10rem] bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        value={diagMode}
                        onChange={(e) => setDiagMode(e.target.value)}
                      >
                        <option value="off">Off</option>
                        <option value="yx">y = x</option>
                        <option value="means">Through means</option>
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="text-[11px] text-slate-400 mb-1">Diag color</label>
                      <input
                        type="color"
                        className="w-10 h-8 cursor-pointer rounded-md border border-slate-700 bg-slate-900"
                        value={diagColor}
                        onChange={(e) => setDiagColor(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-[11px] text-slate-400 mb-1">Diag dash</label>
                      <select
                        className="border border-slate-700 rounded-md px-2 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        value={diagDash}
                        onChange={(e) => setDiagDash(e.target.value)}
                      >
                        <option value="6 6">6 6</option>
                        <option value="4 4">4 4</option>
                        <option value="2 6">2 6</option>
                        <option value="">Solid</option>
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="text-[11px] text-slate-400 mb-1">Show</label>
                      <select
                        className="border border-slate-700 rounded-md px-2 py-1 text-xs bg-slate-900/80 text-slate-100 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        value={ngsChartLimit}
                        onChange={(e) => setNgsChartLimit(e.target.value)}
                      >
                        {CHART_LIMIT_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={resetChartDefaults}
                      className="h-8 rounded-md border border-slate-600 bg-slate-800 px-3 text-xs font-semibold text-slate-100 transition hover:border-sky-500 hover:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      title="Reset axes, gate, and chart styling"
                    >
                      Reset
                    </button>
                    <div className="text-[11px] text-slate-400 hidden sm:block">Showing {chartRows.length.toLocaleString()} players</div>
                  </div>
                  <div className="text-[11px] text-slate-400 sm:hidden ml-auto">Showing {chartRows.length.toLocaleString()} players</div>
                </div>

                {/* Chart */}
                <div className="mx-auto w-full max-w-[1120px] lg:w-[85%] aspect-[49/27] min-h-[320px] rounded-xl bg-slate-900 px-2 pt-2 pb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 28, right: 36, bottom: 58, left: 36 }}>
                      <CartesianGrid stroke="#1F2937" strokeDasharray="4 4" />

                      <XAxis
                        type="number"
                        dataKey={xKey}
                        domain={xDomain}
                        allowDataOverflow
                        tick={{ fontSize: 11, fill: "#CBD5F5" }}
                        tickFormatter={(v) => fmt(v)}
                        label={{
                          value: prettyHeader(xKey),
                          position: "bottom",
                          offset: 8,
                          style: { fill: "#9CA3AF", fontSize: 12 },
                        }}
                        axisLine={{ stroke: axisColor, strokeWidth: 1.5 }}
                        tickLine={{ stroke: axisColor }}
                      />

                      <YAxis
                        type="number"
                        dataKey={yKey}
                        domain={yDomain}
                        allowDataOverflow
                        tick={{ fontSize: 11, fill: "#CBD5F5" }}
                        tickFormatter={(v) => fmt(v)}
                        label={{
                          value: prettyHeader(yKey),
                          angle: -90,
                          position: "insideLeft",
                          style: { fill: "#9CA3AF", fontSize: 12 },
                        }}
                        axisLine={{ stroke: axisColor, strokeWidth: 1.5 }}
                        tickLine={{ stroke: axisColor }}
                      />

                      <Tooltip
                        cursor={{ stroke: "#64748B", strokeDasharray: "4 4" }}
                        content={(props) => (
                          <ChartTooltip {...props} xKey={xKey} yKey={yKey} />
                        )}
                        wrapperStyle={{ zIndex: 1000, outline: "none" }}
                        allowEscapeViewBox={{ x: true, y: true }}
                      />

                      {/* Mean crosshairs */}
                      <ReferenceLine
                        x={xMean}
                        stroke="#94A3B8"
                        strokeWidth={1.5}
                        strokeDasharray="6 6"
                        ifOverflow="extendDomain"
                        label={{
                          value: `Mean ${prettyHeader(xKey)} (${fmt(xMean)})`,
                          position: "top",
                          offset: 12,
                          fill: "#9CA3AF",
                          fontSize: 12,
                        }}
                      />
                      <ReferenceLine
                        y={yMean}
                        stroke="#94A3B8"
                        strokeWidth={1.5}
                        strokeDasharray="6 6"
                        ifOverflow="extendDomain"
                        label={{
                          value: `Mean ${prettyHeader(yKey)} (${fmt(yMean)})`,
                          position: "right",
                          offset: 12,
                          fill: "#9CA3AF",
                          fontSize: 12,
                        }}
                      />

                      {/* Diagonal */}
                      {diagSegment && <ReferenceLine segment={diagSegment} stroke={diagColor} strokeWidth={1.75} strokeDasharray={diagDash} />}

                      <Scatter
                        data={chartRows}
                        shape={<PlayerLogoPoint />}
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="px-4 pb-4 -mt-2 text-center text-sm text-slate-300">
                  {tab} — {prettyHeader(xKey)} vs {prettyHeader(yKey)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
